// =============================================================================
// Compliance — the FULL back-of-house module (HACCP day, probe calibration, fish
// freezing, emergency fridge, hygiene declaration, cleaning, pest, corrective
// actions, SOP / FSMS / supplier / legal hub), lifted out of the retired kitchen
// app (src/boh-kitchen.jsx cmpMondayOf … ComplianceModule, 20 Sep 2026) so it
// renders inside the ONE Trafford site app now the kitchen is on the premises.
// Meadowhall keeps the FOH subset in foh.jsx (cleaning · pest · corrective);
// sites.js `compliance` ("full" | "subset") decides which one a site mounts.
//
// What changed in the lift (everything else is the kitchen code, verbatim):
//   * Storage units + probes are PER SITE. The kitchen hardcoded its own five
//     fridges (F1–F5) and three probes (P1–P3); a Trafford reading logged against
//     those ids would be a record of a unit that is not in the building. Both
//     lists now come from the admin-managed "fridges" / "probes" surfaces
//     (Command Centre → Compliance), read from the site's OWN scope only (the
//     shared '*' scope holds the closed kitchen's units and is deliberately not
//     inherited for physical kit) and resolved by complianceUnits.js: the closed
//     kitchen keys keep the old lists for history, any other site with nothing
//     configured gets an EMPTY list and an on-screen pointer to the Command
//     Centre — never a guessed unit. The HACCP home-strip total is derived from
//     those lists (it was the literal 8 + 6 + 10). The home status chip names
//     the mounted site (it said "Manchester Kitchen").
//   * The kitchen.css rules this module relies on are injected once on mount
//     (index.html already carries the shared subset byte-identically), so the
//     FOH bundle's look is untouched and kitchen.css never loads on a till. Its
//     scrims / toasts / PDF viewer are re-stacked BELOW the FOH PIN keypad
//     overlay (z 500 — the topmost layer by rule); the kitchen's own overlay was
//     800 so the original 600 / 700 values never had to consider it.
//   * Free-text placeholders that named the kitchen's units (F2, F3, F4) are
//     generic now; the FSMS / insurance document text is left as written.
//
// Backend + gates are unchanged: complianceLog (BOH · FOH · Admin), haccpEvent /
// haccpSign (BOH · Admin) on the Apps Script matrix, with the Lane AK / BK
// Supabase read-flips and the dual-run shadows exactly as the kitchen ran them.
// =============================================================================
import React from "react";
import * as complianceSync from "./complianceSync.js"; // Phase 3 dual-run shadow (compliance + HACCP)
import * as complianceReader from "./complianceReader.js"; // Lane AK / BK read-flips (gated by *_READ_HOSTS)
import * as pinAuth from "./auth.js"; // A1: server-side PIN verify (gated)
import { fetchStaffCerts } from "./staffCerts.js"; // staff certs read from Supabase (hub → Certs)
import { resolveProbes, resolveFridges, haccpTotalFor } from "./complianceUnits.js";
const { useState, useEffect, useCallback, useRef, useMemo, useLayoutEffect } = React;

// The kitchen.css compliance rules (every `.cmp-*` selector + the three cmp
// keyframes), injected once. index.html already ships 104 of these selectors
// byte-for-byte for the FOH subset; the rest style the hub, documents, search
// and the PDF viewer that only the full module renders.
const CMP_FULL_CSS_ID = "rolled-compliance-full-css";
const CMP_FULL_CSS = "@keyframes cmpFadeIn{from{opacity:0}to{opacity:1}}@keyframes cmpPopIn{from{opacity:0;transform:scale(0.92) translateY(8px)}to{opacity:1;transform:none}}@keyframes cmpToastIn{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:none}}.cmp-root{--cm-nori:#00392C;--cm-nori-70:rgba(0,57,44,0.7);--cm-nori-60:rgba(0,57,44,0.6);--cm-nori-45:rgba(0,57,44,0.45);--cm-nori-30:rgba(0,57,44,0.3);--cm-nori-18:rgba(0,57,44,0.18);--cm-nori-08:rgba(0,57,44,0.08);--cm-nori-04:rgba(0,57,44,0.04);--cm-wasabi:#D4FB6F;--cm-wasabi-20:rgba(212,251,111,0.2);--cm-kewpie:#FEFAF0;--cm-rice:#fff;--cm-green:#2e9a52;--cm-green-soft:#e6f4ec;--cm-amber:#e89a2b;--cm-amber-soft:#fef3dc;--cm-coral:#c85a5a;--cm-coral-soft:#fbe5e5;--cm-sun:#f1c544;--cm-sun-soft:#fff5d9;--cm-sky:#6c7bbd;--cm-sky-soft:#e7ebf5;--cm-plum:#854fb3;--cm-plum-soft:#f0e6f8;background:var(--cm-kewpie);color:var(--cm-nori);font-family:'Manrope',system-ui,sans-serif;min-height:calc(100vh - 52px);padding:18px}.cmp-root *{box-sizing:border-box}.cmp-root h1,.cmp-root h2,.cmp-root h3,.cmp-root h4{font-family:gelica,serif;letter-spacing:-0.01em;font-weight:700;color:var(--cm-nori)}.cmp-root .cmp-wrap{max-width:1280px;margin:0 auto}.cmp-root .cmp-status-strip{display:flex;gap:10px;flex-wrap:wrap;padding:12px 16px;background:var(--cm-rice);border:1px solid var(--cm-nori-08);border-radius:14px;margin-bottom:16px;font-size:13px;font-weight:700;align-items:center}.cmp-root .cmp-status-strip .cmp-label{font-size:10px;letter-spacing:0.1em;text-transform:uppercase;color:var(--cm-nori-60);font-weight:800;margin-right:4px}.cmp-root .cmp-chip{display:inline-flex;align-items:center;gap:6px;padding:4px 10px;border-radius:999px;font-size:12px;font-weight:700}.cmp-root .cmp-chip.cmp-ok{background:var(--cm-green-soft);color:var(--cm-green)}.cmp-root .cmp-chip.cmp-warn{background:var(--cm-sun-soft);color:var(--cm-amber)}.cmp-root .cmp-chip.cmp-bad{background:var(--cm-coral-soft);color:var(--cm-coral)}.cmp-root .cmp-chip.cmp-info{background:var(--cm-sky-soft);color:var(--cm-sky)}.cmp-root .cmp-chip.cmp-neutral{background:var(--cm-nori-04);color:var(--cm-nori-70)}.cmp-root .cmp-chip .cmp-dot{width:6px;height:6px;border-radius:50%;background:currentColor}.cmp-root .cmp-tile-list{display:flex;flex-direction:column;gap:10px}.cmp-root .cmp-tile-row{background:var(--cm-rice);border:1px solid var(--cm-nori-08);border-radius:14px;padding:14px 16px;display:flex;align-items:center;gap:14px;cursor:pointer;transition:transform 0.12s,box-shadow 0.12s;font-family:'Manrope',sans-serif;color:var(--cm-nori);text-align:left;width:100%}.cmp-root .cmp-tile-row:hover{transform:translateY(-1px);box-shadow:0 4px 12px rgba(0,57,44,0.06)}.cmp-root .cmp-tile-row .cmp-tile-icon{width:44px;height:44px;border-radius:12px;display:flex;align-items:center;justify-content:center;font-size:22px;background:var(--cm-nori);color:var(--cm-wasabi);flex-shrink:0}.cmp-root .cmp-tile-row .cmp-tile-body{flex:1;min-width:0}.cmp-root .cmp-tile-row .cmp-tile-title{font-weight:800;font-size:15px;line-height:1.2}.cmp-root .cmp-tile-row .cmp-tile-meta{font-size:12px;color:var(--cm-nori-60);margin-top:3px}.cmp-root .cmp-tile-row .cmp-tile-right{display:flex;flex-direction:column;align-items:flex-end;gap:4px}.cmp-root .cmp-tile-row .cmp-chevron{opacity:0.3;font-size:18px}.cmp-root .cmp-card{background:var(--cm-rice);border:1px solid var(--cm-nori-08);border-radius:14px;padding:16px 18px;margin-bottom:14px}.cmp-root .cmp-card h3{font-size:18px;margin-bottom:8px}.cmp-root .cmp-card .cmp-sub{font-size:12px;color:var(--cm-nori-60);margin-bottom:10px}.cmp-root .cmp-card.cmp-amber{border-color:var(--cm-amber);background:#fff9ec}.cmp-root .cmp-card.cmp-green{border-color:var(--cm-green);background:#f4faf6}.cmp-root .cmp-card.cmp-coral{border-color:var(--cm-coral);background:#fef5f5}.cmp-root .cmp-field{display:flex;flex-direction:column;gap:4px;min-width:110px;flex:1}.cmp-root .cmp-field label{font-size:10px;font-weight:800;letter-spacing:0.09em;text-transform:uppercase;color:var(--cm-nori-60)}.cmp-root .cmp-field input,.cmp-root .cmp-field select,.cmp-root .cmp-field textarea{padding:9px 12px;border-radius:8px;border:1px solid var(--cm-nori-18);background:var(--cm-rice);color:var(--cm-nori);font-size:14px;outline:none;font-weight:600;font-family:'Manrope',sans-serif}.cmp-root .cmp-field input:focus,.cmp-root .cmp-field select:focus,.cmp-root .cmp-field textarea:focus{border-color:var(--cm-nori)}.cmp-root .cmp-field textarea{resize:vertical;min-height:68px;font-weight:500}.cmp-root .cmp-field.cmp-warn input,.cmp-root .cmp-field.cmp-warn select,.cmp-root .cmp-field.cmp-warn textarea{border-color:var(--cm-coral);background:#fff5f5}.cmp-root .cmp-btn{padding:10px 16px;border-radius:10px;font-weight:700;font-size:13px;letter-spacing:0.02em;cursor:pointer;display:inline-flex;align-items:center;gap:8px;border:none;font-family:'Manrope',sans-serif}.cmp-root .cmp-btn.cmp-btn-primary{background:var(--cm-nori);color:var(--cm-wasabi)}.cmp-root .cmp-btn.cmp-btn-ghost{background:var(--cm-nori-04);color:var(--cm-nori)}.cmp-root .cmp-btn.cmp-btn-danger{background:var(--cm-coral);color:#fff}.cmp-root .cmp-btn.cmp-btn-wasabi{background:var(--cm-wasabi);color:var(--cm-nori);font-weight:800}.cmp-root .cmp-btn.cmp-btn-sm{padding:7px 11px;font-size:12px}.cmp-root .cmp-btn:disabled{opacity:0.4;cursor:not-allowed}.cmp-root .cmp-matrix{width:100%;border-collapse:collapse;font-size:12px}.cmp-root .cmp-matrix th{text-align:center;font-size:10px;letter-spacing:0.08em;text-transform:uppercase;color:var(--cm-nori-60);padding:8px 6px;font-weight:800;border-bottom:1px solid var(--cm-nori-08)}.cmp-root .cmp-matrix th.cmp-task{text-align:left;width:30%}.cmp-root .cmp-matrix td{padding:5px;border-bottom:1px dashed var(--cm-nori-08);text-align:center;color:var(--cm-nori)}.cmp-root .cmp-matrix td.cmp-task{text-align:left;padding:10px 8px}.cmp-root .cmp-matrix .cmp-tname{font-weight:700;font-size:13px}.cmp-root .cmp-matrix .cmp-trow{font-size:10.5px;color:var(--cm-nori-60);margin-top:2px;line-height:1.3}.cmp-root .cmp-matrix .cmp-cell-btn{width:100%;height:42px;border-radius:8px;border:1px solid var(--cm-nori-18);background:#fff;cursor:pointer;font-size:12px;font-weight:700;display:flex;align-items:center;justify-content:center;color:var(--cm-nori-45);transition:all 0.12s;font-family:'Manrope',sans-serif}.cmp-root .cmp-matrix .cmp-cell-btn:hover{background:var(--cm-nori-04)}.cmp-root .cmp-matrix .cmp-cell-btn.cmp-done{background:var(--cm-wasabi-20);border-color:var(--cm-wasabi);color:var(--cm-nori);font-family:gelica,serif;font-size:13px;font-weight:800;letter-spacing:0.03em}.cmp-root .cmp-matrix .cmp-cell-btn.cmp-skipped{background:var(--cm-nori-08);border-color:var(--cm-nori-18);color:var(--cm-nori-60);font-family:gelica,serif;font-size:13px;font-weight:800;letter-spacing:0.03em}.cmp-root .cmp-matrix .cmp-cell-btn.cmp-today{box-shadow:0 0 0 2px var(--cm-wasabi)}.cmp-root .cmp-row-table{width:100%;border-collapse:collapse;font-size:12.5px;color:var(--cm-nori)}.cmp-root .cmp-row-table th{text-align:left;font-size:10px;letter-spacing:0.08em;text-transform:uppercase;color:var(--cm-nori-60);padding:8px 10px;font-weight:800;border-bottom:1px solid var(--cm-nori-08)}.cmp-root .cmp-row-table td{padding:8px 10px;border-bottom:1px dashed var(--cm-nori-08);vertical-align:middle}.cmp-root .cmp-row-table .cmp-badge{display:inline-flex;padding:2px 7px;border-radius:6px;font-size:10.5px;font-weight:800;letter-spacing:0.03em}.cmp-root .cmp-row-table .cmp-badge.cmp-ok{background:var(--cm-green-soft);color:var(--cm-green)}.cmp-root .cmp-row-table .cmp-badge.cmp-bad{background:var(--cm-coral-soft);color:var(--cm-coral)}.cmp-root .cmp-form-modal-scrim{position:fixed;inset:0;background:rgba(0,57,44,0.55);z-index:600;display:flex;align-items:center;justify-content:center;padding:18px;animation:cmpFadeIn 0.15s}.cmp-root .cmp-form-modal{background:var(--cm-nori);color:var(--cm-kewpie);border-radius:16px;max-width:520px;width:100%;padding:0;animation:cmpPopIn 0.2s;border:1px solid rgba(212,251,111,0.2);max-height:92vh;display:flex;flex-direction:column}.cmp-root .cmp-form-modal h3{color:var(--cm-wasabi);font-size:18px;margin-bottom:6px}.cmp-root .cmp-form-modal .cmp-form-desc{font-size:13px;color:rgba(254,250,240,0.75);margin-bottom:6px;line-height:1.4}.cmp-root .cmp-form-modal .cmp-form-inner{flex:1;overflow-y:auto;padding:12px 24px;display:flex;flex-direction:column;gap:12px}.cmp-root .cmp-form-modal .cmp-form-head{padding:22px 24px 0 24px}.cmp-root .cmp-form-modal .cmp-form-foot{display:flex;gap:10px;padding:14px 24px 22px;justify-content:flex-end;border-top:1px solid rgba(254,250,240,0.08)}.cmp-root .cmp-form-modal .cmp-form-cancel{padding:10px 16px;background:transparent;color:rgba(254,250,240,0.6);font-size:13px;font-weight:700;border-radius:10px;cursor:pointer;border:none;font-family:'Manrope',sans-serif}.cmp-root .cmp-form-modal .cmp-form-submit{padding:10px 18px;border-radius:8px;background:var(--cm-wasabi);color:var(--cm-nori);font-weight:800;font-size:13px;border:none;font-family:'Manrope',sans-serif;cursor:pointer}.cmp-root .cmp-form-modal .cmp-field-block{display:flex;flex-direction:column;gap:5px}.cmp-root .cmp-form-modal .cmp-field-block label{font-size:11px;font-weight:800;letter-spacing:0.06em;text-transform:uppercase;color:rgba(254,250,240,0.65)}.cmp-root .cmp-form-modal .cmp-field-block input,.cmp-root .cmp-form-modal .cmp-field-block textarea,.cmp-root .cmp-form-modal .cmp-field-block select{padding:10px 12px;border-radius:8px;background:rgba(254,250,240,0.08);color:var(--cm-kewpie);border:1px solid rgba(254,250,240,0.18);font-size:14px;font-family:'Manrope',sans-serif;outline:none}.cmp-root .cmp-form-modal .cmp-field-block textarea{resize:vertical;min-height:70px}.cmp-root .cmp-form-modal .cmp-field-block .cmp-hint{font-size:11px;color:rgba(254,250,240,0.55)}.cmp-root .cmp-ca-row{display:flex;align-items:flex-start;gap:12px;padding:12px 14px;background:var(--cm-rice);border:1px solid var(--cm-nori-08);border-radius:10px;margin-bottom:8px}.cmp-root .cmp-ca-row.cmp-open{border-left:3px solid var(--cm-coral)}.cmp-root .cmp-ca-row.cmp-resolved{opacity:0.7}.cmp-root .cmp-ca-row .cmp-pill{width:36px;height:36px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:16px;flex-shrink:0;font-weight:800}.cmp-root .cmp-ca-row .cmp-pill.cmp-open{background:var(--cm-coral-soft);color:var(--cm-coral)}.cmp-root .cmp-ca-row .cmp-pill.cmp-resolved{background:var(--cm-green-soft);color:var(--cm-green)}.cmp-root .cmp-ca-row .cmp-body{flex:1;min-width:0}.cmp-root .cmp-ca-row .cmp-desc{font-size:13.5px;font-weight:700;line-height:1.35;margin-bottom:4px;color:var(--cm-nori)}.cmp-root .cmp-ca-row .cmp-meta{font-size:11.5px;color:var(--cm-nori-60);line-height:1.35}.cmp-root .cmp-tabs{display:flex;gap:4px;background:var(--cm-nori-04);padding:3px;border-radius:10px;margin-bottom:12px}.cmp-root .cmp-tabs button{padding:8px 14px;border-radius:8px;font-size:12.5px;font-weight:700;color:var(--cm-nori-70);background:transparent;border:none;cursor:pointer;font-family:'Manrope',sans-serif}.cmp-root .cmp-tabs button.cmp-active{background:var(--cm-nori);color:var(--cm-wasabi)}.cmp-root .cmp-toast-layer{position:fixed;bottom:20px;left:50%;transform:translateX(-50%);display:flex;flex-direction:column;gap:8px;z-index:700;pointer-events:none;align-items:center}.cmp-root .cmp-toast{background:var(--cm-nori);color:var(--cm-kewpie);padding:11px 16px;border-radius:10px;font-size:13px;font-weight:700;box-shadow:0 6px 24px rgba(0,0,0,0.15);animation:cmpToastIn 0.22s;max-width:92vw;pointer-events:auto;display:flex;align-items:center;gap:8px}.cmp-root .cmp-toast.cmp-success{background:var(--cm-green);color:#fff}.cmp-root .cmp-toast.cmp-warn{background:var(--cm-amber);color:#fff}.cmp-root .cmp-toast.cmp-error{background:var(--cm-coral);color:#fff}.cmp-root .cmp-loading{padding:48px 16px;text-align:center;color:var(--cm-nori-60);font-size:14px;font-weight:600}.cmp-root .cmp-empty{padding:32px 16px;text-align:center;color:var(--cm-nori-60);font-size:13px}.cmp-root .cmp-confirm-scrim{position:fixed;inset:0;background:rgba(0,57,44,0.55);z-index:600;display:flex;align-items:center;justify-content:center;padding:18px}.cmp-root .cmp-confirm-modal{background:var(--cm-nori);color:var(--cm-kewpie);border-radius:16px;max-width:380px;width:100%;padding:24px;text-align:center;animation:cmpPopIn 0.2s}@media(max-width:900px){.cmp-root .cmp-matrix-scroll{overflow-x:auto}.cmp-root .cmp-matrix{min-width:760px}}.cmp-root .cmp-search-bar{position:relative;display:flex;align-items:center;gap:10px;padding:10px 14px;background:var(--cm-rice);border:1px solid var(--cm-nori-08);border-radius:14px;margin-bottom:14px}.cmp-root .cmp-search-bar .cmp-search-icon{font-size:18px;opacity:0.5}.cmp-root .cmp-search-bar input{flex:1;padding:8px 0;border:none;outline:none;background:transparent;color:var(--cm-nori);font-size:14px;font-weight:600;font-family:'Manrope',sans-serif}.cmp-root .cmp-search-bar input::placeholder{color:var(--cm-nori-45);font-weight:500}.cmp-root .cmp-search-bar .cmp-search-clear{background:none;border:none;font-size:18px;color:var(--cm-nori-45);cursor:pointer;padding:0 4px}.cmp-root .cmp-search-results{position:absolute;top:calc(100% + 4px);left:0;right:0;background:var(--cm-rice);border:1px solid var(--cm-nori-08);border-radius:14px;max-height:60vh;overflow-y:auto;z-index:200;box-shadow:0 10px 30px rgba(0,57,44,0.12)}.cmp-root .cmp-search-empty{padding:14px 16px;color:var(--cm-nori-60);font-size:13px;text-align:center}.cmp-root .cmp-search-result{display:flex;align-items:flex-start;gap:10px;padding:10px 14px;cursor:pointer;border-bottom:1px solid var(--cm-nori-04)}.cmp-root .cmp-search-result:last-child{border-bottom:none}.cmp-root .cmp-search-result:hover{background:var(--cm-nori-04)}.cmp-root .cmp-search-result .cmp-cat{font-size:9.5px;font-weight:800;letter-spacing:0.06em;text-transform:uppercase;color:var(--cm-nori-60);background:var(--cm-nori-04);padding:3px 7px;border-radius:5px;flex-shrink:0;margin-top:2px}.cmp-root .cmp-search-result .cmp-result-title{flex:1;font-size:13px;font-weight:700;color:var(--cm-nori);line-height:1.3}.cmp-root .cmp-search-result .cmp-result-meta{font-size:11.5px;font-weight:500;color:var(--cm-nori-60);margin-top:2px}.cmp-root .cmp-home-grid{display:grid;grid-template-columns:1.1fr 1fr;gap:24px}.cmp-root .cmp-col-head{display:flex;justify-content:space-between;align-items:baseline;margin-bottom:10px;padding:0 2px}.cmp-root .cmp-col-head h2{font-size:16px;letter-spacing:0.04em;text-transform:uppercase;color:var(--cm-nori-60);font-family:'Manrope',sans-serif;font-weight:800}.cmp-root .cmp-col-head .cmp-col-sub{font-size:11px;color:var(--cm-nori-45);font-weight:600}.cmp-root .cmp-hub-grid{display:grid;grid-template-columns:1fr 1fr;gap:10px}.cmp-root .cmp-hub-card{background:var(--cm-rice);border:1px solid var(--cm-nori-08);border-radius:14px;padding:14px;cursor:pointer;transition:transform 0.12s,box-shadow 0.12s;font-family:'Manrope',sans-serif;color:var(--cm-nori);text-align:left;width:100%;display:flex;flex-direction:column;gap:5px;border-left-width:1px}.cmp-root .cmp-hub-card:hover{transform:translateY(-1px);box-shadow:0 4px 12px rgba(0,57,44,0.06)}.cmp-root .cmp-hub-card .cmp-hub-icon{font-size:24px}.cmp-root .cmp-hub-card .cmp-hub-title{font-size:13.5px;font-weight:800;line-height:1.2}.cmp-root .cmp-hub-card .cmp-hub-meta{font-size:11px;color:var(--cm-nori-60);font-weight:500}.cmp-root .cmp-hub-card .cmp-hub-state{margin-top:4px;font-size:10.5px;font-weight:800;letter-spacing:0.04em;padding:2px 7px;border-radius:5px;align-self:flex-start}.cmp-root .cmp-hub-card .cmp-hub-state.cmp-state-ok{background:var(--cm-green-soft);color:var(--cm-green)}.cmp-root .cmp-hub-card .cmp-hub-state.cmp-state-warn{background:var(--cm-sun-soft);color:var(--cm-amber)}.cmp-root .cmp-hub-card .cmp-hub-state.cmp-state-bad{background:var(--cm-coral-soft);color:var(--cm-coral)}.cmp-root .cmp-doc-list{display:flex;flex-direction:column;gap:6px}.cmp-root .cmp-doc-row{display:flex;align-items:flex-start;gap:10px;padding:10px 12px;background:var(--cm-rice);border:1px solid var(--cm-nori-08);border-radius:10px;cursor:pointer;transition:transform 0.1s,box-shadow 0.1s}.cmp-root .cmp-doc-row:hover{transform:translateY(-1px);box-shadow:0 2px 8px rgba(0,57,44,0.06)}.cmp-root .cmp-doc-row .cmp-doc-icon{font-size:18px;flex-shrink:0;margin-top:1px}.cmp-root .cmp-doc-row .cmp-doc-body{flex:1;min-width:0}.cmp-root .cmp-doc-row .cmp-doc-name{font-size:13px;font-weight:700;line-height:1.3}.cmp-root .cmp-doc-row .cmp-doc-meta{font-size:11px;color:var(--cm-nori-60);font-weight:500;margin-top:2px}.cmp-root .cmp-doc-row .cmp-doc-right{flex-shrink:0;display:flex;align-items:center;gap:8px}.cmp-root .cmp-pdf-scrim{position:fixed;inset:0;background:rgba(0,57,44,0.65);z-index:700;display:flex;align-items:center;justify-content:center;padding:18px;animation:cmpFadeIn 0.15s}.cmp-root .cmp-pdf-modal{background:var(--cm-rice);color:var(--cm-nori);border-radius:16px;max-width:780px;width:100%;max-height:92vh;display:flex;flex-direction:column;animation:cmpPopIn 0.2s;overflow:hidden}.cmp-root .cmp-pdf-head{padding:18px 22px 14px;border-bottom:1px solid var(--cm-nori-08);display:flex;justify-content:space-between;align-items:flex-start;gap:14px}.cmp-root .cmp-pdf-head h3{font-size:18px;margin:0 0 4px}.cmp-root .cmp-pdf-head .cmp-pdf-meta{font-size:11.5px;color:var(--cm-nori-60);font-weight:600}.cmp-root .cmp-pdf-close{background:var(--cm-nori-04);border:none;border-radius:50%;width:32px;height:32px;font-size:16px;cursor:pointer;color:var(--cm-nori);font-weight:700;flex-shrink:0}.cmp-root .cmp-pdf-body{flex:1;overflow-y:auto;padding:18px 22px;font-size:13.5px;line-height:1.55;color:var(--cm-nori)}.cmp-root .cmp-pdf-body p{margin-bottom:10px}.cmp-root .cmp-pdf-body .cmp-pdf-section-head{font-size:11px;font-weight:800;letter-spacing:0.06em;text-transform:uppercase;color:var(--cm-nori-60);margin:14px 0 6px}@media(max-width:900px){.cmp-root .cmp-home-grid{grid-template-columns:1fr;gap:14px}.cmp-root .cmp-hub-grid{grid-template-columns:1fr 1fr}}@media(max-width:540px){.cmp-root .cmp-hub-grid{grid-template-columns:1fr}}.cmp-root .cmp-form-modal-scrim,.cmp-root .cmp-confirm-scrim{z-index:480}.cmp-root .cmp-pdf-scrim,.cmp-root .cmp-toast-layer{z-index:490}";
function ensureCss(){
  if (typeof document === "undefined" || document.getElementById(CMP_FULL_CSS_ID)) return;
  const el = document.createElement("style");
  el.id = CMP_FULL_CSS_ID;
  el.textContent = CMP_FULL_CSS;
  document.head.appendChild(el);
}

function cmpMondayOf(dateStrOrDate) {
  // TZ-safe: parse YYYY-MM-DD strings as LOCAL midnight (not UTC) and
  // format via local Y/M/D so a BST/GMT offset can't shift the result
  // by a day. Without this, "2026-04-28" parses as UTC midnight, comes
  // out as 01:00 BST Tuesday, and Monday-of-week resolves to the
  // previous Sunday.
  var d;
  if (dateStrOrDate instanceof Date) {
    d = new Date(dateStrOrDate.getFullYear(), dateStrOrDate.getMonth(), dateStrOrDate.getDate());
  } else if (typeof dateStrOrDate === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateStrOrDate)) {
    var parts = dateStrOrDate.split("-").map(Number);
    d = new Date(parts[0], parts[1] - 1, parts[2]);
  } else {
    d = new Date(dateStrOrDate);
    d = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  }
  var dow = (d.getDay() + 6) % 7; // Mon=0
  d.setDate(d.getDate() - dow);
  var yy = d.getFullYear();
  var mm = d.getMonth() + 1;
  var dd = d.getDate();
  return yy + "-" + String(mm).padStart(2, "0") + "-" + String(dd).padStart(2, "0");
}
function cmpFriendly(iso) {
  if (!iso) return "—";
  var d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", {day: "2-digit", month: "short"});
}
function cmpInitials(name) {
  return String(name || "").trim().slice(0, 2).toUpperCase() || "??";
}
function cmpIsTestMode() {
  try {
    if (typeof window !== "undefined" && window.__cmpTestMode === true) return true;
    // localStorage flag survives reloads — set with localStorage.setItem('cmpTestMode','1')
    // before kicking off a QA run. Cleared with localStorage.removeItem('cmpTestMode').
    if (typeof localStorage !== "undefined" && localStorage.getItem("cmpTestMode") === "1") return true;
  } catch(_) {}
  return false;
}

var __cmpToastPush = null;
function cmpToast(msg, tone) {
  if (__cmpToastPush) __cmpToastPush({msg: msg, tone: tone || "", id: Date.now() + Math.random()});
}
function CmpToastLayer() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    __cmpToastPush = (t) => {
      setItems(p => [...p, t]);
      setTimeout(() => setItems(p => p.filter(x => x.id !== t.id)), 3200);
    };
    return () => { __cmpToastPush = null; };
  }, []);
  return (
    <div className="cmp-toast-layer">
      {items.map(t => (
        <div key={t.id} className={"cmp-toast " + (t.tone === "success" ? "cmp-success" : t.tone === "warn" ? "cmp-warn" : t.tone === "error" ? "cmp-error" : "")}>{t.msg}</div>
      ))}
    </div>
  );
}

// Single-helper backend writer — envelope keys (action / event / site /
// staffPin / testMode) MUST win over payload (the 95cef98 fix). Order
// matters: payload first, envelope second so Object.assign overwrites.
function cmpComplianceLog(backend, site, event, payload, staffPin) {
  var body = Object.assign({}, payload || {}, {
    action: "complianceLog",
    event: event,
    site: site,
    staffPin: staffPin,
    testMode: cmpIsTestMode()
  });
  return fetch(backend, {
    method: "POST",
    headers: {"Content-Type": "text/plain;charset=utf-8"},
    body: JSON.stringify(body)
  }).then(function(r) {
    if (!r.ok) throw new Error("HTTP " + r.status);
    return r.json();
  }).then(function(d) {
    if (d && d.status === "error") throw new Error(d.message || "Server error");
    // Phase 3 dual-run shadow (best-effort, fire-and-forget): mirror the
    // ACCEPTED write into Supabase, carrying the old backend's response so
    // its minted ids (incidentId / batchId / correctiveActionId) become the
    // case links (legacy_ref). Old write failed = nothing happened = no
    // shadow; a shadow failure never reaches this promise chain — the gap
    // shows up in the reconcile, never on the kitchen.
    try {
      complianceSync.recordComplianceLog({
        siteKey: site, event: event, payload: payload || {},
        staffPin: staffPin, testMode: cmpIsTestMode(), response: d
      }).catch(function(){});
    } catch(_e){}
    return d;
  });
}

// HACCP uses its own action (haccpEvent / haccpSign) rather than
// complianceLog. Wrappers preserve the testMode plumbing.
function cmpHaccpEvent(backend, site, payload, staffPin) {
  var body = Object.assign({}, payload || {}, {
    action: "haccpEvent",
    site: site,
    staffPin: staffPin,
    testMode: cmpIsTestMode()
  });
  return fetch(backend, {
    method: "POST",
    headers: {"Content-Type": "text/plain;charset=utf-8"},
    body: JSON.stringify(body)
  }).then(function(r) {
    if (!r.ok) throw new Error("HTTP " + r.status);
    return r.json();
  }).then(function(d) {
    if (d && d.status === "error") throw new Error(d.message || "Server error");
    // Phase 3 dual-run shadow — same contract as cmpComplianceLog above.
    try {
      var p = payload || {};
      complianceSync.recordHaccpEvent({
        siteKey: site, section: p.section, field: p.field, value: p.value,
        extra: p.extra || null, date: p.date || null,
        staffPin: staffPin, testMode: cmpIsTestMode()
      }).catch(function(){});
    } catch(_e){}
    return d;
  });
}
function cmpHaccpSign(backend, site, payload, staffPin) {
  var body = Object.assign({}, payload || {}, {
    action: "haccpSign",
    site: site,
    staffPin: staffPin,
    testMode: cmpIsTestMode()
  });
  return fetch(backend, {
    method: "POST",
    headers: {"Content-Type": "text/plain;charset=utf-8"},
    body: JSON.stringify(body)
  }).then(function(r) {
    if (!r.ok) throw new Error("HTTP " + r.status);
    return r.json();
  }).then(function(d) {
    if (d && d.status === "error") throw new Error(d.message || "Server error");
    // Phase 3 dual-run shadow — the chef's day sign-off. The sheet remains
    // the legal record during dual-run; this row is the copy that lets the
    // reconcile prove the new home is trustworthy before any flip.
    try {
      complianceSync.recordHaccpSign({
        siteKey: site, date: (payload && payload.date) || null,
        staffPin: staffPin, testMode: cmpIsTestMode()
      }).catch(function(){});
    } catch(_e){}
    return d;
  });
}

var CMP_CONFIG_TTL_MS = 5 * 60 * 1000;
function cmpFetchComplianceConfig(backend, site, opts) {
  var force = opts && opts.force;
  // Wave 6 review fix (2026-04-30): include testMode in the cache key so
  // toggling the QA flag busts the cache instead of serving up to 5
  // minutes of stale-mode payload.
  var cacheKey = "cmpComplianceConfig." + site + "." + (cmpIsTestMode() ? "test" : "prod");
  var nowMs = Date.now();
  if (!force) {
    try {
      var raw = localStorage.getItem(cacheKey);
      if (raw) {
        var cached = JSON.parse(raw);
        if (cached && (nowMs - cached.ts) < CMP_CONFIG_TTL_MS) {
          return Promise.resolve(cached.payload);
        }
      }
    } catch(_) {}
  }
  // The existing Google-Sheet fetch, unchanged — the OFF path AND the config reader's
  // fallback on any Supabase error/drift. Keeps the 5-min cache write byte-for-byte.
  var sheetFetch = function() {
    return fetch(backend, {
      method: "POST",
      headers: {"Content-Type": "text/plain;charset=utf-8"},
      body: JSON.stringify({action: "getComplianceConfig", site: site, testMode: cmpIsTestMode()})
    }).then(function(r) {
      if (!r.ok) throw new Error("HTTP " + r.status);
      return r.json();
    }).then(function(d) {
      if (d && d.status === "error") throw new Error(d.message || "Config fetch error");
      try { localStorage.setItem(cacheKey, JSON.stringify({ts: nowMs, payload: d})); } catch(_) {}
      return d;
    });
  };
  // Lane BK config read-flip (gated OFF by default — COMPLIANCE_CONFIG_READ_HOSTS empty).
  // With the gate off this returns exactly the sheet fetch above (OFF path byte-identical).
  // ON: serve Supabase ONLY when it resolves to the same lists as the live Sheet (the
  // drift guard inside readComplianceConfig); on drift/error -> the Sheet. The returned
  // config is cached under the same 5-min key so the resolvers/screens are unchanged.
  // Lane BV: on an AUTHORITATIVE host (COMPLIANCE_CONFIG_AUTHORITATIVE_HOSTS / ?compconfigauth=on,
  // empty today) there is NO Sheet fallback — the Sheet copy goes stale once the Command Centre
  // stops writing it — so a Supabase failure rejects like a failed Sheet fetch does today (the
  // screens' existing catch -> "Couldn't load …" toast). Guarded hosts keep the Sheet fallback.
  if (complianceReader.complianceConfigReadEnabled()) {
    var fallback = complianceReader.complianceConfigAuthoritativeEnabled()
      ? function() { return Promise.reject(new Error("Config unavailable (this tablet reads the live record only, no backup fallback)")); }
      : sheetFetch;
    return complianceReader.readComplianceConfig({ site: site, testMode: cmpIsTestMode(), sheetFetch: sheetFetch })
      .then(function(cfg) {
        if (!cfg) return fallback();
        try { localStorage.setItem(cacheKey, JSON.stringify({ts: nowMs, payload: cfg})); } catch(_) {}
        return cfg;
      })
      .catch(function() { return fallback(); });
  }
  return sheetFetch();
}

// Resolves "items" array per (surfaceKey, siteKey). Falls back to '*'
// if no per-site override. Kitchen sends site:"mcr"; backend normalises
// to "kitchen" — config.site fallback handles the round-trip.
function cmpResolveSurfaceItems(config, surfaceKey, siteKey) {
  if (!config || !config.surfaces || !config.surfaces[surfaceKey]) return [];
  var sf = config.surfaces[surfaceKey];
  var lookup = (config.site || siteKey);
  var perSite = sf.sites && (sf.sites[lookup] || sf.sites[siteKey] || sf.sites["*"]);
  if (!perSite) return [];
  return (perSite.items || []).filter(function(i) { return (i.status || "active") === "active"; });
}

// Same lookup WITHOUT the '*' fallback, for surfaces that describe physical kit
// (fridges, probes): the Command Centre seeds those at '*' with the closed
// kitchen's units, and a site must never inherit another building's fridges.
// No site override → [] → complianceUnits.js decides (legacy kitchen / empty).
function cmpResolveSiteOnlyItems(config, surfaceKey, siteKey) {
  if (!config || !config.surfaces || !config.surfaces[surfaceKey]) return [];
  var sf = config.surfaces[surfaceKey];
  var lookup = (config.site || siteKey);
  var perSite = sf.sites && (sf.sites[lookup] || sf.sites[siteKey]);
  if (!perSite || (perSite.status || "active") === "archived") return [];
  return (perSite.items || []).filter(function(i) { return (i.status || "active") === "active"; });
}

// Hydrates a form's state from the audit trail. Public endpoint, masked
// PIN attribution, whitelisted tab names. Returns rows newest-first for
// the past `days` (default 7). Each row is {_ts, ...schema columns}.
function cmpFetchFormToday(backend, site, form, days) {
  // The existing Google-Sheet fetch, unchanged — this is the OFF path AND the
  // reader's fallback on any Supabase error.
  var sheetFetch = function() {
    return fetch(backend, {
      method: "POST",
      headers: {"Content-Type": "text/plain;charset=utf-8"},
      body: JSON.stringify({
        action: "getComplianceFormToday",
        site: site,
        form: form,
        days: days || 7,
        testMode: cmpIsTestMode()
      })
    }).then(function(r) {
      if (!r.ok) throw new Error("HTTP " + r.status);
      return r.json();
    }).then(function(d) {
      if (d && d.status === "error") throw new Error(d.message || "Reader error");
      return (d && d.rows) || [];
    });
  };
  // Lane AK read-flip (gated OFF by default — COMPLIANCE_READ_HOSTS is empty).
  // With the gate off this returns exactly the sheet rows above.
  if (complianceReader.complianceReadEnabled()) {
    return complianceReader.readComplianceFormToday(site, form, days, { testMode: cmpIsTestMode(), sheetFetch: sheetFetch })
      .then(function(res) { return (res && res.rows) || []; })
      .catch(function() { return sheetFetch(); });
  }
  return sheetFetch();
}

// Today's ISO date prefix (YYYY-MM-DD) — TZ-safe, formatted from local
// Y/M/D so a UTC offset can't push us into yesterday around midnight.
function cmpTodayIso() {
  var d = new Date();
  var yy = d.getFullYear();
  var mm = d.getMonth() + 1;
  var dd = d.getDate();
  return yy + "-" + String(mm).padStart(2, "0") + "-" + String(dd).padStart(2, "0");
}

// Fetches a date-range slice of historical compliance form rows. Used
// by every form's CmpCalendarButton — when staff tap a past day (or
// past week for cleaning / pest-monday), the form switches to read-only
// mode populated from this response. Same whitelist + masking as
// cmpFetchFormToday but with explicit dateFrom/dateTo bounds.
function cmpFetchFormHistory(backend, site, form, dateFrom, dateTo) {
  var sheetFetch = function() {
    return fetch(backend, {
      method: "POST",
      headers: {"Content-Type": "text/plain;charset=utf-8"},
      body: JSON.stringify({
        action: "getComplianceFormHistory",
        site: site,
        form: form,
        dateFrom: dateFrom,
        dateTo: dateTo,
        testMode: cmpIsTestMode()
      })
    }).then(function(r) {
      if (!r.ok) throw new Error("HTTP " + r.status);
      return r.json();
    }).then(function(d) {
      if (d && d.status === "error") throw new Error(d.message || "History reader error");
      return (d && d.rows) || [];
    });
  };
  // Lane AK read-flip (gated OFF by default). OFF path = the sheet fetch above.
  if (complianceReader.complianceReadEnabled()) {
    return complianceReader.readComplianceFormHistory(site, form, dateFrom, dateTo, { testMode: cmpIsTestMode(), sheetFetch: sheetFetch })
      .then(function(res) { return (res && res.rows) || []; })
      .catch(function() { return sheetFetch(); });
  }
  return sheetFetch();
}

// HACCP single-day reader. Distinct from cmpFetchFormHistory because
// HACCP returns a structured {sheet, signOff, status} object — not a
// flat row array — and lives in two backend tabs (HACCP Daily Sheet +
// HACCP Events) with their own CCP-keyed shape.
function cmpFetchHaccpDay(backend, site, date) {
  var sheetFetch = function() {
    return fetch(backend, {
      method: "POST",
      headers: {"Content-Type": "text/plain;charset=utf-8"},
      body: JSON.stringify({
        action: "haccpToday",
        site: site,
        date: date,
        testMode: cmpIsTestMode()
      })
    }).then(function(r) {
      if (!r.ok) throw new Error("HTTP " + r.status);
      return r.json();
    });
  };
  // Lane AK read-flip. The HACCP daily sheet is the LEGAL signature, so it has
  // its OWN gate (haccpReadEnabled), flipped separately from the everyday ticks
  // and only on Adam's dated legal sign-off. OFF by default → sheetFetch, i.e.
  // the same structured {date, site, status, sheet, signOff} object as before.
  if (complianceReader.haccpReadEnabled()) {
    return complianceReader.readHaccpDay(site, date, { testMode: cmpIsTestMode(), sheetFetch: sheetFetch })
      .then(function(res) { return res || sheetFetch(); })
      .catch(function() { return sheetFetch(); });
  }
  return sheetFetch();
}

// Calendar button → modal grid → onPick(YYYY-MM-DD or YYYY-MM-DD of
// Monday for week-mode). Lifted from compliance-demo.html (2026-04-30).
// Mode "day" picks a single date; mode "week" picks the Monday of the
// chosen week (used by Cleaning + Pest Monday Sweep). Future dates are
// disabled. Pill label: "History" by default, "<friendly date>" when
// viewing past day, "Week of <Mon>" when viewing past week.
function CmpCalendarButton({onPick, viewingDate, mode}) {
  mode = mode || "day";
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const todayStr = cmpTodayIso();
  const daysInMonth = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const firstDay = (new Date(month.getFullYear(), month.getMonth(), 1).getDay() + 6) % 7; // Mon=0
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  // TZ-safe: format from local Y/M/D directly so BST/GMT offset can't
  // shift the date backwards via UTC conversion. Bug seen 2026-04-30:
  // toISOString() on midnight-BST returned the previous day in UTC.
  const iso = (d) => {
    const yy = month.getFullYear();
    const mm = month.getMonth() + 1;
    return yy + "-" + String(mm).padStart(2, "0") + "-" + String(d).padStart(2, "0");
  };
  const now = new Date();
  const isFuture = (d) => new Date(month.getFullYear(), month.getMonth(), d) > now;
  const isToday = (d) => iso(d) === todayStr;
  const viewingMonday = (mode === "week" && viewingDate) ? cmpMondayOf(viewingDate) : viewingDate;
  const isViewing = (d) => mode === "week" ? cmpMondayOf(iso(d)) === viewingMonday : iso(d) === viewingDate;
  const monthLabel = month.toLocaleDateString("en-GB", {month: "long", year: "numeric"});
  const todayMon = cmpMondayOf(todayStr);
  const isDefault = !viewingDate || viewingDate === todayStr || (mode === "week" && viewingMonday === todayMon);
  const btnLabel = isDefault
    ? "History"
    : (mode === "week" ? "Week of " + cmpFriendly(viewingMonday) : cmpFriendly(viewingDate));
  const handlePick = (d) => {
    const picked = mode === "week" ? cmpMondayOf(iso(d)) : iso(d);
    onPick(picked);
    setOpen(false);
  };
  const resetTarget = mode === "week" ? todayMon : todayStr;
  return (
    <>
      <button className={"cmp-btn cmp-btn-sm " + (!isDefault ? "cmp-btn-primary" : "cmp-btn-ghost")} onClick={() => setOpen(true)}>
        📅 {btnLabel}
      </button>
      {open && (
        <div className="cmp-form-modal-scrim" onClick={() => setOpen(false)} style={{zIndex:180}}>
          <div className="cmp-form-modal" onClick={e => e.stopPropagation()} style={{maxWidth:360,padding:18}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
              <button className="cmp-btn cmp-btn-sm cmp-btn-ghost" onClick={() => setMonth(m => new Date(m.getFullYear(), m.getMonth() - 1, 1))}>‹</button>
              <h3 style={{color:"var(--cm-wasabi)",margin:0}}>{monthLabel}</h3>
              <button className="cmp-btn cmp-btn-sm cmp-btn-ghost" onClick={() => setMonth(m => new Date(m.getFullYear(), m.getMonth() + 1, 1))}>›</button>
            </div>
            {mode === "week" && (
              <div style={{color:"rgba(254,250,240,0.55)",textAlign:"center",marginBottom:8,fontSize:11}}>Tap any day to load that week (Mon–Sun)</div>
            )}
            <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:3,marginBottom:8}}>
              {["M","T","W","T","F","S","S"].map((l,i) => (
                <div key={i} style={{textAlign:"center",fontSize:10,letterSpacing:"0.06em",color:"rgba(254,250,240,0.5)",fontWeight:800,padding:"6px 0"}}>{l}</div>
              ))}
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:3}}>
              {cells.map((d,i) => {
                if (d === null) return <div key={i} />;
                const disabled = isFuture(d);
                const today = isToday(d);
                const viewing = isViewing(d);
                return (
                  <button key={i} disabled={disabled}
                    onClick={() => handlePick(d)}
                    style={{
                      background: viewing ? "var(--cm-wasabi)" : today ? "rgba(212,251,111,0.18)" : "rgba(254,250,240,0.06)",
                      color: viewing ? "var(--cm-nori)" : today ? "var(--cm-wasabi)" : "var(--cm-kewpie)",
                      border: "none", borderRadius: 8, padding: "9px 0",
                      fontSize: 12.5, fontWeight: viewing || today ? 800 : 600,
                      cursor: disabled ? "not-allowed" : "pointer",
                      opacity: disabled ? 0.25 : 1,
                      fontFamily: "inherit"
                    }}>
                    {d}
                  </button>
                );
              })}
            </div>
            <button className="cmp-btn cmp-btn-primary" style={{width:"100%",marginTop:12}} onClick={() => { onPick(resetTarget); setOpen(false); }}>← Back to {mode === "week" ? "this week" : "today"}</button>
            <div style={{color:"rgba(254,250,240,0.55)",textAlign:"center",fontSize:11,marginTop:8}}>{mode === "week" ? "Past weeks open read-only. Future weeks disabled." : "Past days open read-only. Today is editable. Future dates disabled."}</div>
          </div>
        </div>
      )}
    </>
  );
}

// Banner shown at the top of every form when viewing a past date.
// Mirrors compliance-demo.html HistoryBanner. Plum tone signals
// "historical / read-only" so staff don't accidentally try to edit.
// `historical` (optional) lets week-mode forms pass an explicit flag —
// e.g. picking the current week's Monday is NOT historical even though
// the raw date string differs from today.
function CmpHistoryBanner({date, onClear, label, backLabel, historical}) {
  const isHistorical = (typeof historical === "boolean")
    ? historical
    : !!(date && date !== cmpTodayIso());
  if (!isHistorical) return null;
  return (
    <div className="cmp-card" style={{background:"#f0e6f8",border:"1px solid #6b3fa0",display:"flex",alignItems:"center",gap:10,marginBottom:14}}>
      <div style={{fontSize:18}}>🕰️</div>
      <div style={{flex:1}}>
        <strong style={{fontSize:13.5,color:"#6b3fa0"}}>Viewing {label || cmpFriendly(date)}</strong>
        <span style={{marginLeft:8,fontSize:13,color:"var(--cm-nori-70)"}}>Read-only historical view.</span>
      </div>
      <button className="cmp-btn cmp-btn-sm cmp-btn-ghost" onClick={onClear}>{backLabel || "← Back to today"}</button>
    </div>
  );
}

// Hook that wraps the calendar / history flow for any compliance form.
// Each form spreads {viewDate, isHistorical, histRows, signedBy, ...}
// straight into its render. Day-mode forms get back a single date;
// week-mode forms get back the Monday of the picked week and the rows
// span Mon–Sun. HACCP uses a different reader (cmpFetchHaccpDay) so
// it's wired by hand below — every other form goes through here.
function useCmpHistoryView(backend, siteKey, form, mode) {
  const today = cmpTodayIso();
  const [viewDate, setViewDateRaw] = useState(today);
  const [histRows, setHistRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const setViewDate = useCallback((d) => {
    if (!d) { setViewDateRaw(today); return; }
    setViewDateRaw(mode === "week" ? cmpMondayOf(d) : d);
  }, [mode, today]);
  const isHistorical = viewDate !== today && (mode !== "week" || viewDate !== cmpMondayOf(today));
  useEffect(() => {
    if (!isHistorical) { setHistRows([]); return; }
    setLoading(true);
    let dateFrom = viewDate, dateTo = viewDate;
    if (mode === "week") {
      // Parse viewDate as LOCAL midnight (not UTC) to avoid the same TZ
      // shift fixed in cmpMondayOf. Format Sunday from local Y/M/D.
      const parts = String(viewDate).split("-").map(Number);
      const mon = new Date(parts[0], parts[1] - 1, parts[2]);
      const sun = new Date(mon.getFullYear(), mon.getMonth(), mon.getDate() + 6);
      dateTo = sun.getFullYear() + "-" + String(sun.getMonth() + 1).padStart(2, "0") + "-" + String(sun.getDate()).padStart(2, "0");
    }
    cmpFetchFormHistory(backend, siteKey, form, dateFrom, dateTo)
      .then(rows => { setHistRows(rows || []); setLoading(false); })
      .catch(e => { cmpToast("History load failed: " + e.message, "error"); setLoading(false); });
  }, [backend, siteKey, form, viewDate, isHistorical, mode]);
  // Resolve a "signed by" stamp if the backend rows carry one. Each
  // form has its own conventions (CleaningLog uses signoff rows in
  // SignOffAudit; pest-daily uses the latest row of the day). We
  // surface the freshest row's name + timestamp by default; forms
  // that need a tighter rule pass overrideSignedBy in their render.
  const signedBy = (() => {
    if (!isHistorical || !histRows.length) return null;
    const newest = histRows[0];
    if (newest && newest.StaffName) return {name: newest.StaffName, ts: newest._ts};
    return null;
  })();
  return {viewDate, setViewDate, isHistorical, histRows, signedBy, loading, today};
}

// Banner showing WHO submitted the historical form, lifted from the
// audit trail (signedBy comes from the live backend response — never
// synthesized). Per JJ 2026-04-30: "anything in historical mode needs
// to have who submitted it" — without live signedBy we render nothing
// rather than a fake signer.
function CmpHistoricalSignerBanner({date, formLabel, signedBy, historical}) {
  const isHistorical = (typeof historical === "boolean")
    ? historical
    : !!(date && date !== cmpTodayIso());
  if (!isHistorical) return null;
  if (!signedBy || !signedBy.name) return null;
  const name = String(signedBy.name);
  let timeLabel = "";
  if (signedBy.ts) {
    try {
      const d = new Date(signedBy.ts);
      if (!isNaN(d.getTime())) timeLabel = d.toTimeString().slice(0, 5);
    } catch (_) {}
  } else if (signedBy.timeLabel) {
    timeLabel = signedBy.timeLabel;
  }
  let dateLabel = date;
  try { dateLabel = new Date(date).toLocaleDateString("en-GB",{weekday:"short",day:"numeric",month:"short",year:"numeric"}); } catch (_) {}
  return (
    <div className="cmp-card" style={{background:"rgba(212,251,111,0.18)",border:"1px solid var(--cm-wasabi)"}}>
      <h4 style={{color:"var(--cm-nori)",marginBottom:4}}>✓ {formLabel || "Submitted"}</h4>
      <div style={{fontSize:13}}>Submitted by <strong>{name}</strong> on <strong>{dateLabel}</strong>{timeLabel ? " at " + timeLabel : ""}.</div>
    </div>
  );
}

function cmpSundayPillTone(now) {
  var d = now || new Date();
  if (d.getDay() !== 0) return null;
  return d.getHours() >= 17 ? "bad" : "warn";
}

function makeCmpPinPrompt(requestPin, staffByPin) {
  return function cmpPinPrompt(opts) {
    requestPin(async function(staffName, staffPin) {
      // Row H (Lane BF): on a server-PIN-verify host do NOT read the roster cache
      // — the server verify below is the only source of role + homeSite for this
      // one PIN. OFF path byte-identical (reads staffByPin exactly as before).
      // This mirrors the FOH copy (foh.jsx makeCmpPinPrompt), which #256 already
      // gated; the kitchen copy was still reading the roster and now matches.
      var info = pinAuth.serverPinVerifyEnabled() ? null : (staffByPin && staffByPin[staffPin]);
      var role = info && info.role ? String(info.role) : "";
      var homeSite = info && info.homeSite ? info.homeSite : "";
      if (pinAuth.serverPinVerifyEnabled()) {
        try { var srv = await pinAuth.verifyPin(staffPin); if (srv && srv.ok) { role = String(srv.role || role); homeSite = srv.homeSite || homeSite; } } catch (e) {}
      }
      var roles = role.split(",").map(function(s) { return s.trim(); }).filter(Boolean);
      var effRoles = roles.slice();
      if (effRoles.indexOf("BOH") !== -1 && effRoles.indexOf("FOH") === -1) effRoles.push("FOH");
      if (opts.requiredRoles && opts.requiredRoles.length) {
        var ok = opts.requiredRoles.some(function(r) { return effRoles.indexOf(r) !== -1; });
        if (!ok) {
          cmpToast(staffName + " (" + (role || "no role") + ") not authorised — needs " + opts.requiredRoles.join(" or "), "error");
          if (opts.onCancel) opts.onCancel();
          return;
        }
      }
      var staff = {
        pin: staffPin,
        name: staffName,
        role: role,
        roles: roles,
        initials: cmpInitials(staffName),
        home: homeSite
      };
      opts.onConfirm(staff);
    }, opts.onCancel || null);
  };
}

// AM/PM drum-roll TimePicker — lifted from compliance-demo.html
// (2026-04-30, parity sweep). Three scroll-snap wheels (Hour / Minute /
// AM-PM). Pattern: CmpTimePicker shows the selected time as a read-only
// display; tapping opens CmpTimePickerModal hosting the wheels. Returns
// "HH:MM" 24h so the data shape matches a native <input type="time"> —
// CmpFormModal's type:"time" branch renders this in place of the
// browser's native picker.
function cmpParseHHMM(v) {
  if (!v) return {hour:9, minute:0, period:"AM"};
  const [h,m] = v.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  let hour12 = h % 12; if (hour12 === 0) hour12 = 12;
  return {hour: hour12, minute: m || 0, period};
}
function cmpFormatHHMM({hour, minute, period}) {
  let h24 = hour % 12;
  if (period === "PM") h24 += 12;
  return String(h24).padStart(2,"0") + ":" + String(minute).padStart(2,"0");
}
function cmpFormatTime12Display(hhmm) {
  if (!hhmm) return "—";
  const {hour, minute, period} = cmpParseHHMM(hhmm);
  return hour + ":" + String(minute).padStart(2,"0") + " " + period;
}
const CMP_TIME_ITEM_H = 44;
const CMP_TIME_VISIBLE = 5;
const CMP_TIME_BODY_H = CMP_TIME_ITEM_H * CMP_TIME_VISIBLE;
const CMP_TIME_CHEV_H = 26;
const CMP_TIME_PAD = ((CMP_TIME_VISIBLE - 1) / 2) * CMP_TIME_ITEM_H;

function CmpTimeWheel({items, selectedIndex, onChange, render}) {
  const ITEM_H = CMP_TIME_ITEM_H;
  const VISIBLE = CMP_TIME_VISIBLE;
  const PAD = CMP_TIME_PAD;
  const ref = useRef(null);
  const settleTimer = useRef(null);
  const programmatic = useRef(false);
  const lastWheelAt = useRef(0);
  const [liveIdx, setLiveIdx] = useState(selectedIndex);
  useEffect(() => {
    if (!ref.current) return;
    const target = selectedIndex * ITEM_H;
    if (Math.abs(ref.current.scrollTop - target) > 1) {
      programmatic.current = true;
      ref.current.scrollTop = target;
      setLiveIdx(selectedIndex);
      setTimeout(() => { programmatic.current = false; }, 60);
    }
  }, [selectedIndex]);
  const onScroll = () => {
    if (!ref.current) return;
    const liveRaw = ref.current.scrollTop / ITEM_H;
    const liveClamped = Math.max(0, Math.min(items.length - 1, Math.round(liveRaw)));
    if (liveClamped !== liveIdx) setLiveIdx(liveClamped);
    if (programmatic.current) return;
    clearTimeout(settleTimer.current);
    settleTimer.current = setTimeout(() => {
      if (!ref.current) return;
      const idx = Math.round(ref.current.scrollTop / ITEM_H);
      const clamped = Math.max(0, Math.min(items.length - 1, idx));
      const targetTop = clamped * ITEM_H;
      if (Math.abs(ref.current.scrollTop - targetTop) > 0.5) {
        programmatic.current = true;
        ref.current.scrollTo({top: targetTop, behavior:"smooth"});
        setTimeout(() => { programmatic.current = false; }, 240);
      }
      if (clamped !== selectedIndex) onChange(clamped);
    }, 110);
  };
  const onWheel = (e) => {
    e.preventDefault();
    const now = Date.now();
    if (now - lastWheelAt.current < 90) return;
    lastWheelAt.current = now;
    const dir = e.deltaY > 0 ? 1 : -1;
    onItemClick(Math.max(0, Math.min(items.length - 1, liveIdx + dir)));
  };
  const targetIdxRef = useRef(selectedIndex);
  useEffect(() => { targetIdxRef.current = selectedIndex; }, [selectedIndex]);
  const onItemClick = (i) => {
    if (!ref.current) return;
    targetIdxRef.current = i;
    programmatic.current = true;
    ref.current.scrollTo({top: i * ITEM_H, behavior:"smooth"});
    setLiveIdx(i);
    setTimeout(() => { programmatic.current = false; }, 240);
    if (i !== selectedIndex) onChange(i);
  };
  const step = (dir) => {
    const next = Math.max(0, Math.min(items.length - 1, targetIdxRef.current + dir));
    onItemClick(next);
  };
  return (
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",position:"relative"}}>
      <button type="button" tabIndex={-1} onClick={() => step(-1)} style={{height:CMP_TIME_CHEV_H,padding:"0 14px",border:"none",background:"transparent",color:"var(--cm-wasabi)",fontSize:14,cursor:"pointer",fontFamily:"inherit",fontWeight:800,lineHeight:1,display:"flex",alignItems:"center"}}>▲</button>
      <div ref={ref}
        onScroll={onScroll}
        onWheel={onWheel}
        style={{
          height: VISIBLE * ITEM_H,
          overflowY:"scroll",
          width: 80,
          textAlign:"center",
          fontSize:22,
          fontWeight:700,
          color:"var(--cm-kewpie)",
          fontFamily:"inherit",
          msOverflowStyle:"none",
          scrollbarWidth:"none",
          touchAction:"pan-y",
          WebkitOverflowScrolling:"touch",
          position:"relative",
          overscrollBehavior:"contain"
        }}>
        <div style={{height: PAD}} />
        {items.map((it, i) => {
          const dist = Math.abs(i - liveIdx);
          return (
            <div key={i} onClick={() => onItemClick(i)} style={{
              height: ITEM_H,
              display:"flex",
              alignItems:"center",
              justifyContent:"center",
              opacity: dist === 0 ? 1 : dist === 1 ? 0.55 : dist === 2 ? 0.28 : 0.15,
              transform: `scale(${dist === 0 ? 1 : 0.86})`,
              transition:"opacity 0.16s, transform 0.16s",
              cursor:"pointer",
              userSelect:"none"
            }}>{render ? render(it) : it}</div>
          );
        })}
        <div style={{height: PAD}} />
      </div>
      <button type="button" tabIndex={-1} onClick={() => step(1)} style={{height:CMP_TIME_CHEV_H,padding:"0 14px",border:"none",background:"transparent",color:"var(--cm-wasabi)",fontSize:14,cursor:"pointer",fontFamily:"inherit",fontWeight:800,lineHeight:1,display:"flex",alignItems:"center"}}>▼</button>
    </div>
  );
}

function CmpTimePickerModal({value, onConfirm, onCancel, title}) {
  const [{hour, minute, period}, setState] = useState(() => cmpParseHHMM(value));
  const setH = (idx) => setState(s => {
    // Auto-flip AM↔PM on the 11→12 boundary, per JJ's brief 2026-04-30
    // (parity with mockup). 11→12 forward means crossing noon/midnight,
    // 12→11 backward means crossing back. Anything else stays.
    const newHour = idx + 1;
    let newPeriod = s.period;
    if (s.hour === 11 && newHour === 12) newPeriod = s.period === "AM" ? "PM" : "AM";
    else if (s.hour === 12 && newHour === 11) newPeriod = s.period === "AM" ? "PM" : "AM";
    return {...s, hour: newHour, period: newPeriod};
  });
  const setM = (idx) => setState(s => ({...s, minute: idx}));
  const setP = (idx) => setState(s => ({...s, period: idx === 0 ? "AM" : "PM"}));
  return (
    <div className="cmp-form-modal-scrim" onClick={onCancel} style={{zIndex:200}}>
      <div className="cmp-form-modal" onClick={e => e.stopPropagation()} style={{maxWidth:420,padding:0}}>
        <div style={{padding:"22px 24px 8px",textAlign:"center"}}>
          <h3 style={{color:"var(--cm-wasabi)",marginBottom:6}}>{title || "Pick a time"}</h3>
          <div style={{fontFamily:"monospace",fontWeight:800,fontSize:32,color:"var(--cm-kewpie)",letterSpacing:"0.05em",margin:"6px 0 12px"}}>
            {hour}:{String(minute).padStart(2,"0")} <span style={{color:"var(--cm-wasabi)"}}>{period}</span>
          </div>
        </div>
        <div style={{padding:"4px 24px 12px",position:"relative"}}>
          <div style={{
            position:"absolute",
            top: 4 + CMP_TIME_CHEV_H + CMP_TIME_PAD,
            left: 24, right: 24,
            height: CMP_TIME_ITEM_H,
            background:"rgba(212,251,111,0.10)",
            borderTop:"1px solid rgba(212,251,111,0.45)",
            borderBottom:"1px solid rgba(212,251,111,0.45)",
            borderRadius:6,
            pointerEvents:"none",
            zIndex:0
          }} />
          <div style={{
            display:"grid",
            gridTemplateColumns:"1fr auto 1fr 1fr",
            alignItems:"stretch",
            justifyItems:"center",
            gap:14,
            position:"relative",
            zIndex:1
          }}>
            <CmpTimeWheel items={Array.from({length:12},(_,i)=>i+1)} selectedIndex={hour-1} onChange={setH} />
            <div style={{
              display:"flex",
              alignItems:"center",
              justifyContent:"center",
              fontSize:30,
              fontWeight:800,
              color:"var(--cm-wasabi)",
              height: CMP_TIME_CHEV_H * 2 + CMP_TIME_BODY_H,
              lineHeight:1
            }}>:</div>
            <CmpTimeWheel items={Array.from({length:60},(_,i)=>i)} selectedIndex={minute} onChange={setM} render={n => String(n).padStart(2,"0")} />
            <CmpTimeWheel items={["AM","PM"]} selectedIndex={period === "AM" ? 0 : 1} onChange={setP} />
          </div>
        </div>
        <div style={{display:"flex",gap:10,padding:"14px 24px 22px",justifyContent:"flex-end",borderTop:"1px solid rgba(254,250,240,0.08)"}}>
          <button onClick={onCancel} style={{padding:"10px 16px",border:"1px solid rgba(254,250,240,0.25)",background:"transparent",color:"var(--cm-kewpie)",borderRadius:8,fontFamily:"inherit",fontWeight:600,cursor:"pointer"}}>Cancel</button>
          <button onClick={() => onConfirm(cmpFormatHHMM({hour, minute, period}))} style={{padding:"10px 18px",borderRadius:8,background:"var(--cm-wasabi)",color:"var(--cm-nori)",fontWeight:800,fontSize:13,border:"none",fontFamily:"inherit",cursor:"pointer"}}>Done</button>
        </div>
      </div>
    </div>
  );
}

function CmpTimePicker({value, onChange, label, theme}) {
  const [open, setOpen] = useState(false);
  const isDark = theme === "dark";
  const style = isDark ? {
    width:"100%", padding:"12px 14px", borderRadius:8,
    background:"rgba(254,250,240,0.08)", color: value ? "var(--cm-kewpie)" : "rgba(254,250,240,0.55)",
    border:"1px solid rgba(254,250,240,0.18)",
    fontSize:14, fontFamily:"inherit", cursor:"pointer",
    display:"flex", alignItems:"center", justifyContent:"space-between", gap:8, textAlign:"left"
  } : {
    width:"100%", padding:"10px 14px", borderRadius:8,
    background: "var(--cm-rice)", color: value ? "var(--cm-nori)" : "var(--cm-nori-60)",
    border:"1px solid var(--cm-nori-18)",
    fontSize:14, fontFamily:"inherit", cursor:"pointer",
    display:"flex", alignItems:"center", justifyContent:"space-between", gap:8, textAlign:"left"
  };
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} style={style}>
        <span style={{fontWeight:700,fontFamily:"monospace",fontSize:16,letterSpacing:"0.02em"}}>{value ? cmpFormatTime12Display(value) : "Tap to pick a time"}</span>
        <span style={{color: isDark ? "var(--cm-wasabi)" : "var(--cm-nori)",fontSize:16}}>🕒</span>
      </button>
      {open && (
        <CmpTimePickerModal
          value={value}
          title={label || "Pick a time"}
          onConfirm={(v) => { onChange(v); setOpen(false); }}
          onCancel={() => setOpen(false)}
        />
      )}
    </>
  );
}

function CmpFormModal({title, desc, fields, initial, submitLabel, onSubmit, onCancel}) {
  const [values, setValues] = useState(() => initial || Object.fromEntries(fields.map(f => [f.key, f.default || ""])));
  const submit = () => {
    for (let i = 0; i < fields.length; i++) {
      const f = fields[i];
      if (f.required && !String(values[f.key] || "").trim()) {
        cmpToast(f.label + " is required", "error");
        return;
      }
    }
    onSubmit(values);
  };
  return (
    <div className="cmp-form-modal-scrim" onClick={onCancel}>
      <div className="cmp-form-modal" onClick={e => e.stopPropagation()}>
        <div className="cmp-form-head">
          <h3>{title}</h3>
          {desc && <div className="cmp-form-desc">{desc}</div>}
        </div>
        <div className="cmp-form-inner">
          {fields.map(f => (
            <div key={f.key} className="cmp-field-block">
              <label>{f.label}{f.required && <span style={{color:"#ff8888"}}> *</span>}</label>
              {f.type === "select" ? (
                <select value={values[f.key] || ""} onChange={e => setValues(v => Object.assign({}, v, {[f.key]: e.target.value}))}>
                  {(f.options || []).map(o => {
                    const opt = (typeof o === "object") ? o : {value: o, label: o};
                    return <option key={opt.value} value={opt.value}>{opt.label}</option>;
                  })}
                </select>
              ) : f.type === "textarea" ? (
                <textarea value={values[f.key] || ""} onChange={e => setValues(v => Object.assign({}, v, {[f.key]: e.target.value}))} placeholder={f.placeholder || ""} rows={3} />
              ) : f.type === "time" ? (
                <CmpTimePicker
                  value={values[f.key] || ""}
                  label={f.label}
                  theme="dark"
                  onChange={(v) => setValues(vals => Object.assign({}, vals, {[f.key]: v}))}
                />
              ) : (
                <input type={f.type || "text"} value={values[f.key] || ""} onChange={e => setValues(v => Object.assign({}, v, {[f.key]: e.target.value}))} placeholder={f.placeholder || ""} />
              )}
              {f.hint && <span className="cmp-hint">{f.hint}</span>}
            </div>
          ))}
        </div>
        <div className="cmp-form-foot">
          <button className="cmp-form-cancel" onClick={onCancel}>Cancel</button>
          <button className="cmp-form-submit" onClick={submit}>{submitLabel || "Save"}</button>
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// Documentation Hub data (Phase 2 v1.2 — lifted from compliance-demo.html)
// =====================================================================
// Static reference data — these are the actual Rolled docs/policies. SOPs
// + RAs + SSoWs are stable lists; admin sheet wiring is a v1.3 follow-up.
// Maintenance Records intentionally OUT per Wave 6 of original review.

const CMP_SOPS = [
  {id:"SOP-01", title:"SOP 1 — Food Allergies & Intolerances",            version:"v1", updated:"2026-02-23"},
  {id:"SOP-02", title:"SOP 2 — Cleaning Methods",                         version:"v1", updated:"2026-02-23"},
  {id:"SOP-2a", title:"SOP 2a — Blast Chilling",                          version:"v1", updated:"2026-02-23"},
  {id:"SOP-03", title:"SOP 3 — Colour-Coded Equipment",                   version:"v1", updated:"2026-02-23"},
  {id:"SOP-04", title:"SOP 4 — Cook-Chill & Cook-Freeze",                 version:"v1", updated:"2026-02-23"},
  {id:"SOP-06", title:"SOP 6 — Cooling Food",                             version:"v1", updated:"2026-02-23"},
  {id:"SOP-07", title:"SOP 7 — Defrosting Foods",                         version:"v1", updated:"2026-02-23"},
  {id:"SOP-08", title:"SOP 8 — Food Deliveries",                          version:"v1", updated:"2026-02-23"},
  {id:"SOP-10", title:"SOP 10 — Fitness to Work Guidelines",              version:"v1", updated:"2026-02-23"},
  {id:"SOP-11", title:"SOP 11 — Food Contamination",                      version:"v2", updated:"2026-04-19"},
  {id:"SOP-12", title:"SOP 12 — Food Labelling",                          version:"v1", updated:"2026-03-19"},
  {id:"SOP-13", title:"SOP 13 — Fridge & Freezer Breakdown",              version:"v2", updated:"2026-04-19"},
  {id:"SOP-14", title:"SOP 14 — Food Storage",                            version:"v1", updated:"2026-02-23"},
  {id:"SOP-15", title:"SOP 15 — Safe Use of Ice",                         version:"v1", updated:"2026-02-23", keywords:"ice cubes water freezer"},
  {id:"SOP-16", title:"SOP 16 — Personal Hygiene",                        version:"v2", updated:"2026-04-19", keywords:"handwashing hands uniform jewellery hair nails"},
  {id:"SOP-17", title:"SOP 17 — Pest Control",                            version:"v2", updated:"2026-04-19", keywords:"rodents mice rats insects flies droppings"},
  {id:"SOP-18", title:"SOP 18 — Microwaves",                              version:"v1", updated:"2026-02-23"},
  {id:"SOP-19", title:"SOP 19 — Raw Food Washing",                        version:"v1", updated:"2026-02-23"},
  {id:"SOP-21", title:"SOP 21 — Waste Management",                        version:"v1", updated:"2026-03-19"},
  {id:"SOP-22", title:"SOP 22 — Cold Food Display",                       version:"v1", updated:"2026-02-23"},
  {id:"SOP-24", title:"SOP 24 — Glass & Crockery Safe Handling",          version:"v1", updated:"2026-02-23"},
  {id:"SOP-25", title:"SOP 25 — Enforcement Officers",                    version:"v1", updated:"2026-02-23"},
  {id:"SOP-26", title:"SOP 26 — Sushi Rolling Machine (Suzumo SVR-NXA-CE)", version:"v1", updated:"2026-02-24"},
  {id:"SOP-27", title:"SOP 27 — Rice Mixing Machine (Suzumo MCR-UNC)",    version:"v1", updated:"2026-02-24"},
  {id:"SOP-28", title:"SOP 28 — Compact Rice Mixer (Suzumo MCR-SSC)",     version:"v1", updated:"2026-02-24"},
  {id:"SOP-29", title:"SOP 29 — Sushi Rice Acidification CCP (pH ≤ 4.3)", version:"v1", updated:"2026-03-12", keywords:"rice acidification ph vinegar ambient hold"},
  {id:"SOP-30", title:"SOP 30 — Freezing of Fish (parasite control)",     version:"v1", updated:"2026-03-05", keywords:"salmon tuna sashimi sushi raw fish parasite freezing sailbrand farmed wild halibut trout"}
];

// Per-SOP body summaries (2026-04-30 lift from JJ's Drive folder
// `1eNwqTqManzrzzzVranTo7HWR35O68jE-`). Each entry is a tight
// 4-paragraph summary in Rolled's voice — NOT a verbatim regurgitation
// of the source PDF. Full authoritative PDF lives in the FSMS Drive
// folder; cmpOpenSop falls back to a generic stub if a SOP is missing
// from this map. v2 entries (SOP-11/13/16/17) reflect the v2 PDFs Adam
// signed off on 2026-04-19 with Sheffield EHO Kirstie's input.
const CMP_SOP_BODIES = {
  "SOP-01": [
    "Food allergies and intolerances are immune-system or digestive responses to specific foods. ~1–2% of UK adults and ~5–8% of children are affected. A severe allergic reaction (anaphylactic shock) can be fatal within minutes — staff must take every allergen request seriously and never guess about ingredients.",
    "The 14 declared allergens (Natasha's Law / FIC Regulations) must be communicated accurately for every menu item: cereals containing gluten, crustaceans, eggs, fish, peanuts, soybeans, milk, tree nuts, celery, mustard, sesame, sulphites, lupin, molluscs.",
    "When a customer declares an allergy: stop, ask which allergen, check the ingredient master + supplier specs, prepare in a cleaned workstation with dedicated colour-coded equipment, and double-check the finished item against the label before handing over. If unsure, refuse the order.",
    "Every kiosk holds an allergen matrix on Drive (cross-referenced from the in-app Allergen Matrix tile). All FOH + BOH staff complete Allergen Awareness training within 30 days of hire and refresh every 3 years."
  ],
  "SOP-02": [
    "Cleaning and disinfection prevent cross-contamination — the single biggest driver of foodborne illness in catering. Two distinct steps: cleaning removes visible soil with detergent; disinfection kills the bacteria that remain.",
    "Method matrix: work surfaces use detergent + disinfectant; pans, knives, mixing bowls, utensils run through the mechanical dishwasher; chopping boards rotate by colour code (see SOP 3); fridges, freezers and the rolling/rice machines follow the manufacturer's clean-in-place instructions in the Cleaning Schedule.",
    "Sanitiser must hit BS EN 1276 / BS EN 13697 standard at the dilution and contact time on the bottle. Staff dilute on shift, never store pre-mix overnight. Surfaces are cleaned BEFORE service, AFTER each task, and at end of day.",
    "Sign-off: every cell on the 14-task Cleaning Schedule is initialled the same shift it's done. The Sunday weekly Manager Sign Off is mandatory — any signed-in PIN attests, but the sign-off is the legal record that the week's matrix is complete (FSMS §7.1)."
  ],
  "SOP-2a": [
    "Blast chillers cool cooked food rapidly through the +5°C–+63°C danger zone where pathogenic bacteria multiply fastest. Rapid cooling is one of the highest-leverage controls in the kitchen — a slow cool can re-introduce risk into food that's already been cooked safely.",
    "Procedure: switch the chiller on in advance to pre-cool the chamber. Begin chilling within 30 minutes of cook completion. Portion bulk foods so cool air can circulate. Probe the core temperature at the start and end of every cycle.",
    "Critical limit: cooked food must reach +5°C or below within 90 minutes of cook completion. If a cycle exits >+5°C, extend the run or transfer to refrigeration, log the deviation as a Corrective Action, and probe again before storage.",
    "Records: blast cycles are logged on the Daily HACCP form (delivery/cooking/cooling section) with start time, end time, food item, core temp, and signing initials."
  ],
  "SOP-03": [
    "Colour-coded equipment prevents cross-contamination between food groups. The simpler the system the easier it is to monitor — Rolled's chopping boards, knives, food storage containers and cleaning cloths follow one consistent code across all kiosks.",
    "Standard code: red = raw meat, blue = raw fish, yellow = cooked meats, green = salad / fruit / veg, white = bakery / dairy, brown = root vegetables. Posters are displayed in every prep area for quick reference.",
    "Boards must be in good condition — deeply scored or split boards are replaced immediately because grooves harbour bacteria. Damaged equipment is logged on the Daily HACCP Records and a replacement requisitioned.",
    "Cleaning and storage: each colour is washed and disinfected separately (no mixed-colour batches in the dishwasher). Boards air-dry on dedicated racks colour-segregated to prevent contact between raw and ready-to-eat surfaces."
  ],
  "SOP-04": [
    "Cook-chill and cook-freeze let us prepare large volumes ahead of service. Cook-chill = cook → blast-chill → store cold → reheat to order. Cook-freeze = cook → blast-chill → portion → freeze → defrost + reheat to order.",
    "Time/temperature parameters at every stage are critical control points. Cook to ≥75°C core. Cool to ≤+5°C within 90 minutes (cook-chill) or freeze to ≤−18°C (cook-freeze). Reheat to ≥75°C core for ≥30 seconds before service.",
    "Date-label every product with cook date + use-by. Cook-chill shelf life is 5 days inclusive of cook day at ≤+5°C. Cook-freeze shelf life is governed by the food spec (typically 1–3 months at ≤−18°C).",
    "Every batch is logged on the Daily HACCP Records with cook time, blast/freeze cycle times, core temps, and a final reheat probe at service. A failed cycle goes straight to a Corrective Action — that batch is not served."
  ],
  "SOP-06": [
    "Pathogenic bacteria can't grow above +63°C or below +5°C. Between those limits is the Danger Zone — rapid bacterial multiplication and spore germination. Cooling food correctly is one of the most common causes of foodborne illness when done badly.",
    "Preferred method is a blast chiller within 30 minutes of cook completion (see SOP 2a). At ambient: cool to <+20°C within 90 minutes, then refrigerate immediately. Speed cooling by reducing portion size, using ice/water baths, or pre-chilled shallow trays.",
    "Cool in clean disinfected trays in the coolest part of the kitchen, covered with greaseproof. Never stack hot containers — they'll insulate each other and trap heat. Never put still-warm food in a sealed fridge — it raises the ambient and risks the rest of the load.",
    "All cooling cycles get logged on the Daily HACCP Records. A failed cycle is a Corrective Action: extend cooling, transfer to a faster cool method, or discard the batch if the danger zone exceeded 4 hours."
  ],
  "SOP-07": [
    "Freezing doesn't kill pathogenic bacteria — it just stops them growing. As food warms during defrosting, bacteria reactivate and multiply, especially in the surface layers. Bad defrosting is a major cause of foodborne illness.",
    "Always defrost in a refrigerator (≤+5°C), placed BELOW high-risk / ready-to-eat foods to prevent drip cross-contamination. Remove outer packaging before defrost. Spread items in a single layer in clean trays — never top-up with new product mid-defrost.",
    "Critical limit: food is fully defrosted only when no ice crystals remain in the core (probe-check). Use within 24 hours of full defrost, never re-freeze. Tag every batch with defrost-start date so the next user can verify.",
    "Microwave or running cold water defrosting is permitted only when the food will be cooked or used immediately — these methods raise the surface temperature into the danger zone briefly and need close monitoring."
  ],
  "SOP-08": [
    "Goods receipt is the first critical control point in the chain. Bad deliveries (warm, damaged, expired, contaminated, or from an unapproved supplier) propagate risk into everything downstream. Inspection at the door is non-negotiable.",
    "Check on every delivery: vehicle is clean and segregated (chilled / frozen / ambient kept separate); packaging is intact; product is within use-by; chilled items are ≤+8°C (preferably ≤+5°C); frozen items are ≤−15°C; fish is between 0°C and +3°C. Probe-check one chilled and one frozen item per supplier.",
    "Reject any delivery that fails: damaged packaging, signs of pest activity, broken seals, expired product, or temperature failure. Reject quietly and professionally — the supplier's job is to deliver compliant product, not yours to rescue it.",
    "Every delivery is logged on the Daily HACCP Records (CCP-1) with arrival time, supplier, probe readings, and signing initials. Spec-checked supplier list lives in the Suppliers tile + the Drive folder. Periodic supplier audits document due-diligence."
  ],
  "SOP-10": [
    "Food handlers carrying infections — especially gastro-intestinal — can contaminate food and cause widespread illness. Reg (EC) 852/2004 Annex II Chapter VIII is the legal basis: any handler with a notifiable disease must NOT work with or touch food.",
    "Symptoms requiring immediate exclusion from food handling: diarrhoea, vomiting, infected skin lesions, weeping eczema, sore throat with fever, jaundice, or any infection that could be transmitted via food. Report to a manager BEFORE starting your shift.",
    "Return-to-work rule: 48 hours symptom-free for diarrhoea/vomiting before re-entering the kitchen. Other infections need GP clearance. Manager keeps a Fitness-to-Work record on file (privacy-preserving — symptoms only, no diagnoses).",
    "Cuts and grazes are covered with a blue waterproof dressing + disposable glove. Long-term skin conditions (eczema, dermatitis) need a manager risk-assessment and may require redeployment to a non-food role until cleared."
  ],
  "SOP-11": [
    "v2 (issued 19 April 2026) — references to an external pest contractor removed; Atlantic salmon and tuna referenced explicitly in raw-fish handling sections. Approved by Adam Miller, Director.",
    "Three contamination types to control: microbiological (pathogens — bacteria, viruses, parasites, mould), chemical (cleaning chemicals, allergens, pesticide residue), physical (glass, hair, jewellery, packaging fragments). Every step in the kitchen is a potential introduction point — identify, prevent, monitor.",
    "Microbiological controls: hand-washing (SOP 16), colour-coded equipment (SOP 3), separation of raw and ready-to-eat (especially raw salmon and tuna from rolled product), temperature control across CCP-1 / CCP-3 / CCP-5 / CCP-6, and the Sanitiser Schedule.",
    "Chemical and physical controls: COSHH-managed chemicals stored away from food; allergen segregation per SOP 1; brittle plastic or glass items removed where possible (SOP 24); jewellery and false nails banned from food prep; report any breakage on the Daily HACCP Records as a Corrective Action."
  ],
  "SOP-12": [
    "Food Labelling Regulations 1996 (as amended) plus Trading Standards / Consumer Protection laws. Restaurants are exempt from many provisions but still have core labelling duties — accurate descriptions, allergen disclosure, country of origin where claimed.",
    "Pre-packed for direct sale (PPDS / Natasha's Law): all 14 allergens highlighted in bold inside the ingredient list on the wrapper. Applies to every roll, miso cup, snack, and any item packed in the kiosk before being sold.",
    "Date labelling: 'Use By' for high-risk perishable foods (microbiological safety driver); 'Best Before' for ambient-stable foods (quality driver). Sushi rolls + cooked rice carry Use By dates calculated from cook + acidification time.",
    "In-house batch labels capture cook date, use-by, allergens, batch number, and the staff initial. Labels are printed via the in-kiosk label printer; spent labels go in the waste, not back in the box. Batch records are retained for 90 days for traceability."
  ],
  "SOP-13": [
    "v2 (issued 19 April 2026) — Section 13.3 added covering the customer Display Fridge failure addendum (2-hourly monitoring, +8°C threshold, 4-hour rule, Do Not Use sign, mandatory Corrective Action). Agreed with Sheffield City Council EHO Kirsty on 2 April 2026. Approved by Adam Miller, Director.",
    "Refrigerator breakdown: probe representative samples from top, middle and bottom shelves. Decision tree by core food temperature and time discovered: ≤+8°C is safe to relocate to a working unit; +8°C–+15°C may be sold same day if used within 4 hours of failure; >+15°C or >4 hours = discard.",
    "Freezer breakdown: ice crystals still present + product feels firm = transfer to working freezer. Soft / partially defrosted = treat as defrosted (see SOP 7), use or cook within 24 hours. Fully defrosted with no ice = discard or, if probed ≤+5°C, transfer to refrigerator and use within 24 hours.",
    "Customer Display Fridge addendum: 2-hourly probe checks while a deviation is open; if temp >+8°C for any 2-hour check, raise an Emergency Fridge Incident in the Compliance app, mark Do Not Use, and apply the 4-hour rule. Disposal beyond 4 hours requires admin sign-off in the in-app workflow."
  ],
  "SOP-14": [
    "Shelf life dates and storage conditions are the backbone of food safety in storage. Every item carries a max refrigerated shelf life calculated from the day of production (inclusive). Get this wrong and you risk serving food past its safe window.",
    "Prep-kitchen high-risk items: handheld rolls — same day; cooked sushi rice — same day after acidification (see SOP 29); cooked proteins — 3 days at ≤+5°C; cooked vegetables — 3 days at ≤+5°C. Date-label every container the moment it enters storage.",
    "Raw items: defrosted fish — 24 hours at 0°C–+3°C; raw vegetables — 5–7 days per supplier spec at +1°C–+5°C; opened sauces and pickles — per manufacturer label. Closed packaging follows the printed Use By / Best Before unaltered.",
    "Storage hygiene: raw on the bottom shelf, ready-to-eat on top, allergen-segregated. FIFO (First In First Out) on every shelf. Weekly stock rotation check. Anything missing a date label = discard immediately, don't guess."
  ],
  "SOP-15": [
    "Contaminated ice can carry foodborne pathogens straight into a customer's drink. Hazards: microbiological (bacteria from dirty machines or hands), foreign body (chips, scoops left inside), non-potable water (failed mains supply), chemical (cleaning residue).",
    "Ice machines must sit in a clean, well-maintained food room with proper drainage. Position away from hot equipment, food prep zones, and waste. Use a dedicated scoop stored OUTSIDE the machine — never leave the scoop sitting in the ice.",
    "Cleaning: all accessible parts (seals, hinges, dispensing chute) cleaned and disinfected on the weekly Cleaning Schedule. Quarterly deep clean strips the unit for descaling. Visible mould or biofilm = stop using, deep clean, log a Corrective Action.",
    "Service: scoop ice, never pour from a cup. Don't store anything else inside the machine (bottles, food). If a glass breaks near the ice, the entire batch is discarded, the chamber is sanitised, and the event is logged on Daily HACCP Records."
  ],
  "SOP-16": [
    "v2 (issued 19 April 2026) — hand-washing rules now explicitly include 'after handling or using raw fish (salmon, tuna)'. Approved by Adam Miller, Director.",
    "Personal hygiene is the single highest-leverage food safety control. A smartly dressed, hygiene-conscious team signals professionalism to customers and protects against foodborne illness from cross-contamination.",
    "Hand-wash with soap and hot running water for ≥20 seconds: on arrival, after each break, after using the toilet, after handling raw food (especially raw salmon and tuna), after touching face / hair / waste / cleaning chemicals, and between any task switch. Drying with paper towel only — no shared cloth.",
    "Uniform: clean apron over a Rolled tee, hair tied back / under a cap, no jewellery except a plain wedding band, no false nails or nail polish, no perfume that transfers to food. Cuts covered with a blue waterproof plaster + disposable glove. Report illness BEFORE starting (see SOP 10)."
  ],
  "SOP-17": [
    "v2 (issued 19 April 2026) — sections rewritten to reflect Rolled Meadowhall's internal pest control model: Daily Pest Control Checklist + Weekly Pest Control Checklist, no external contractor. Agreed with Sheffield City Council EHO Kirstie on 2 April 2026. Approved by Adam Miller, Director.",
    "A pest is any unwanted animal, insect or bird that contaminates food or whose presence is undesirable. Common in a sushi kiosk: rats, mice, pigeons, cockroaches, flies, stored-product insects (weevils, beetles, mites). Pests carry both microbiological and physical hazards.",
    "Detection is the daily Pest Control Checklist (in-app form): every staff member checks droppings, gnawed packaging, nests, smells, fly activity, sightings — by zone. The Monday Deep Sweep adds 5 deeper checks (under counters, drains, stock storage edges). Any evidence auto-raises a Corrective Action.",
    "Prevention: keep external doors closed; seal entry points; store bins lidded and away from external walls; rotate and inspect dry stock; inspect deliveries on arrival; clean immediately after spills. An infestation found by an EHO can lead to closure and prosecution — daily diligence is the only safeguard."
  ],
  "SOP-18": [
    "Microwave ovens reheat food fast but they heat unevenly — cold spots in the food can leave bacteria viable even when the surface looks hot. Used wrong, a microwave is a cross-contamination risk dressed up as a quick reheat.",
    "Use only commercial-rated microwaves. Do not microwave food in metal or non-microwave-safe containers. Stir or rotate halfway through every cycle. Cover with a microwave-safe lid to retain steam and even heating.",
    "Critical limit: reheat to a core temperature of ≥75°C for ≥30 seconds. Probe the centre AND the thinnest portion. If either is below target, run an extra cycle and re-probe. Never serve until both readings clear the limit.",
    "Records: every reheat cycle logged on the Daily HACCP Records with food item, reheat duration, two-point probe readings, and signing initials. Microwaves are cleaned and disinfected daily — splatter is a contamination + smell vector."
  ],
  "SOP-19": [
    "Foodborne illness (e.g. dysentery) can transmit via contaminated leaves and vegetables. Anything served raw — salads, cucumber, tomato, fruit garnish — must be thoroughly washed to remove soil, pesticide residue, parasites and pathogens.",
    "Method: separate leaves from the head before washing (lettuce, celery, endive). Wash whole leaves in clean potable water. Disinfect with food-safe sanitiser per the manufacturer's contact-time. Rinse thoroughly with potable water. Drain and store in a clean container at ≤+5°C.",
    "Whole salad veg (tomato, cucumber, peppers): wash and disinfect on the outer surface even when the skin will be peeled — the knife transfers contamination through the cut. Same rule for fruit garnishes that touch the finished plate.",
    "Wash zone is dedicated and colour-coded (green) to prevent cross-contamination from raw fish or meat prep. Wash sink is cleaned before each session. Records: washing tasks appear on the daily Cleaning Schedule with time and signing initials."
  ],
  "SOP-21": [
    "Food waste is a major pest attractor and a source of cross-contamination. Raw scraps (fish trim, vegetable peel, meat) and decaying matter spread bacteria fast — bins are never an afterthought. Internal storage and external collection both need disciplined controls.",
    "Internal: 'clean as you go', wash hands after every waste handling, lidded foot-pedal bins line every prep zone. Bin liners are doubled, knotted before transfer, and never overfilled. Bins are emptied at task changeover and at end of service.",
    "External: skips and bins live in a designated waste area away from food deliveries and the customer entrance. Lids stay closed. Waste contractor empties on a fixed schedule — failure to collect = log a Corrective Action and escalate to operations.",
    "Allergen waste (peanuts, sesame, etc.) goes in a separate bag colour to prevent cross-contact during disposal. Glass and crockery break-glass goes in a dedicated rigid container, never in the food-waste stream (see SOP 24)."
  ],
  "SOP-22": [
    "Cold display units hold high-risk ready-to-eat product (cooked rolls, miso prep, garnishes) in front of the customer. They are CCP-6 chilled storage with a higher-risk profile because the unit door opens repeatedly and ambient air rushes in.",
    "Critical limit: display ≤+5°C at all times. Probe-check at start of service, mid-shift, and pre-close — three readings minimum, all logged on the Daily HACCP Records. Use a sanitised probe between food types to prevent cross-contamination.",
    "Pre-service: clean and disinfect the unit, switch on early to pre-chill, verify the unit holds temp empty before loading. Don't overfill — air flow is what keeps the temperature even, and obstructed flow creates warm pockets.",
    "Service: rotate stock so the oldest goes out first; date-label the day's batch; keep portions covered when not on the front line; remove anything past the day's use-by. Display stock is NEVER carried over to the next day."
  ],
  "SOP-24": [
    "Glass and crockery breakages can lead to foreign body contamination — the worst kind because shards can injure a customer and the legal exposure is severe. Reactions to glass-in-food range from refunds to prosecution.",
    "Safe handling: carry one item at a time; never stack damaged crockery; check rims for chips before each service; rotate to remove worn-out items. Dishwasher loading is single-layer — overcrowding chips edges and breaks handles.",
    "If a breakage happens in or near food: stop service in the affected zone, isolate ALL exposed product, and discard generously. Inspect surrounding food and surfaces for shards. Sanitise and re-probe. Resume only when zone is verified clear.",
    "Every breakage is logged on the Daily HACCP Records (item, location, action taken, signing initials). The log is reviewed monthly for patterns — a recurring breakage in the same zone signals an equipment, training or layout issue."
  ],
  "SOP-25": [
    "Local Authority Environmental Health Officers (EHOs) and Trading Standards Officers regulate every Rolled kiosk. They have right of unannounced entry at any reasonable time, can seize or sample foods, require improvements, and prohibit equipment use.",
    "Approach: be polite, helpful, truthful. Never obstruct or argue. Ask for ID at the door, log the visit (officer name, authority, arrival time), notify the on-call manager immediately, and accompany the officer through the kiosk.",
    "What they'll ask to see: HACCP records (Daily HACCP Records, Pest Log, Cleaning Schedule, Probe Calibration, Fish Freezing log, Corrective Actions), Allergen Matrix, supplier specs and certs, fitness-to-work records, training records, temperature monitoring history.",
    "After the visit: officer writes a report — accept any improvement notice in writing, log the date and required actions, and return the report to the manager + Director (Adam) the same day. Always follow up with action evidence within the deadline."
  ],
  "SOP-26": [
    "The Suzumo SVR-NXA-CE is a semi-automatic norimaki rolling machine — agitating, transferring, rice-sheet cutting and forming mechanisms operate at speed and can be dangerous if used incorrectly. Only trained, authorised staff operate the machine.",
    "Pre-operation checks: confirm authorisation; visually inspect for damage, loose parts, foreign objects; check the rice-sheet former, conveyor belt and cutter housing are seated properly; clean and lubricate per the daily schedule.",
    "Operation: never reach into a moving cutter or former. Stop the machine before clearing jams. Guards stay in place during all running cycles. PPE = closed-toe non-slip shoes + clean apron. Long hair tied back, no loose sleeves or jewellery.",
    "Cleaning + maintenance: end-of-day strip-down per the manufacturer instructions. Sanitise rice-contact parts with food-safe sanitiser. Faults or unusual noise are logged on the Daily HACCP Records (equipment section) and the machine is taken out of service until cleared."
  ],
  "SOP-27": [
    "The Suzumo MCR-UNC (Sharikka) is a cooked-rice mixer for sushi rice (shari) — blends rice with vinegar concentrate via a powered agitator and drum. Max capacity 15 kg per batch; mixing cycle 3–5 minutes. Produces uniform shari at scale; misuse damages rice or causes injury.",
    "Pre-operation: verify training authorisation; visually inspect the mixing drum, drum lid, agitator, sensors; confirm the unit is clean and dry; check the drum is fully seated and the lid latch engages.",
    "Operation: load rice within capacity (no overfilling — agitator stalls and damages the motor); add vinegar concentrate per SOP 29; close the lid before starting; let the cycle complete before opening. Never reach in mid-cycle.",
    "Cleaning + maintenance: end-of-day strip-down per the manufacturer instructions. Drum, agitator and lid go through the dishwasher or hand-clean with food-safe sanitiser. Faults logged + machine taken out of service until repaired (see SOP 13 for fridge breakdown — same disciplined log)."
  ],
  "SOP-28": [
    "The Suzumo MCR-SSC (Sharikka Compact) is the compact variant for the same shari preparation — drum capacity 6.5 kg, mixing cycle ~3 minutes, designed for tighter prep stations. Same operating principles as SOP 27 with smaller-capacity safeguards.",
    "Pre-operation checks: same as SOP 27 — training, inspection, clean / dry / seated drum, working sensors, latched lid.",
    "Operation: respect the 6.5 kg cap — overfilling is the most common failure mode on this unit. Cycle runs ~3 minutes; let it complete and don't open mid-cycle. Vinegar concentrate per SOP 29.",
    "Cleaning + maintenance: identical to SOP 27 — end-of-day strip-down, dishwasher or hand sanitise, faults logged on Daily HACCP Records and the unit taken out of service until repaired."
  ],
  "SOP-29": [
    "Sushi rice acidification is a Critical Control Point (CCP-2). Cooked rice is high-risk — it supports rapid growth of Bacillus cereus and other pathogens at ambient temperature. Acidifying the rice to pH ≤ 4.3 inhibits growth and lets us hold rice ambient for service.",
    "Vinegar concentrate recipe (per FSMS): rice vinegar, sugar, salt — measured to spec, mixed cold, stored in a labelled food-safe container at ≤+5°C until use. Concentrate is prepared fresh weekly and date-labelled.",
    "Per batch: cook rice → mix with concentrate using SOP 27 / 28 → probe pH using a calibrated pH meter (see Probe Calibration in the Compliance app). Critical limit: pH ≤ 4.3. If a batch reads > 4.3, add concentrate, re-mix, re-test. If still > 4.3, discard and start again.",
    "Records: every batch logged on the Daily HACCP Records (rice/pH section) with batch ID, time, pH reading, signing initials. Ambient hold time after acidification is ≤4 hours per the FSMS — anything older is rolled into a sealed cold container and used or discarded per shelf-life."
  ],
  "SOP-30": [
    "Fish parasites (Anisakis larvae and others) are present in certain wild fish — salmon, herring, cod, monkfish among others. Parasites can transfer to humans when fish is eaten raw or undercooked. Freezing is the legally recognised parasite kill step before raw use.",
    "Critical limit: −20°C for ≥24 hours OR −35°C for ≥15 hours, measured at the fish core. The kitchen freezer must reach and hold the target temperature throughout the cycle. Probe the core at start, mid-cycle and end.",
    "Sailbrand farmed exemption: salmon and tuna sourced from approved Sailbrand farmed operations are exempt from the freezing requirement under their certified parasite-control programme. Documentation lives in the Suppliers tile + Drive folder. Wild or non-Sailbrand fish always freezes.",
    "Records: every intake logged in the Fish Freezing form in the Compliance app with batch ID, species, supplier, freezer in/out times, core temps and pass/fail. A failed cycle (warmer than −20°C or shorter than 24h) auto-raises a Corrective Action and the batch is held until re-frozen and re-tested."
  ]
};
const CMP_RISK_ASSESSMENTS = [
  "RA-01 Slips, Trips & Falls","RA-02 Manual Handling","RA-03 Chemical Exposure (COSHH)",
  "RA-04 Knife Use","RA-05 Hot Oil / Deep Fryer","RA-06 Fridge & Freezer Access",
  "RA-07 Electricity (Plug-in Appliances)","RA-08 Rice Cooker Steam & Burns","RA-09 Food Processor / Slicer",
  "RA-10 Waste Handling","RA-11 Lone Working (Close Down)","RA-12 Customer Interaction / Aggression",
  "RA-13 Allergen Exposure","RA-14 Fire","RA-15 First Aid / Injury Response",
  "RA-16 Pregnant & Nursing Workers","RA-17 Young Workers","RA-18 Working at Height (Shelving)",
  "RA-19 Display Fridge Glass","RA-20 Mobile Catering (Delivery)"
];
const CMP_SSOW = [
  "SSOW-01 Rice Cooker Operation","SSOW-02 Slicer Cleaning","SSOW-03 Fryer Oil Change",
  "SSOW-04 Knife Sharpening","SSOW-05 Fridge Coil Cleaning","SSOW-06 Bar Display Changeover",
  "SSOW-07 Walk-in Freezer Entry","SSOW-08 Deep Fat Cleaning","SSOW-09 Chemical Dilution (Cleaning)",
  "SSOW-10 End of Day Lock-up","SSOW-11 Customer Complaint Handling","SSOW-12 Allergen Cross-check",
  "SSOW-13 Incident Reporting","SSOW-14 Probe Sanitising","SSOW-15 Pest Sighting Response",
  "SSOW-16 Emergency Fridge Failure","SSOW-17 Fire Evacuation","SSOW-18 First Aid on Scene",
  "SSOW-19 Bin Area Clean-down","SSOW-20 Uniform & PPE"
];
const CMP_SUPPLIERS = [
  {name:"Sailbrand", cat:"Fish (raw salmon, raw tuna)", cert:"Parasite freezing policy on file (FS SOP 30)",
   docTitle:"Sailbrand · Parasite Freezing Policy",
   docMeta:"Cross-linked to FS SOP 30 · last verified 2026-04-25",
   docBody:[
     "Sailbrand confirms all Atlantic salmon, Atlantic halibut and rainbow trout supplied from approved farmed sources are exempt from on-site freezing per FS SOP 30 / EU Regulation 853/2004.",
     "All wild-caught fish supplied for raw or under-treated use is delivered already frozen at −20°C ≥ 24h or −35°C ≥ 15h, with batch-level evidence retained for 12 months.",
     "Each delivery includes a batch traceability sheet matching the Rolled Fish Freezing Log entry. Discrepancies route to the EHO contact within 24h."
   ]},
  {name:"Urban Foodservices", cat:"Foodservice", cert:"BRCGS Grade AA · expiry 2027-02-18",
   docTitle:"Urban Foodservices · BRCGS Audit Cert",
   docMeta:"BRCGS Grade AA · expiry 2027-02-18",
   docBody:[
     "Urban Foodservices is BRCGS Global Standard for Food Safety certified at Grade AA, audit valid through 2027-02-18.",
     "Cold-chain delivery temps logged at vehicle level; receipt on Rolled side captured via Goods Receipt CCP-1.",
     "Allergen statements per SKU available on request — used to populate the Rolled allergen matrix."
   ]},
  {name:"Green Giggles", cat:"Produce", cert:"BRCGS Grade A · expiry 2026-11-04",
   docTitle:"Green Giggles · BRCGS + Produce Spec",
   docMeta:"BRCGS Grade A · expiry 2026-11-04",
   docBody:[
     "Green Giggles is BRCGS Global Standard for Food Safety certified at Grade A, audit valid through 2026-11-04.",
     "Pesticide residue testing on rotation per crop type; batch-level results filed on supplier portal."
   ]}
];
const CMP_LEGAL = [
  {title:"Public Liability Insurance", type:"Insurance",     issued:"2025-09-01", expires:"2026-09-01"},
  {title:"Halal Certification",        type:"Certification", issued:"2025-05-15", expires:"2026-05-15"}
];
const CMP_REFERENCE = [
  {title:"Cleaning Method Chart", meta:"v3 · Oct 2025"},
  {title:"Shelf Life Chart",      meta:"v2 · Sep 2025"}
];
const CMP_CERT_LABELS = {
  "L2-food-safety": "Level 2 Food Safety",
  "L3-food-safety": "Level 3 Food Safety",
  "h-and-s":        "Health & Safety"
};
// FSMS Appendix B summary (read-only). Full document lives in Drive.
const CMP_FSMS_SUMMARY = {
  title: "Food Safety Management System — Rolled",
  meta:  "v2.1 · 19 Apr 2026 · Adam Miller",
  body: [
    "The Rolled Food Safety Management System is the single source of truth for all CCPs, prerequisites, supplier approval, and corrective-action workflow across Trafford, Arndale, Meadowhall and the Manchester central kitchen.",
    "CCP-1 Goods Receipt: chilled ≤8°C, frozen ≤−15°C, fish 0 to +3°C. Probe one chilled + one frozen item per supplier per delivery.",
    "CCP-2 Sushi Rice Acidification: every batch logged individually; pH ≤ 4.3 to clear ambient hold. SOP 29.",
    "CCP-3 Rice Cooling: 4-hour ambient-hold rule tracked on physical tub labels (no digital tracker — see SOP 29).",
    "CCP-4 Fish Freezing: −20°C ≥ 24h or −35°C ≥ 15h before raw use. SOP 30. Sailbrand exemption on file for approved farmed sources.",
    "CCP-5 Cooking: core 75°C for 30 seconds. Probe first + last item per cook batch.",
    "CCP-6 Chilled Storage: fridge ≤+5°C, freezer ≤−18°C, fish freezer ≤−20°C. AM + PM probe readings logged on the Daily HACCP form.",
    "Pest control: daily visual + Monday Deep Sweep (5 zones). Evidence auto-raises a Corrective Action.",
    "Cleaning Schedule: 14 daily/weekly tasks per kitchen; Sunday weekly Manager Sign Off any signed-in PIN attests.",
    "Emergency Fridge: 2-hourly monitoring while a deviation is open; 4-hour rule for stock disposal (Admin sign-off).",
    "Declaration of Competency: BOH + FOH variants; annual refresh; manager countersign per FSMS Appendix D."
  ]
};

// Single PDF / doc viewer overlay. Components anywhere call openPdf(doc).
let _cmpOpenPdfFn = null;
function cmpOpenPdf(doc) { if (_cmpOpenPdfFn) _cmpOpenPdfFn(doc); }
function CmpPdfLayer() {
  const [doc, setDoc] = useState(null);
  useEffect(() => { _cmpOpenPdfFn = setDoc; return () => { _cmpOpenPdfFn = null; }; }, []);
  if (!doc) return null;
  return (
    <div className="cmp-pdf-scrim" onClick={() => setDoc(null)}>
      <div className="cmp-pdf-modal" onClick={e => e.stopPropagation()}>
        <div className="cmp-pdf-head">
          <div style={{flex:1,minWidth:0}}>
            <h3>{doc.title}</h3>
            {doc.meta && <div className="cmp-pdf-meta">{doc.meta}</div>}
          </div>
          <button className="cmp-pdf-close" onClick={() => setDoc(null)}>✕</button>
        </div>
        <div className="cmp-pdf-body">
          {(doc.body || []).map((p, i) => <p key={i}>{p}</p>)}
        </div>
      </div>
    </div>
  );
}

// Search index over docs + form sub-tiles. Wave 6 follow-up
// (2026-05-01): rebuilt on every query via cmpGetSops() / cmpGetSuppliers()
// / cmpGetLegal() so admin renames + new entries appear in search without
// a tablet reload. Form tiles are static (route names don't change).
function cmpBuildSearchIndex() {
  const idx = [];
  cmpGetSops().forEach(s => idx.push({cat:"SOP", title:s.title, meta:s.id + " · " + s.version, keywords:s.keywords||"", route:"hub-sops"}));
  idx.push({cat:"FSMS", title:CMP_FSMS_SUMMARY.title, meta:CMP_FSMS_SUMMARY.meta, keywords:"fsms appendix policy", route:"hub-fsms"});
  CMP_RISK_ASSESSMENTS.forEach(r => idx.push({cat:"Risk", title:r, meta:"", route:"hub-risk"}));
  CMP_SSOW.forEach(r => idx.push({cat:"SSoW", title:r, meta:"", route:"hub-ssow"}));
  cmpGetSuppliers().forEach(s => idx.push({cat:"Supplier", title:(s.label||s.name||""), meta:(s.cat||"") + " · " + (s.cert||""), keywords:(s.cat||"")+" "+(s.cert||""), route:"hub-suppliers"}));
  cmpGetLegal().forEach(l => idx.push({cat:"Legal", title:(l.label||l.title||""), meta:(l.type||"") + " · expires " + cmpFriendly(l.expires), route:"hub-legal"}));
  CMP_REFERENCE.forEach(r => idx.push({cat:"Reference", title:r.title, meta:r.meta, route:"hub-reference"}));
  const formTiles = [
    {title:"Daily HACCP", meta:"Opening · CCP-6 · closing", keywords:"haccp opening closing daily delivery cooling cook fridge freezer probe ph rice ccp", route:"haccp"},
    {title:"Probe Calibration", meta:"Weekly + on-drop", keywords:"probe calibration thermometer ice boiling weekly", route:"probe"},
    {title:"Pest Daily Check", meta:"Per-area visual check", keywords:"pest mice rats droppings rodents insects flies", route:"pest"},
    {title:"Pest Monday Deep Sweep", meta:"Weekly · 5 deeper checks", keywords:"pest monday sweep weekly", route:"pestmonday"},
    {title:"Fish Freezing", meta:"CCP-4 parasite control", keywords:"fish freezing salmon tuna sashimi parasite ccp4 sailbrand", route:"fish"},
    {title:"Emergency Fridge", meta:"Incident-driven", keywords:"emergency fridge breakdown incident temperature failure", route:"emergency"},
    {title:"Cleaning Schedule", meta:"Weekly cadence", keywords:"cleaning schedule disinfect chemical ppe weekly", route:"cleaning"},
    {title:"Declaration of Competency", meta:"Per-staff sign-off", keywords:"declaration competency food safety boh foh sign-off fsms acceptance", route:"declaration"},
    {title:"Corrective Actions", meta:"Open & resolved stream", keywords:"corrective action concern incident resolve", route:"corrective"}
  ];
  formTiles.forEach(t => idx.push({cat:"Form", title:t.title, meta:t.meta, keywords:t.keywords, route:t.route}));
  return idx;
}

function CmpSearchBar({nav}) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const results = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return [];
    return cmpBuildSearchIndex()
      .filter(r => r.title.toLowerCase().includes(needle) || (r.meta||"").toLowerCase().includes(needle) || r.cat.toLowerCase().includes(needle) || (r.keywords||"").toLowerCase().includes(needle))
      .slice(0, 14);
  }, [q]);
  return (
    <div className="cmp-search-bar" ref={ref}>
      <span className="cmp-search-icon">🔍</span>
      <input
        placeholder="Search documents — SOP, risk assessment, supplier, form…"
        value={q}
        onFocus={() => setOpen(true)}
        onChange={e => { setQ(e.target.value); setOpen(true); }}
      />
      {q && <button className="cmp-search-clear" onClick={() => { setQ(""); setOpen(false); }}>×</button>}
      {open && q && (
        <div className="cmp-search-results">
          {results.length === 0 ? (
            <div className="cmp-search-empty">No matches for "{q}"</div>
          ) : results.map((r, i) => (
            <div key={i} className="cmp-search-result" onClick={() => { setQ(""); setOpen(false); nav(r.route); }}>
              <span className="cmp-cat">{r.cat}</span>
              <div style={{flex:1,minWidth:0}}>
                <div className="cmp-result-title">{r.title}</div>
                {r.meta && <div className="cmp-result-meta">{r.meta}</div>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Runtime SOP list — populated by CmpHubSops + CmpHome when they fetch
// the compliance config (Wave 6.6, 2026-04-30). When set, it overrides
// the hardcoded CMP_SOPS + CMP_SOP_BODIES constants. The hardcoded copy
// is the offline / surface-failure fallback, frozen at 2026-04-30. New
// SOPs and edits go via admin → ComplianceConfig sheet → here.
var cmpRuntimeSops = null;        // [{id, label/title, version, updated, keywords, body[]}]
var cmpRuntimeSopBodies = null;   // {id: [paragraph, ...]}
// Wave 6 follow-up (2026-05-01): same runtime-cache pattern for the
// suppliers + legal-docs surfaces, used by the search index.
var cmpRuntimeSuppliers = null;   // [{id, label, cat, cert, ...}]
var cmpRuntimeLegal = null;       // [{id, label, type, issued, expires, ...}]
function cmpSetRuntimeSuppliers(items) {
  if (Array.isArray(items) && items.length) cmpRuntimeSuppliers = items;
}
function cmpSetRuntimeLegal(items) {
  if (Array.isArray(items) && items.length) cmpRuntimeLegal = items;
}
function cmpGetSuppliers() {
  return cmpRuntimeSuppliers || CMP_SUPPLIERS.map(function(s){return Object.assign({},s,{label:s.name});});
}
function cmpGetLegal() {
  return cmpRuntimeLegal || CMP_LEGAL.map(function(l){return Object.assign({},l,{label:l.title});});
}

function cmpSetRuntimeSops(items) {
  if (!Array.isArray(items) || items.length === 0) return;
  cmpRuntimeSops = items.map(function(it) {
    return {
      id: it.id,
      title: it.label || it.title || "",
      version: it.version || "",
      updated: it.updated || "",
      keywords: it.keywords || "",
      body: Array.isArray(it.body) ? it.body : []
    };
  });
  cmpRuntimeSopBodies = {};
  cmpRuntimeSops.forEach(function(s) {
    if (s.body && s.body.length) cmpRuntimeSopBodies[s.id] = s.body;
  });
}
function cmpGetSops() { return cmpRuntimeSops || CMP_SOPS; }
function cmpGetSopBody(id) {
  if (cmpRuntimeSopBodies && cmpRuntimeSopBodies[id]) return cmpRuntimeSopBodies[id];
  return CMP_SOP_BODIES && CMP_SOP_BODIES[id];
}

// SOP popup body — reads metadata from the runtime SOP catalog (when
// loaded) or falls back to the hardcoded CMP_SOPS catalog. Body text
// comes from cmpGetSopBody which prefers the runtime/admin-edited copy.
function cmpOpenSop(sop) {
  // Re-resolve from the runtime catalog if available — admins may have
  // edited the title or version since this `sop` ref was captured.
  var live = cmpGetSops().find(function(s) { return s.id === sop.id; }) || sop;
  var body = cmpGetSopBody(live.id);
  if (!body || !body.length) {
    var titlePart = String(live.title || "").split("—")[1];
    var topic = titlePart ? titlePart.trim() : (live.title || live.id);
    body = [
      "This SOP defines the standard procedure for " + topic + " across all Rolled sites.",
      "Full PDF lives in the FSMS Drive folder. This in-app view shows metadata + cross-references.",
      live.keywords ? ("Keywords: " + live.keywords) : "Cross-referenced by the FSMS appendix and the Daily HACCP form."
    ];
  }
  cmpOpenPdf({
    title: live.title,
    meta: live.id + " · " + live.version + " · updated " + live.updated,
    body: body
  });
}

// =====================================================================
// Documentation hub sub-pages
// =====================================================================

function CmpHubSops({backend, site}) {
  const [sops, setSops] = useState(cmpGetSops());
  useEffect(() => {
    if (!backend || !site) return;
    cmpFetchComplianceConfig(backend, site.key)
      .then(c => {
        const items = cmpResolveSurfaceItems(c, "sops", site.key);
        if (items && items.length) {
          cmpSetRuntimeSops(items);
          setSops(cmpGetSops());
        }
      })
      .catch(() => {});
  }, [backend, site && site.key]);
  return (
    <div className="cmp-wrap">
      <div className="cmp-card">
        <h3>SOPs · Standard Operating Procedures</h3>
        <div className="cmp-sub">{sops.length} documents · FSMS Appendix A · all current</div>
      </div>
      <div className="cmp-doc-list">
        {sops.map(s => (
          <div key={s.id} className="cmp-doc-row" onClick={() => cmpOpenSop(s)}>
            <div className="cmp-doc-icon">📘</div>
            <div className="cmp-doc-body">
              <div className="cmp-doc-name">{s.title}</div>
              <div className="cmp-doc-meta">{s.id} · {s.version} · updated {cmpFriendly(s.updated)}</div>
            </div>
            <div className="cmp-doc-right"><span className="cmp-chevron">›</span></div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CmpHubFsms() {
  return (
    <div className="cmp-wrap">
      <div className="cmp-card">
        <h3>{CMP_FSMS_SUMMARY.title}</h3>
        <div className="cmp-sub">{CMP_FSMS_SUMMARY.meta}</div>
      </div>
      <div className="cmp-card">
        {CMP_FSMS_SUMMARY.body.map((p, i) => (
          <p key={i} style={{fontSize:13.5,lineHeight:1.6,color:"var(--cm-nori)",marginBottom:10}}>{p}</p>
        ))}
        <div style={{marginTop:14,padding:"10px 14px",background:"var(--cm-sky-soft)",border:"1px solid var(--cm-sky)",borderRadius:10,fontSize:12,color:"var(--cm-nori-70)"}}>
          Full PDF lives in the FSMS Drive folder. This view shows the executive summary; for SOPs see the SOPs tile, for cross-references see the relevant form.
        </div>
      </div>
    </div>
  );
}

function CmpHubRisk() {
  return (
    <div className="cmp-wrap">
      <div className="cmp-card">
        <h3>Risk Assessments</h3>
        <div className="cmp-sub">{CMP_RISK_ASSESSMENTS.length} on file · review annually</div>
      </div>
      <div className="cmp-doc-list">
        {CMP_RISK_ASSESSMENTS.map((r, i) => (
          <div key={i} className="cmp-doc-row" onClick={() => cmpOpenPdf({title:r,meta:"Risk Assessment · review annually",body:["This risk assessment covers "+r.split(" ").slice(1).join(" ")+" hazards across the kitchen.","Hazards, control measures, residual risk, and review schedule live in the FSMS Risk Assessments folder."]})}>
            <div className="cmp-doc-icon">⚠️</div>
            <div className="cmp-doc-body">
              <div className="cmp-doc-name">{r}</div>
              <div className="cmp-doc-meta">Tap to view summary</div>
            </div>
            <div className="cmp-doc-right"><span className="cmp-chevron">›</span></div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CmpHubSsow() {
  return (
    <div className="cmp-wrap">
      <div className="cmp-card">
        <h3>Safe Systems of Work</h3>
        <div className="cmp-sub">{CMP_SSOW.length} on file · Rolled-specific in authoring</div>
      </div>
      <div className="cmp-doc-list">
        {CMP_SSOW.map((r, i) => (
          <div key={i} className="cmp-doc-row" onClick={() => cmpOpenPdf({title:r,meta:"Safe System of Work",body:["This SSoW describes the safe procedure for "+r.split(" ").slice(1).join(" ")+".","PPE, sequence, hazards, supervisor sign-off — full text in the SSoW folder."]})}>
            <div className="cmp-doc-icon">🛠️</div>
            <div className="cmp-doc-body">
              <div className="cmp-doc-name">{r}</div>
              <div className="cmp-doc-meta">Tap to view summary</div>
            </div>
            <div className="cmp-doc-right"><span className="cmp-chevron">›</span></div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CmpHubSuppliers({backend, site}) {
  const [suppliers, setSuppliers] = useState(cmpGetSuppliers());
  useEffect(() => {
    if (!backend || !site) return;
    cmpFetchComplianceConfig(backend, site.key)
      .then(c => {
        const items = cmpResolveSurfaceItems(c, "suppliers", site.key);
        if (items && items.length) {
          cmpSetRuntimeSuppliers(items);
          setSuppliers(items);
        }
      })
      .catch(() => {});
  }, [backend, site && site.key]);
  return (
    <div className="cmp-wrap">
      <div className="cmp-card">
        <h3>Approved Suppliers</h3>
        <div className="cmp-sub">{suppliers.length} on file · BRCGS or equivalent due-diligence required for first delivery</div>
      </div>
      <div className="cmp-doc-list">
        {suppliers.map((s, i) => (
          <div key={s.id || i} className="cmp-doc-row" onClick={() => cmpOpenPdf({title:s.docTitle, meta:s.docMeta, body: Array.isArray(s.body) ? s.body : (s.docBody || [])})}>
            <div className="cmp-doc-icon">🚚</div>
            <div className="cmp-doc-body">
              <div className="cmp-doc-name">{s.label || s.name}</div>
              <div className="cmp-doc-meta">{s.cat} · {s.cert}</div>
            </div>
            <div className="cmp-doc-right"><span className="cmp-chevron">›</span></div>
          </div>
        ))}
      </div>
    </div>
  );
}

function CmpHubLegal({backend, site}) {
  const [docs, setDocs] = useState(cmpGetLegal());
  useEffect(() => {
    if (!backend || !site) return;
    cmpFetchComplianceConfig(backend, site.key)
      .then(c => {
        const items = cmpResolveSurfaceItems(c, "legal-docs", site.key);
        if (items && items.length) {
          cmpSetRuntimeLegal(items);
          setDocs(items);
        }
      })
      .catch(() => {});
  }, [backend, site && site.key]);
  return (
    <div className="cmp-wrap">
      <div className="cmp-card">
        <h3>Insurance & Halal</h3>
        <div className="cmp-sub">Legal & certification documents</div>
      </div>
      <div className="cmp-doc-list">
        {docs.map((l, i) => {
          const title = l.label || l.title || "";
          const days = Math.floor((new Date(l.expires) - new Date()) / (1000*60*60*24));
          const tone = days < 0 ? "bad" : days < 30 ? "warn" : "ok";
          // Body resolution order: surface-supplied body[] (Wave 6.7) →
          // hardcoded title-keyed fallback (legacy CMP_LEGAL) → empty.
          const fallbackBody = title.indexOf("Insurance") !== -1 ? [
            "Public Liability Insurance covers third-party injury or property damage at any Rolled trading location (Trafford, Arndale, Meadowhall, Manchester central kitchen).",
            "Limit of indemnity: £5,000,000. Underwriter: Hiscox. Policy reference held by JJ; auto-renewal flag in 1Password.",
            "Renewed annually. EHO and shopping-centre landlords typically request a copy at year-start; PDF available on the Drive in /Compliance/Legal/."
          ] : [
            "Halal Certification covers all proteins handled at Rolled — Atlantic salmon, tuna, chicken, prawns, eggplant, tofu — sourced through approved suppliers (Sailbrand, Urban Foodservices).",
            "Issuing body: Halal Monitoring Committee (HMC). Certificate number held by JJ on Drive.",
            "Annual renewal cycle. Customer-facing claim 'halal' should never appear on menus until renewal is verified."
          ];
          const body = (Array.isArray(l.body) && l.body.length) ? l.body : fallbackBody;
          return (
            <div key={l.id || i} className="cmp-doc-row" onClick={() => cmpOpenPdf({title:title, meta:l.type+" · issued "+cmpFriendly(l.issued)+" · expires "+cmpFriendly(l.expires), body: body})}>
              <div className="cmp-doc-icon">📄</div>
              <div className="cmp-doc-body">
                <div className="cmp-doc-name">{title}</div>
                <div className="cmp-doc-meta">{l.type} · issued {cmpFriendly(l.issued)} · expires {cmpFriendly(l.expires)}</div>
              </div>
              <div className="cmp-doc-right">
                <span className={"cmp-chip cmp-" + tone}><span className="cmp-dot" />{days < 0 ? "expired" : days + "d"}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CmpHubReference() {
  return (
    <div className="cmp-wrap">
      <div className="cmp-card">
        <h3>Reference Charts</h3>
        <div className="cmp-sub">Quick-reference posters · printable</div>
      </div>
      <div className="cmp-doc-list">
        {CMP_REFERENCE.map((r, i) => (
          <div key={i} className="cmp-doc-row" onClick={() => cmpOpenPdf({title:r.title,meta:r.meta,body:["Reference chart — printable poster format. Source PDF in Drive."]})}>
            <div className="cmp-doc-icon">📊</div>
            <div className="cmp-doc-body">
              <div className="cmp-doc-name">{r.title}</div>
              <div className="cmp-doc-meta">{r.meta}</div>
            </div>
            <div className="cmp-doc-right"><span className="cmp-chevron">›</span></div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Staff Certs — grouped by cert type (L2 / L3 Food Safety / H&S).
// Reads the admin-managed list from Supabase (src/lib/staffCerts.js); was the
// public Apps Script getStaffCerts POST until the 15 Jul cutover. Admin adds
// certs through the Command Centre → Staff Certs tile; the PDF lives in Drive
// (FileUrl is just a Drive link). No mockup data — empty state shows
// "No certs on file" until an admin adds some, which is the state today.
// CMP_CERT_LABELS already declared at module top — reused here.
function cmpCertDaysUntil(iso) {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  return Math.floor(ms / (1000*60*60*24));
}
function cmpCertFriendly(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", {day:"numeric", month:"short", year:"numeric"});
}
// Reads Supabase now (src/lib/staffCerts.js), not the Apps Script getStaffCerts POST.
// Same public exposure, same {certId,staff,cat,issued,expires,fileUrl,notes} shape, so
// everything below this line is untouched.
//
// The testMode split does NOT carry over: it selected the 'StaffCerts_TEST' sheet tab,
// and there is no _TEST table. Both tabs were empty (probed 15 Jul), so nothing is lost
// — but anything in QA that leaned on cmpIsTestMode() for certs now reads live data.
function CmpHubCerts() {
  const [certs, setCerts] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    fetchStaffCerts()
      .then(setCerts)
      .catch(e => setError(e.message));
  }, []);
  const grouped = {};
  (certs || []).forEach(c => { (grouped[c.cat] = grouped[c.cat] || []).push(c); });
  const expected = ["L2-food-safety", "L3-food-safety", "h-and-s"];
  if (error) {
    return <div className="cmp-wrap"><div className="cmp-card cmp-coral"><h3>Couldn't load staff certs</h3><div style={{fontSize:13}}>{error}</div></div></div>;
  }
  if (certs === null) return <div className="cmp-loading">Loading staff certs…</div>;
  if (certs.length === 0) {
    return (
      <div className="cmp-wrap">
        <div className="cmp-card">
          <h3>Staff Certs</h3>
          <div className="cmp-sub">Grouped by cert type. Admin uploads via the Compliance Config tile.</div>
        </div>
        <div className="cmp-empty">No staff certs on file yet. Ask an admin to upload them.</div>
      </div>
    );
  }
  return (
    <div className="cmp-wrap">
      <div className="cmp-card">
        <h3>Staff Certs</h3>
        <div className="cmp-sub">Grouped by cert type. Admin manages uploads via the Compliance Config → Staff Certs tile.</div>
      </div>
      {expected.map(cat => {
        const rows = (grouped[cat] || []).slice().sort((a,b) => a.staff.localeCompare(b.staff));
        return (
          <div key={cat} className="cmp-card">
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12,marginBottom:8}}>
              <h4 style={{margin:0}}>{CMP_CERT_LABELS[cat] || cat}</h4>
              <span className={"cmp-chip cmp-" + (rows.length === 0 ? "warn" : "ok")}>
                <span className="cmp-dot" />{rows.length} on file
              </span>
            </div>
            <div className="cmp-doc-list">
              {rows.length === 0 && (
                <div style={{fontSize:12,color:"var(--cm-nori-60)",fontStyle:"italic",padding:"6px 0"}}>None on file.</div>
              )}
              {rows.map(r => {
                const d = cmpCertDaysUntil(r.expires);
                const tone = d == null ? "ok" : (d < 0 ? "bad" : d < 30 ? "warn" : "ok");
                const expLabel = d == null ? "—" : (d < 0 ? "expired" : d < 30 ? d + "d left" : cmpCertFriendly(r.expires));
                const open = () => {
                  if (r.fileUrl) window.open(r.fileUrl, "_blank", "noopener");
                };
                return (
                  <div key={r.certId} className="cmp-doc-row" onClick={open} style={{cursor: r.fileUrl ? "pointer" : "default"}}>
                    <div className="cmp-doc-icon" style={{fontSize:13,fontWeight:800,fontFamily:"gelica,serif"}}>{r.staff.slice(0,2).toUpperCase()}</div>
                    <div className="cmp-doc-body">
                      <div className="cmp-doc-name">{r.staff}</div>
                      <div className="cmp-doc-meta">{r.issued ? "Issued " + cmpCertFriendly(r.issued) : "Issued —"}{r.fileUrl ? " · PDF on file" : " · no PDF link"}</div>
                    </div>
                    <div className="cmp-doc-right">
                      <span className={"cmp-chip cmp-" + tone}><span className="cmp-dot" />{expLabel}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CmpHome({nav, sundayTone, todayCounts, probesDue, siteName}) {
  // todayCounts: {haccpDone, haccpTotal, cleaningCellsToday, pestDoneToday, openIncidents, openCorrective}
  const c = todayCounts || {};
  return (
    <div className="cmp-wrap">
      <CmpSearchBar nav={nav} />
      {probesDue && probesDue.length > 0 && (
        <div className="cmp-card cmp-amber" style={{marginBottom:14,display:"flex",alignItems:"center",gap:12}}>
          <div style={{fontSize:24}}>🌡️</div>
          <div style={{flex:1}}>
            <h4 style={{fontSize:15,marginBottom:2,color:"var(--cm-amber)"}}>Probe calibration due</h4>
            <div style={{fontSize:13,color:"var(--cm-nori-70)"}}>
              {probesDue.map(p => p.name + " (" + (p.daysAgo === null ? "never calibrated" : p.daysAgo + "d ago")).join(") · ")}{")"} — calibrate before next use.
            </div>
          </div>
          <button className="cmp-btn cmp-btn-primary cmp-btn-sm" onClick={() => nav("probe")}>Calibrate now</button>
        </div>
      )}
      <div className="cmp-status-strip">
        <span className="cmp-label">Today</span>
        <span className="cmp-chip cmp-info"><span className="cmp-dot" />{siteName || "This site"}</span>
        {c.haccpTotal > 0 && (
          <span className={"cmp-chip cmp-" + (c.haccpDone === c.haccpTotal ? "ok" : "warn")}>
            <span className="cmp-dot" />HACCP {c.haccpDone}/{c.haccpTotal}
          </span>
        )}
        {typeof c.cleaningCellsToday === "number" && (
          <span className={"cmp-chip cmp-" + (c.cleaningCellsToday > 0 ? "ok" : "warn")}>
            <span className="cmp-dot" />Cleaning {c.cleaningCellsToday > 0 ? c.cleaningCellsToday + " today" : "Due"}
          </span>
        )}
        <span className={"cmp-chip cmp-" + (c.pestDoneToday ? "ok" : "warn")}>
          <span className="cmp-dot" />Pest {c.pestDoneToday ? "✓" : "Due"}
        </span>
        {c.openIncidents > 0 && (
          <span className="cmp-chip cmp-bad"><span className="cmp-dot" />{c.openIncidents} open incident{c.openIncidents>1?"s":""}</span>
        )}
        {c.openCorrective > 0 && (
          <span className="cmp-chip cmp-warn"><span className="cmp-dot" />{c.openCorrective} open action{c.openCorrective>1?"s":""}</span>
        )}
        {sundayTone && (
          <span className={"cmp-chip cmp-" + sundayTone}><span className="cmp-dot" />Weekly Sign-Off Due</span>
        )}
      </div>

      <div className="cmp-home-grid">
        {/* LEFT — Forms */}
        <div>
          <div className="cmp-col-head">
            <h2>Forms</h2>
            <span className="cmp-col-sub">Daily · Weekly · On demand</span>
          </div>
          <div className="cmp-tile-list">
            <button className="cmp-tile-row" onClick={() => nav("haccp")}>
              <div className="cmp-tile-icon">📋</div>
              <div className="cmp-tile-body">
                <div className="cmp-tile-title">Daily HACCP</div>
                <div className="cmp-tile-meta">Opening · CCP-1 deliveries · CCP-6 fridges · CCP-5 cooking · CCP-2 pH · closing</div>
              </div>
              <div className="cmp-tile-right"><span className="cmp-chevron">›</span></div>
            </button>
            <button className="cmp-tile-row" onClick={() => nav("cleaning")}>
              <div className="cmp-tile-icon">🧽</div>
              <div className="cmp-tile-body">
                <div className="cmp-tile-title">Weekly Cleaning Schedule</div>
                <div className="cmp-tile-meta">Tasks × days · sign off on Sunday</div>
              </div>
              <div className="cmp-tile-right"><span className="cmp-chevron">›</span></div>
            </button>
            <button className="cmp-tile-row" onClick={() => nav("probe")}>
              <div className="cmp-tile-icon">🌡️</div>
              <div className="cmp-tile-body">
                <div className="cmp-tile-title">Probe Calibration</div>
                <div className="cmp-tile-meta">Weekly + on-drop · ±1°C tolerance</div>
              </div>
              <div className="cmp-tile-right"><span className="cmp-chevron">›</span></div>
            </button>
            <button className="cmp-tile-row" onClick={() => nav("pest")}>
              <div className="cmp-tile-icon">🔍</div>
              <div className="cmp-tile-body">
                <div className="cmp-tile-title">Pest Daily Check</div>
                <div className="cmp-tile-meta">Per-area · droppings, damage, smells, sightings</div>
              </div>
              <div className="cmp-tile-right"><span className="cmp-chevron">›</span></div>
            </button>
            <button className="cmp-tile-row" onClick={() => nav("pestmonday")}>
              <div className="cmp-tile-icon">🧹</div>
              <div className="cmp-tile-body">
                <div className="cmp-tile-title">Pest Monday Deep Sweep</div>
                <div className="cmp-tile-meta">Weekly · 5 deeper checks · Monday pre-open</div>
              </div>
              <div className="cmp-tile-right"><span className="cmp-chevron">›</span></div>
            </button>
            <button className="cmp-tile-row" onClick={() => nav("fish")}>
              <div className="cmp-tile-icon">🐟</div>
              <div className="cmp-tile-body">
                <div className="cmp-tile-title">Fish Freezing</div>
                <div className="cmp-tile-meta">Per intake · −20°C × 24h before raw use</div>
              </div>
              <div className="cmp-tile-right"><span className="cmp-chevron">›</span></div>
            </button>
            <button className="cmp-tile-row" onClick={() => nav("emergency")}>
              <div className="cmp-tile-icon">⚠️</div>
              <div className="cmp-tile-body">
                <div className="cmp-tile-title">Emergency Fridge Incident</div>
                <div className="cmp-tile-meta">Raise · 2-hourly checks · disposal sign-off</div>
              </div>
              <div className="cmp-tile-right"><span className="cmp-chevron">›</span></div>
            </button>
            <button className="cmp-tile-row" onClick={() => nav("declaration")}>
              <div className="cmp-tile-icon">✍️</div>
              <div className="cmp-tile-body">
                <div className="cmp-tile-title">Declaration of Competency &amp; FSMS</div>
                <div className="cmp-tile-meta">Self-signed · annual refresh · manager countersign</div>
              </div>
              <div className="cmp-tile-right"><span className="cmp-chevron">›</span></div>
            </button>
            <button className="cmp-tile-row" onClick={() => nav("corrective")}>
              <div className="cmp-tile-icon">🧯</div>
              <div className="cmp-tile-body">
                <div className="cmp-tile-title">Corrective Actions</div>
                <div className="cmp-tile-meta">Raise + resolve · stream from all forms</div>
              </div>
              <div className="cmp-tile-right"><span className="cmp-chevron">›</span></div>
            </button>
          </div>
        </div>

        {/* RIGHT — Documentation */}
        <div>
          <div className="cmp-col-head">
            <h2>Documentation</h2>
            <span className="cmp-col-sub">Read-only · EHO-ready</span>
          </div>
          <div className="cmp-hub-grid">
            <div className="cmp-hub-card" onClick={() => nav("hub-certs")}>
              <div className="cmp-hub-icon">👥</div>
              <div className="cmp-hub-title">Staff Certs</div>
              <div className="cmp-hub-meta">L2 / L3 Food Safety · H&amp;S</div>
            </div>
            <div className="cmp-hub-card" onClick={() => nav("hub-sops")}>
              <div className="cmp-hub-icon">📘</div>
              <div className="cmp-hub-title">SOPs</div>
              <div className="cmp-hub-meta">{cmpGetSops().length} documents</div>
              <div className="cmp-hub-state cmp-state-ok">All current</div>
            </div>
            <div className="cmp-hub-card" onClick={() => nav("hub-fsms")}>
              <div className="cmp-hub-icon">📕</div>
              <div className="cmp-hub-title">FSMS</div>
              <div className="cmp-hub-meta">23-page master document</div>
              <div className="cmp-hub-state cmp-state-ok">{CMP_FSMS_SUMMARY.meta.split(" · ")[0]} · {CMP_FSMS_SUMMARY.meta.split(" · ")[1]}</div>
            </div>
            <div className="cmp-hub-card" onClick={() => nav("hub-risk")}>
              <div className="cmp-hub-icon">⚠️</div>
              <div className="cmp-hub-title">Risk Assessments</div>
              <div className="cmp-hub-meta">{CMP_RISK_ASSESSMENTS.length} on file</div>
              <div className="cmp-hub-state cmp-state-ok">All current</div>
            </div>
            <div className="cmp-hub-card" onClick={() => nav("hub-ssow")}>
              <div className="cmp-hub-icon">🛠️</div>
              <div className="cmp-hub-title">Safe Systems of Work</div>
              <div className="cmp-hub-meta">{CMP_SSOW.length} on file</div>
              <div className="cmp-hub-state cmp-state-warn">Rolled-specific in authoring</div>
            </div>
            <div className="cmp-hub-card" onClick={() => nav("hub-suppliers")}>
              <div className="cmp-hub-icon">🚚</div>
              <div className="cmp-hub-title">Approved Suppliers</div>
              <div className="cmp-hub-meta">{CMP_SUPPLIERS.length} on file · due-diligence certs</div>
              <div className="cmp-hub-state cmp-state-ok">Current</div>
            </div>
            <div className="cmp-hub-card" onClick={() => nav("hub-legal")}>
              <div className="cmp-hub-icon">📄</div>
              <div className="cmp-hub-title">Insurance &amp; Halal</div>
              <div className="cmp-hub-meta">Legal & certification docs</div>
              <div className="cmp-hub-state cmp-state-warn">Halal expires soon</div>
            </div>
            <div className="cmp-hub-card" onClick={() => nav("hub-reference")}>
              <div className="cmp-hub-icon">📊</div>
              <div className="cmp-hub-title">Reference Charts</div>
              <div className="cmp-hub-meta">Cleaning Method · Shelf Life</div>
              <div className="cmp-hub-state cmp-state-ok">v3 · Oct 2025</div>
            </div>
            <div className="cmp-hub-card" onClick={() => cmpToast("Allergen Matrix lives in the dedicated Allergens tile — JJ-owned, in progress.", "warn")}>
              <div className="cmp-hub-icon">🥜</div>
              <div className="cmp-hub-title">Allergen Matrix</div>
              <div className="cmp-hub-meta">Lives in separate Allergens tile</div>
              <div className="cmp-hub-state cmp-state-warn">JJ-owned · in progress</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- Cleaning Schedule (Trafford-shipped pattern) ----
function CmpCleaningSchedule({backend, site, requestPin, staffByPin}) {
  const pinPrompt = useMemo(() => makeCmpPinPrompt(requestPin, staffByPin), [requestPin, staffByPin]);
  const [config, setConfig] = useState(null);
  const [matrix, setMatrix] = useState({});
  const [weekSig, setWeekSig] = useState(null);
  const [pendingCell, setPendingCell] = useState(null);
  const hist = useCmpHistoryView(backend, site.key, "CleaningLog", "week");
  // Week-mode: weekStart tracks the calendar pick. viewDate is already
  // the Monday (the hook normalised it on setViewDate). Today's view
  // falls back to cmpMondayOf(today).
  const weekStart = hist.isHistorical ? hist.viewDate : cmpMondayOf(new Date());
  const todayIdx = (new Date().getDay() + 6) % 7;
  const sundayTone = cmpSundayPillTone();
  const days = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
  const dayDates = days.map((_, i) => { const d = new Date(weekStart); d.setDate(d.getDate() + i); return d; });
  const weekRangeLabel = dayDates[0].toLocaleDateString("en-GB",{day:"numeric",month:"short"}) + " – " + dayDates[6].toLocaleDateString("en-GB",{day:"numeric",month:"short",year:"numeric"});

  useEffect(() => {
    cmpFetchComplianceConfig(backend, site.key)
      .then(setConfig)
      .catch(e => cmpToast("Couldn't load cleaning tasks: " + e.message, "error"));
  }, [backend, site.key]);

  const tasks = useMemo(() => config ? cmpResolveSurfaceItems(config, "cleaning-tasks", site.key) : [], [config, site.key]);

  // Rehydrate the matrix from the audit trail. Live mode reads the last
  // 14 days, historical mode uses the hook's pre-fetched Mon→Sun slice.
  // Reset matrix + weekSig immediately on weekStart change so the
  // previous week's cells don't briefly bleed through while the new
  // fetch resolves.
  //
  // Wave 6 follow-up (2026-05-01): rows now carry a stable TaskId from
  // the cleaning-tasks surface. Match by TaskId first → admin reorders
  // don't shift historical ticks. Fall back to TaskIndex for legacy
  // rows written before this change.
  useEffect(() => {
    setMatrix({});
    setWeekSig(null);
    const idByPosition = tasks.map(t => t.id);
    const positionById = {};
    tasks.forEach((t, i) => { if (t && t.id) positionById[t.id] = i; });
    const hydrate = (rows) => {
      const m = {};
      rows.forEach(r => {
        if (String(r.WeekStart) !== weekStart) return;
        const di = parseInt(r.DayIndex, 10);
        if (isNaN(di)) return;
        let ti;
        if (r.TaskId && positionById[r.TaskId] !== undefined) {
          ti = positionById[r.TaskId];
        } else {
          ti = parseInt(r.TaskIndex, 10);
          if (isNaN(ti)) return;
        }
        if (m[ti] && m[ti][di]) return;
        if (!m[ti]) m[ti] = {};
        m[ti][di] = {state: r.State || "empty", init: cmpInitials(r.StaffName || "")};
      });
      setMatrix(m);
    };
    if (hist.isHistorical) {
      hydrate(hist.histRows || []);
    } else {
      cmpFetchFormToday(backend, site.key, "CleaningLog", 14)
        .then(hydrate)
        .catch(() => {});
    }
  }, [backend, site.key, weekStart, hist.isHistorical, hist.histRows, tasks]);

  const cellState = (taskIdx, dayIdx) => (matrix[taskIdx] && matrix[taskIdx][dayIdx]) || {state: "empty"};

  const writeCell = (taskIdx, dayIdx, state, staff) => {
    const t = tasks[taskIdx];
    const taskName = t && t.label;
    const taskId = t && t.id;
    cmpComplianceLog(backend, site.key, "cleaning-cell", {
      weekStart: weekStart, taskIndex: taskIdx, dayIndex: dayIdx,
      taskName: taskName, taskId: taskId, state: state
    }, staff.pin).then(() => {
      setMatrix(m => Object.assign({}, m, {
        [taskIdx]: Object.assign({}, m[taskIdx] || {}, {[dayIdx]: {state: state, init: staff.initials}})
      }));
      cmpToast(state === "done" ? "Cell marked complete" : state === "skipped" ? "Cell marked not completed" : "Cell sign-off removed", state === "skipped" ? "warn" : "success");
    }).catch(e => cmpToast("Save failed: " + e.message, "error"));
  };

  const onCellClick = (taskIdx, dayIdx) => {
    if (hist.isHistorical) return; // read-only past weeks
    const cur = cellState(taskIdx, dayIdx);
    if (cur.state === "done" || cur.state === "skipped") {
      pinPrompt({prompt: "Remove cleaning sign-off", requiredRoles: ["Admin"], onConfirm: (s) => writeCell(taskIdx, dayIdx, "empty", s)});
    } else {
      setPendingCell({taskIdx, dayIdx});
    }
  };

  const completeCell = (yes) => {
    const {taskIdx, dayIdx} = pendingCell;
    setPendingCell(null);
    const taskName = tasks[taskIdx] && tasks[taskIdx].label;
    pinPrompt({
      prompt: yes ? "PIN — Task Completed" : "PIN — Task Not Completed",
      desc: taskName + " · " + days[dayIdx],
      onConfirm: (s) => writeCell(taskIdx, dayIdx, yes ? "done" : "skipped", s),
      onCancel: () => setPendingCell({taskIdx, dayIdx})
    });
  };

  const weekSign = () => {
    pinPrompt({
      prompt: "Weekly Manager Sign Off",
      desc: "Confirms the week's cleaning has been reviewed. Any signed-in staff PIN works.",
      onConfirm: (s) => {
        cmpComplianceLog(backend, site.key, "cleaning-weekly-signoff", {weekStart: weekStart}, s.pin).then(() => {
          setWeekSig({init: s.initials, name: s.name, ts: new Date().toISOString()});
          cmpToast("Week signed off by " + s.name, "success");
        }).catch(e => cmpToast("Sign-off failed: " + e.message, "error"));
      }
    });
  };

  if (!config) return <div className="cmp-loading">Loading cleaning schedule…</div>;
  if (!tasks.length) return <div className="cmp-empty">No cleaning tasks configured for this site yet. Ask admin to add some.</div>;

  return (
    <div className="cmp-wrap">
      <CmpHistoryBanner date={hist.viewDate} historical={hist.isHistorical} onClear={() => hist.setViewDate(null)} label={"Week of " + cmpFriendly(weekStart)} backLabel="← Back to this week" />
      <CmpHistoricalSignerBanner date={hist.viewDate} historical={hist.isHistorical} formLabel="Cleaning — historical week" signedBy={hist.signedBy} />
      {!hist.isHistorical && sundayTone && !weekSig && (
        <div className={"cmp-card cmp-" + (sundayTone === "bad" ? "coral" : "amber")}>
          <h4 style={{color:"var(--cm-" + (sundayTone === "bad" ? "coral" : "amber") + ")",marginBottom:4}}>Sunday — Sign-Off Required</h4>
          <div style={{fontSize:13,color:"var(--cm-nori-70)"}}>Last day of this cleaning week. Any signed-in staff can sign the week off below before close.</div>
        </div>
      )}
      <div className="cmp-card">
        <div style={{display:"flex",justifyContent:"space-between",flexWrap:"wrap",gap:12,alignItems:"center"}}>
          <div style={{flex:1,minWidth:0}}>
            <h3>Weekly Cleaning Schedule</h3>
            <div className="cmp-sub">Week of <strong>{weekRangeLabel}</strong>. Tap a cell to mark Completed (green) or Not completed (grey). Both record initials. Sign the week off on Sunday.</div>
          </div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
            <CmpCalendarButton onPick={hist.setViewDate} viewingDate={hist.viewDate} mode="week" />
            <span style={{fontSize:11,letterSpacing:"0.08em",textTransform:"uppercase",padding:"6px 12px",borderRadius:6,background:"var(--cm-wasabi-20)",color:"var(--cm-nori)",fontWeight:800,whiteSpace:"nowrap"}}>📅 {weekRangeLabel}</span>
          </div>
        </div>
      </div>
      {weekSig && (
        <div className="cmp-card cmp-green">
          <h4 style={{color:"var(--cm-green)",marginBottom:4}}>✓ Weekly Sign Off Complete</h4>
          <div style={{fontSize:13,color:"var(--cm-nori-70)"}}>Signed by <strong>{weekSig.name}</strong> at <strong>{new Date(weekSig.ts).toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"})}</strong>.</div>
        </div>
      )}
      <div className="cmp-card cmp-matrix-scroll" style={{padding:"4px 8px"}}>
        <table className="cmp-matrix">
          <thead><tr>
            <th className="cmp-task">Task</th>
            {days.map((d, i) => (
              <th key={d} style={i === todayIdx ? {color:"var(--cm-nori)"} : null}>
                <div style={{fontSize:11,fontWeight:800,letterSpacing:"0.06em"}}>{d.toUpperCase()}</div>
                <div style={{fontSize:9,fontWeight:700,opacity:0.65,marginTop:2}}>{dayDates[i].toLocaleDateString("en-GB",{day:"numeric",month:"short"}).toUpperCase()}</div>
              </th>
            ))}
          </tr></thead>
          <tbody>
            {tasks.map((t, i) => (
              <tr key={t.id || i}>
                <td className="cmp-task"><div className="cmp-tname">{t.label}</div></td>
                {days.map((_, d) => {
                  const cell = cellState(i, d);
                  const isToday = d === todayIdx;
                  const tooltip = cell.state === "done" ? "Done by " + cell.init : cell.state === "skipped" ? "Not completed (signed by " + cell.init + ")" : "Tap to initial";
                  return (
                    <td key={d}>
                      <button className={"cmp-cell-btn " + (cell.state === "done" ? "cmp-done" : cell.state === "skipped" ? "cmp-skipped" : "") + (isToday ? " cmp-today" : "")} onClick={() => onCellClick(i, d)} title={tooltip}>
                        {(cell.state === "done" || cell.state === "skipped") ? cell.init : "·"}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!hist.isHistorical && (
        <div style={{display:"flex",gap:12,flexWrap:"wrap",marginTop:12,alignItems:"center"}}>
          {!weekSig && <button className="cmp-btn cmp-btn-primary" onClick={weekSign}>Weekly Manager Sign Off (PIN required)</button>}
          <span style={{fontSize:12,color:"var(--cm-nori-60)"}}>Any signed-in staff PIN works. Sign off on Sunday. Unsigned weeks raise an admin alert.</span>
        </div>
      )}
      <div className="cmp-card" style={{background:"var(--cm-sky-soft)",border:"1px solid var(--cm-sky)",marginTop:12,padding:"10px 14px"}}>
        <div style={{fontSize:13,lineHeight:1.5,color:"var(--cm-nori-70)"}}>
          <strong>Cell colours:</strong> 🟢 green = completed · ⚪ grey = not completed (initials still captured) · empty = not yet recorded.
        </div>
      </div>
      {!hist.isHistorical && pendingCell && (
        <div className="cmp-confirm-scrim" onClick={() => setPendingCell(null)}>
          <div className="cmp-confirm-modal" onClick={e => e.stopPropagation()}>
            <h3 style={{color:"var(--cm-wasabi)",marginBottom:8}}>Was the task completed?</h3>
            <div style={{color:"rgba(254,250,240,0.75)",fontSize:13,marginBottom:20,lineHeight:1.4}}>
              {tasks[pendingCell.taskIdx].label} · {days[pendingCell.dayIdx]}
            </div>
            <div style={{display:"flex",gap:10,justifyContent:"center",flexWrap:"wrap"}}>
              <button onClick={() => completeCell(false)} style={{padding:"14px 22px",borderRadius:10,background:"#7a7a7a",color:"#fff",fontWeight:800,fontSize:14,border:"none",cursor:"pointer",fontFamily:"Manrope,sans-serif"}}>No, not completed</button>
              <button onClick={() => completeCell(true)} style={{padding:"14px 22px",borderRadius:10,background:"var(--cm-wasabi)",color:"var(--cm-nori)",fontWeight:800,fontSize:14,border:"none",cursor:"pointer",fontFamily:"Manrope,sans-serif"}}>Yes, completed</button>
            </div>
            <div style={{fontSize:11,color:"rgba(254,250,240,0.55)",marginTop:12}}>Both capture your PIN. No still records who attempted it (cell turns grey, not green).</div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Pest Daily Check ----
function CmpPestDaily({backend, site, requestPin, staffByPin}) {
  const pinPrompt = useMemo(() => makeCmpPinPrompt(requestPin, staffByPin), [requestPin, staffByPin]);
  const [config, setConfig] = useState(null);
  const [newRow, setNewRow] = useState({area: "", evidence: "No", details: "", action: ""});
  const [todayRows, setTodayRows] = useState([]);
  const hist = useCmpHistoryView(backend, site.key, "PestLog", "day");
  const visibleRows = hist.isHistorical
    ? hist.histRows.filter(r => String(r.Event) === "daily-check").map(r => ({
        ts: r._ts, area: r.Area || "", evidence: r.Evidence || "No",
        details: r.Details || "", action: r.CorrectiveAction || "",
        init: cmpInitials(r.StaffName || ""), incidentId: r.CorrectiveActionId || ""
      }))
    : todayRows;

  useEffect(() => {
    cmpFetchComplianceConfig(backend, site.key)
      .then(c => {
        setConfig(c);
        const areas = cmpResolveSurfaceItems(c, "pest-areas", site.key);
        if (areas.length && !newRow.area) setNewRow(r => Object.assign({}, r, {area: areas[0].label}));
      })
      .catch(e => cmpToast("Couldn't load pest areas: " + e.message, "error"));
  }, [backend, site.key]);

  // Rehydrate today's pest rows.
  useEffect(() => {
    const today = cmpTodayIso();
    cmpFetchFormToday(backend, site.key, "PestLog", 1)
      .then(rows => {
        const today_rows = rows
          .filter(r => String(r.Date) === today && String(r.Event) === "daily-check")
          .map(r => ({
            ts: r._ts, area: r.Area || "", evidence: r.Evidence || "No",
            details: r.Details || "", action: r.CorrectiveAction || "",
            init: cmpInitials(r.StaffName || ""),
            incidentId: r.CorrectiveActionId || ""
          }));
        setTodayRows(today_rows);
      })
      .catch(() => {});
  }, [backend, site.key]);

  const areas = useMemo(() => config ? cmpResolveSurfaceItems(config, "pest-areas", site.key) : [], [config, site.key]);

  const submit = () => {
    if (newRow.evidence === "Yes" && (!newRow.details.trim() || !newRow.action.trim())) {
      cmpToast("Yes-evidence requires details + corrective action", "error"); return;
    }
    if (!newRow.area) { cmpToast("Pick an area", "error"); return; }
    pinPrompt({
      prompt: "Sign off pest check",
      desc: "Your PIN stamps this check. Yes-rows raise a corrective action automatically.",
      onConfirm: (staff) => {
        cmpComplianceLog(backend, site.key, "pest-daily", {
          area: newRow.area, evidence: newRow.evidence, details: newRow.details, action: newRow.action
        }, staff.pin).then((d) => {
          setTodayRows(r => [{
            ts: new Date().toISOString(), area: newRow.area, evidence: newRow.evidence,
            details: newRow.details, action: newRow.action, init: staff.initials,
            incidentId: d && d.incidentId ? d.incidentId : ""
          }, ...r]);
          setNewRow({area: (areas[0] && areas[0].label) || "", evidence: "No", details: "", action: ""});
          cmpToast(newRow.evidence === "Yes" ? "Yes-row submitted · corrective action raised" : "Pest check logged", newRow.evidence === "Yes" ? "warn" : "success");
        }).catch(e => cmpToast("Save failed: " + e.message, "error"));
      }
    });
  };

  if (!config) return <div className="cmp-loading">Loading pest areas…</div>;
  if (!areas.length) return <div className="cmp-empty">No pest areas configured for this site yet. Ask admin to add some.</div>;

  return (
    <div className="cmp-wrap">
      <CmpHistoryBanner date={hist.viewDate} onClear={() => hist.setViewDate(null)} />
      <CmpHistoricalSignerBanner date={hist.viewDate} formLabel="Pest Daily — historical view" signedBy={hist.signedBy} />
      <div className="cmp-card">
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,marginBottom:8}}>
          <h3 style={{margin:0}}>Daily Pest Visual Check</h3>
          <CmpCalendarButton onPick={hist.setViewDate} viewingDate={hist.viewDate} mode="day" />
        </div>
        <div className="cmp-sub">Check each area for droppings, damage, smells, sightings. Reference: <a style={{color:"var(--cm-nori)",fontWeight:700,borderBottom:"1px dotted",cursor:"pointer"}} onClick={() => cmpOpenSop({id:"SOP-17",title:"SOP 17 — Pest Control",version:"v2",updated:"2026-04-19"})}>SOP 17</a>.</div>
        <div className="cmp-card" style={{background:"var(--cm-sky-soft)",border:"1px solid var(--cm-sky)",margin:"12px 0 0",padding:"10px 12px"}}>
          <h5 style={{fontSize:11,fontWeight:800,letterSpacing:"0.06em",textTransform:"uppercase",color:"var(--cm-nori-60)",marginBottom:8}}>What to Look For</h5>
          <div style={{padding:"4px 0",fontSize:12,lineHeight:1.5}}><strong style={{color:"var(--cm-nori)"}}>Droppings:</strong> <span style={{color:"var(--cm-nori-70)"}}>Small, dark pellets.</span></div>
          <div style={{padding:"4px 0",fontSize:12,lineHeight:1.5,borderTop:"1px dashed var(--cm-nori-08)"}}><strong style={{color:"var(--cm-nori)"}}>Damage:</strong> <span style={{color:"var(--cm-nori-70)"}}>Gnaw marks on packaging, cables, or structure.</span></div>
          <div style={{padding:"4px 0",fontSize:12,lineHeight:1.5,borderTop:"1px dashed var(--cm-nori-08)"}}><strong style={{color:"var(--cm-nori)"}}>Smells:</strong> <span style={{color:"var(--cm-nori-70)"}}>Unusual or ammonia-like odours.</span></div>
          <div style={{padding:"4px 0",fontSize:12,lineHeight:1.5,borderTop:"1px dashed var(--cm-nori-08)"}}><strong style={{color:"var(--cm-nori)"}}>Sightings:</strong> <span style={{color:"var(--cm-nori-70)"}}>Live or dead insects/rodents.</span></div>
          <div style={{padding:"4px 0",fontSize:12,lineHeight:1.5,borderTop:"1px dashed var(--cm-nori-08)"}}><strong style={{color:"var(--cm-nori)"}}>Proofing:</strong> <span style={{color:"var(--cm-nori-70)"}}>Any new holes or gaps in the walls/floors.</span></div>
        </div>
        {!hist.isHistorical && (
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(220px,1fr))",gap:12,marginTop:12,alignItems:"start"}}>
            <div className="cmp-field">
              <label>Area Checked</label>
              <select value={newRow.area} onChange={e => setNewRow(r => Object.assign({}, r, {area: e.target.value}))} style={{height:42}}>
                {areas.map(a => <option key={a.id || a.label} value={a.label}>{a.label}</option>)}
              </select>
            </div>
            <div className="cmp-field">
              <label>Evidence of Pests?</label>
              <select value={newRow.evidence} onChange={e => setNewRow(r => Object.assign({}, r, {evidence: e.target.value}))} style={{height:42}}>
                <option>No</option><option>Yes</option>
              </select>
            </div>
          </div>
        )}
        {!hist.isHistorical && newRow.evidence === "Yes" && (
          <div style={{marginTop:12,display:"flex",flexDirection:"column",gap:12}}>
            <div className="cmp-field cmp-warn">
              <label>Details (required)</label>
              <textarea value={newRow.details} onChange={e => setNewRow(r => Object.assign({}, r, {details: e.target.value}))} placeholder="Droppings? Damage? Smell? Location?" />
            </div>
            <div className="cmp-field cmp-warn">
              <label>Corrective action (required)</label>
              <textarea value={newRow.action} onChange={e => setNewRow(r => Object.assign({}, r, {action: e.target.value}))} placeholder="What did you do? Who else notified?" />
              <span className="cmp-hint" style={{color:"var(--cm-coral)",fontWeight:700}}>⚠ Yes-row raises a Corrective Action automatically</span>
            </div>
          </div>
        )}
        {!hist.isHistorical && (
          <div style={{marginTop:12}}>
            <button className="cmp-btn cmp-btn-primary" onClick={submit}>Submit Check (PIN required)</button>
          </div>
        )}
      </div>
      {visibleRows.length > 0 && (
        <div className="cmp-card">
          <h3>{hist.isHistorical ? "Checks on " + cmpFriendly(hist.viewDate) : "This Session"}</h3>
          <table className="cmp-row-table">
            <thead><tr><th>Time</th><th>Area</th><th>Evidence</th><th>Details</th><th>Action</th><th>By</th></tr></thead>
            <tbody>
              {visibleRows.map((r, i) => (
                <tr key={i} style={r.evidence === "Yes" ? {background:"var(--cm-coral-soft)"} : null}>
                  <td style={{fontWeight:700}}>{new Date(r.ts).toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"})}</td>
                  <td>{r.area}</td>
                  <td><span className={"cmp-badge " + (r.evidence === "Yes" ? "cmp-bad" : "cmp-ok")}>{r.evidence}</span></td>
                  <td style={{fontSize:12}}>{r.details || <span style={{color:"var(--cm-nori-60)"}}>—</span>}</td>
                  <td style={{fontSize:12}}>{r.action || <span style={{color:"var(--cm-nori-60)"}}>—</span>}</td>
                  <td><span style={{fontFamily:"gelica,serif",background:"var(--cm-wasabi)",padding:"2px 6px",borderRadius:4,fontWeight:800,fontSize:11}}>{r.init}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {hist.isHistorical && !hist.loading && visibleRows.length === 0 && (
        <div className="cmp-empty">No pest checks recorded on {cmpFriendly(hist.viewDate)}.</div>
      )}
    </div>
  );
}

// ---- Pest Monday Deep Sweep ----
// Wave 6.4 (2026-04-30): the 5-item checklist now reads from the
// admin-managed `pest-monday-checks` surface. Items have shape
// {id, n, label, check, action, status}. Historical PestLog rows
// reference TaskNumber by `n` (the displayed integer string), so
// admins can re-order or rename items without breaking ticks.
// The constant below is no longer referenced; backend default seeds the
// surface with these same 5 items so first-mount fallback still renders.
function CmpPestMondaySweep({backend, site, requestPin, staffByPin}) {
  const pinPrompt = useMemo(() => makeCmpPinPrompt(requestPin, staffByPin), [requestPin, staffByPin]);
  const [config, setConfig] = useState(null);
  const [weeklyState, setWeeklyState] = useState({});
  const [correctiveNeeded, setCorrectiveNeeded] = useState(false);
  const [correctiveText, setCorrectiveText] = useState("");
  const [submittedBy, setSubmittedBy] = useState(null);
  const hist = useCmpHistoryView(backend, site.key, "PestLog", "week");
  const weekStart = hist.isHistorical ? hist.viewDate : cmpMondayOf(new Date());

  useEffect(() => {
    cmpFetchComplianceConfig(backend, site.key)
      .then(setConfig)
      .catch(e => cmpToast("Couldn't load deep-sweep checks: " + e.message, "error"));
  }, [backend, site.key]);

  const checks = useMemo(
    () => config ? cmpResolveSurfaceItems(config, "pest-monday-checks", site.key) : [],
    [config, site.key]
  );
  const allChecked = Object.keys(weeklyState).length === checks.length && checks.length > 0;
  const correctiveValid = !correctiveNeeded || correctiveText.trim().length > 0;
  const canSubmit = !hist.isHistorical && allChecked && correctiveValid && !submittedBy;

  // Rehydrate this week's tick state from PestLog rows where Event=='deep-sweep-tick'.
  // Live mode reads the past 14 days; historical mode uses the hook's
  // pre-fetched Mon→Sun slice.
  useEffect(() => {
    const hydrate = (rows) => {
      const ws = new Date(weekStart);
      const next = new Date(ws); next.setDate(next.getDate() + 7);
      const w = {};
      let submission = null;
      rows.forEach(r => {
        const rowDate = new Date(r.Date || r._ts);
        if (rowDate < ws || rowDate >= next) return;
        if (String(r.Event) === "deep-sweep-tick") {
          const taskNum = String(r.TaskNumber || "").trim();
          const idx = checks.findIndex(c => c.n === taskNum);
          if (idx < 0) return;
          if (w[idx]) return;
          w[idx] = {init: cmpInitials(r.StaffName || ""), ts: r._ts};
        } else if (String(r.Event) === "deep-sweep-submit" && !submission) {
          submission = {
            name: r.StaffName || "", init: cmpInitials(r.StaffName || ""),
            ts: r._ts,
            corrective: (String(r.CorrectiveAction || "") || null),
            incidentId: r.CorrectiveActionId || ""
          };
        }
      });
      setWeeklyState(w);
      // submittedBy must reflect ONLY the visible week. In live mode,
      // the previous Pest Daily check session may have left a stale
      // submission visible across a week-boundary or after switching
      // back from a historical view — so always set it from the latest
      // hydrate, including null when there's no submission row yet.
      setSubmittedBy(submission);
    };
    if (hist.isHistorical) {
      hydrate(hist.histRows || []);
    } else {
      cmpFetchFormToday(backend, site.key, "PestLog", 14)
        .then(hydrate)
        .catch(() => {});
    }
  }, [backend, site.key, weekStart, hist.isHistorical, hist.histRows]);

  const checkTask = (i) => {
    pinPrompt({
      prompt: "Mark task checked",
      desc: "Task " + checks[i].n + " · " + (checks[i].label || checks[i].title || ""),
      requiredRoles: ["BOH","Admin"],
      onConfirm: (s) => {
        cmpComplianceLog(backend, site.key, "pest-deep-sweep-tick", {
          taskNumber: checks[i].n, weekStart: weekStart
        }, s.pin).then(() => {
          setWeeklyState(t => Object.assign({}, t, {[i]: {init: s.initials, ts: new Date().toISOString()}}));
          cmpToast("Task " + checks[i].n + " checked", "success");
        }).catch(e => cmpToast("Save failed: " + e.message, "error"));
      }
    });
  };
  const clearTask = (i) => setWeeklyState(t => { const n = Object.assign({}, t); delete n[i]; return n; });

  const submitWeekly = () => {
    if (!canSubmit) return;
    pinPrompt({
      prompt: "Sign off Monday deep sweep",
      desc: correctiveNeeded ? "Includes a corrective action" : "All tasks checked · no corrective action",
      requiredRoles: ["BOH","Admin"],
      onConfirm: (s) => {
        cmpComplianceLog(backend, site.key, "pest-deep-sweep-submit", {
          weekStart: weekStart, correctiveNeeded: correctiveNeeded,
          correctiveText: correctiveNeeded ? correctiveText.trim() : ""
        }, s.pin).then((d) => {
          setSubmittedBy({name: s.name, init: s.initials, ts: new Date().toISOString(),
            corrective: correctiveNeeded ? correctiveText.trim() : null,
            incidentId: d && d.incidentId ? d.incidentId : ""});
          cmpToast("Submitted by " + s.name, correctiveNeeded ? "warn" : "success");
        }).catch(e => cmpToast("Submit failed: " + e.message, "error"));
      }
    });
  };

  return (
    <div className="cmp-wrap">
      <CmpHistoryBanner date={hist.viewDate} historical={hist.isHistorical} onClear={() => hist.setViewDate(null)} label={"Week of " + cmpFriendly(weekStart)} backLabel="← Back to this week" />
      <CmpHistoricalSignerBanner date={hist.viewDate} historical={hist.isHistorical} formLabel="Pest Monday Deep Sweep — historical week" signedBy={hist.signedBy} />
      <div className="cmp-card">
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,marginBottom:8}}>
          <h3 style={{margin:0}}>Monday Deep Sweep</h3>
          <CmpCalendarButton onPick={hist.setViewDate} viewingDate={hist.viewDate} mode="week" />
        </div>
        <div className="cmp-sub">Per FSMS §4.4 + §9.2 — completed Monday pre-open. Tap each task to mark it Checked (PIN required). Capture any corrective action at the bottom before submitting.</div>
      </div>
      {submittedBy && (
        <div className="cmp-card cmp-green">
          <h4 style={{color:"var(--cm-green)",marginBottom:4}}>✓ Submitted</h4>
          <div style={{fontSize:13}}>Submitted by <strong>{submittedBy.name}</strong> at {new Date(submittedBy.ts).toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"})}.{submittedBy.corrective ? " Corrective action captured." : ""}{submittedBy.incidentId ? " " + submittedBy.incidentId : ""}</div>
        </div>
      )}
      {checks.map((c, i) => {
        const state = weeklyState[i];
        return (
          <div key={i} className="cmp-card" style={state ? {borderLeft:"4px solid var(--cm-green)"} : {}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:12}}>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontWeight:800,fontSize:15,marginBottom:4}}>{c.n}. {c.label || c.title}</div>
                <div style={{fontSize:12,color:"var(--cm-nori-70)",lineHeight:1.4}}><strong>Check for:</strong> {c.check}</div>
                <div style={{fontSize:12,color:"var(--cm-nori-70)",lineHeight:1.4}}><strong>Action:</strong> {c.action}</div>
              </div>
              {!state ? (
                <button className="cmp-btn cmp-btn-primary cmp-btn-sm" onClick={() => checkTask(i)} disabled={!!submittedBy || hist.isHistorical}>Checked? (PIN)</button>
              ) : (
                <div style={{display:"flex",gap:8,alignItems:"center",flexShrink:0}}>
                  <span className="cmp-chip cmp-ok"><span className="cmp-dot" />✓ Checked</span>
                  <span style={{fontSize:11,background:"var(--cm-wasabi)",padding:"2px 6px",borderRadius:4,fontFamily:"gelica,serif",fontWeight:800}}>{state.init}</span>
                  {!submittedBy && !hist.isHistorical && <button className="cmp-btn cmp-btn-ghost cmp-btn-sm" onClick={() => clearTask(i)}>Undo</button>}
                </div>
              )}
            </div>
          </div>
        );
      })}
      {!hist.isHistorical && (
        <div className="cmp-card">
          <h4>Corrective Action Required?</h4>
          <div className="cmp-sub" style={{fontSize:12}}>Anything that needs fixing during this sweep? Tick Yes and describe the action — you can't submit without describing what was done.</div>
          <div style={{marginTop:12,display:"flex",gap:14,alignItems:"center"}}>
            <label style={{display:"flex",alignItems:"center",gap:8,cursor:!!submittedBy ? "default" : "pointer"}}>
              <input type="radio" name="cmp-pms-cor" checked={!correctiveNeeded} onChange={() => setCorrectiveNeeded(false)} disabled={!!submittedBy} />
              <strong>No</strong>
            </label>
            <label style={{display:"flex",alignItems:"center",gap:8,cursor:!!submittedBy ? "default" : "pointer"}}>
              <input type="radio" name="cmp-pms-cor" checked={correctiveNeeded} onChange={() => setCorrectiveNeeded(true)} disabled={!!submittedBy} />
              <strong>Yes</strong>
            </label>
          </div>
          {correctiveNeeded && (
            <div className="cmp-field cmp-warn" style={{marginTop:12}}>
              <label>Corrective action</label>
              <textarea value={correctiveText} onChange={e => setCorrectiveText(e.target.value)} placeholder="What was the issue? What did you do about it?" disabled={!!submittedBy} />
            </div>
          )}
        </div>
      )}
      {!hist.isHistorical && (
        <div className="cmp-card" style={{textAlign:"center"}}>
          <div className="cmp-sub" style={{marginBottom:12}}>{Object.keys(weeklyState).length}/{checks.length} checked{correctiveNeeded ? " · corrective required" : ""}</div>
          <button className="cmp-btn cmp-btn-primary" onClick={submitWeekly} disabled={!canSubmit}>
            {submittedBy ? "Submitted — read-only" : "Submit Deep Sweep (PIN required)"}
          </button>
        </div>
      )}
      {hist.isHistorical && submittedBy && submittedBy.corrective && (
        <div className="cmp-card cmp-amber">
          <h4>Corrective action recorded that week</h4>
          <div style={{fontSize:13,marginTop:6}}>{submittedBy.corrective}</div>
        </div>
      )}
    </div>
  );
}

// ---- Probe Calibration ----
// Probes come from the admin-managed "probes" surface (Command Centre →
// Compliance → Probe Calibration) so a new unit logs against ITS probes, never
// the closed kitchen's P1–P3. Resolution + the legacy fallback: complianceUnits.js.
function CmpProbeCalibration({backend, site, requestPin, staffByPin}) {
  const pinPrompt = useMemo(() => makeCmpPinPrompt(requestPin, staffByPin), [requestPin, staffByPin]);
  const [probes, setProbes] = useState(null);            // null = config not loaded yet
  const [probesError, setProbesError] = useState(false);
  const [selectedProbe, setSelectedProbe] = useState("");
  const [method, setMethod] = useState("ice");
  const [reading, setReading] = useState("");
  const [action, setAction] = useState("");
  const [todayRows, setTodayRows] = useState([]);
  const [historyByProbe, setHistoryByProbe] = useState({}); // {probeId: {lastTs, lastReading, daysAgo, init}}
  // Wave 6.3 (2026-04-30): tolerance comes from the admin-managed
  // critical-limits surface so the form's UX matches what the backend
  // actually enforces. Falls back to ±1°C until the fetch resolves.
  const [toleranceC, setToleranceC] = useState(1);
  useEffect(() => {
    cmpFetchComplianceConfig(backend, site.key)
      .then(c => {
        const items = cmpResolveSurfaceItems(c, "critical-limits", site.key);
        const tol = items.find(it => it.id === "probe-tolerance-c");
        if (tol && tol.value !== "" && tol.value != null) {
          const n = Number(tol.value);
          if (!isNaN(n) && n > 0) setToleranceC(n);
        }
        // This site's probes (admin-managed "probes" surface; legacy kitchen fallback).
        const list = resolveProbes(cmpResolveSiteOnlyItems(c, "probes", site.key), site.key);
        setProbes(list); setProbesError(false);
        setSelectedProbe(cur => (cur && list.some(p => p.id === cur)) ? cur : (list[0] ? list[0].id : ""));
      })
      .catch(() => {
        // Config unreachable: never guess a unit list for a live site — the closed
        // kitchen keeps its hardcoded probes, anywhere else shows the retry note.
        const list = resolveProbes([], site.key);
        setProbes(list); setProbesError(list.length === 0);
        setSelectedProbe(list[0] ? list[0].id : "");
      });
  }, [backend, site.key]);
  const hist = useCmpHistoryView(backend, site.key, "ProbeLog", "day");
  const visibleRows = hist.isHistorical
    ? hist.histRows.map(r => ({
        ts: r._ts, probeId: r.ProbeId || "", probeName: r.ProbeName || "",
        method: r.Method || "ice", reading: r.Reading,
        errorC: typeof r.ErrorC === "number" ? r.ErrorC : parseFloat(r.ErrorC),
        outOfTol: r.OutOfTolerance === "Y",
        action: r.Action || "", init: cmpInitials(r.StaffName || ""),
        incidentId: r.CorrectiveActionId || ""
      }))
    : todayRows;
  const target = method === "ice" ? 0 : 100;
  const numeric = parseFloat(reading);
  const errorC = !isNaN(numeric) ? +(numeric - target).toFixed(1) : null;
  const outOfTol = errorC !== null && Math.abs(errorC) > toleranceC;

  // Rehydrate today's session rows + last-calibration-per-probe from
  // the last 30 days of ProbeLog (so the per-probe status card can show
  // "9 days ago" for any probe).
  useEffect(() => {
    cmpFetchFormToday(backend, site.key, "ProbeLog", 30)
      .then(rows => {
        const today = cmpTodayIso();
        const today_rows = rows
          .filter(r => String(r.Date) === today)
          .map(r => ({
            ts: r._ts, probeId: r.ProbeId || "", probeName: r.ProbeName || "",
            method: r.Method || "ice", reading: r.Reading,
            errorC: typeof r.ErrorC === "number" ? r.ErrorC : parseFloat(r.ErrorC),
            outOfTol: r.OutOfTolerance === "Y",
            action: r.Action || "", init: cmpInitials(r.StaffName || ""),
            incidentId: r.CorrectiveActionId || ""
          }));
        setTodayRows(today_rows);
        // Compute last-calibration-per-probe (rows are newest-first)
        const byProbe = {};
        rows.forEach(r => {
          const pId = String(r.ProbeId || "");
          if (!pId || byProbe[pId]) return;
          const ts = r._ts;
          const daysAgo = Math.floor((Date.now() - new Date(ts).getTime()) / (1000*60*60*24));
          byProbe[pId] = {
            lastTs: ts, lastReading: r.Reading, daysAgo: daysAgo,
            init: cmpInitials(r.StaffName || "")
          };
        });
        setHistoryByProbe(byProbe);
      })
      .catch(() => {});
  }, [backend, site.key]);

  const submit = () => {
    if (reading === "" || isNaN(numeric)) { cmpToast("Reading required (numeric)", "error"); return; }
    if (outOfTol && !action.trim()) { cmpToast("Out-of-tolerance reading needs a corrective action note", "error"); return; }
    const probe = (probes || []).find(p => p.id === selectedProbe);
    if (!probe) { cmpToast("No probe selected — set this site's probes up in the Command Centre first", "error"); return; }
    pinPrompt({
      prompt: "Log calibration for " + probe.name,
      desc: "Test in " + (method === "ice" ? "ice water (0°C)" : "boiling water (100°C)") + ", ±1°C tolerance.",
      requiredRoles: ["BOH","Admin"],
      onConfirm: (staff) => {
        cmpComplianceLog(backend, site.key, "probe-calibration", {
          probeId: selectedProbe, probeName: probe.name, method: method, reading: numeric, action: action.trim()
        }, staff.pin).then(d => {
          setTodayRows(r => [{
            ts: new Date().toISOString(), probeId: selectedProbe, probeName: probe.name, method: method,
            reading: numeric, errorC: d && typeof d.errorC === "number" ? d.errorC : errorC,
            outOfTol: !!(d && d.outOfTolerance), action: action.trim(), init: staff.initials,
            incidentId: d && d.incidentId ? d.incidentId : ""
          }, ...r]);
          setReading(""); setAction("");
          cmpToast(outOfTol ? "Out-of-tolerance · corrective action raised" : "Calibration logged", outOfTol ? "warn" : "success");
        }).catch(e => cmpToast("Save failed: " + e.message, "error"));
      }
    });
  };

  return (
    <div className="cmp-wrap">
      <CmpHistoryBanner date={hist.viewDate} onClear={() => hist.setViewDate(null)} />
      <CmpHistoricalSignerBanner date={hist.viewDate} formLabel="Probe Calibration — historical view" signedBy={hist.signedBy} />
      <div className="cmp-card">
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,marginBottom:8}}>
          <h3 style={{margin:0}}>Probe Thermometer Calibration</h3>
          <CmpCalendarButton onPick={hist.setViewDate} viewingDate={hist.viewDate} mode="day" />
        </div>
        <div className="cmp-sub">Weekly + any time a probe is dropped. Test in iced water; target 0°C ± 1°C tolerance. Out-of-tolerance forces a corrective action.</div>
      </div>
      {!hist.isHistorical && <div className="cmp-card">
        <h4>Probes · Status</h4>
        {probes === null && <div className="cmp-empty">Loading this site's probe list…</div>}
        {probes !== null && probes.length === 0 && (
          <div className="cmp-empty">{probesError
            ? "Couldn't load the probe list — check the connection and reopen this page."
            : "No probes are set up for " + site.name + " yet. Add them in the Command Centre → Compliance → Probe Calibration and they'll appear here."}</div>
        )}
        <div className="cmp-doc-list" style={{marginTop:10}}>
          {(probes || []).map(p => {
            const h = historyByProbe[p.id];
            const dueSoon = !h || h.daysAgo >= 7;
            return (
              <div key={p.id} className="cmp-doc-row" style={{cursor:"default"}}>
                <div className="cmp-doc-icon">🌡️</div>
                <div className="cmp-doc-body">
                  <div className="cmp-doc-name">{p.name}</div>
                  <div className="cmp-doc-meta">
                    {h
                      ? "Last calibrated " + cmpFriendly(h.lastTs) + " (" + h.daysAgo + "d ago) · " + h.init + " · " + h.lastReading + "°C"
                      : "Never calibrated in the last 30 days"}
                  </div>
                </div>
                <div className="cmp-doc-right">
                  <span className={"cmp-chip cmp-" + (dueSoon ? "warn" : "ok")}><span className="cmp-dot" />{dueSoon ? "Due" : "Current"}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>}
      {!hist.isHistorical && <div className="cmp-card">
        <h4>Log a Calibration</h4>
        <div className="cmp-sub" style={{fontSize:12}}>FSMS §4.3 CCP-6: ice water is the primary check (0°C ±1°C). Boiling water (100°C ±1°C) is an additional verification.</div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))",gap:12,marginTop:12}}>
          <div className="cmp-field">
            <label>Probe</label>
            <select value={selectedProbe} disabled={!probes || probes.length === 0} onChange={e => setSelectedProbe(e.target.value)}>
              {(probes || []).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div className="cmp-field">
            <label>Method</label>
            <select value={method} onChange={e => setMethod(e.target.value)}>
              <option value="ice">Ice water (0°C ±1°C)</option>
              <option value="boiling">Boiling water (100°C ±1°C)</option>
            </select>
          </div>
          <div className={"cmp-field " + (outOfTol ? "cmp-warn" : "")}>
            <label>Reading °C</label>
            <input type="text" inputMode="decimal" value={reading} onChange={e => setReading(e.target.value)} placeholder={method === "ice" ? "e.g. 0.3" : "e.g. 99.6"} />
            {errorC !== null && (
              <span className="cmp-hint" style={{color: outOfTol ? "var(--cm-coral)" : "var(--cm-green)", fontWeight:700}}>
                Error: {errorC > 0 ? "+" : ""}{errorC}°C{outOfTol ? " · out of tolerance" : " · within tolerance"}
              </span>
            )}
          </div>
        </div>
        {outOfTol && (
          <div className="cmp-field cmp-warn" style={{marginTop:12}}>
            <label>Corrective action (required)</label>
            <textarea value={action} onChange={e => setAction(e.target.value)} placeholder="Probe taken out of service / re-calibrated / using backup probe..." />
            <span className="cmp-hint" style={{color:"var(--cm-coral)",fontWeight:700}}>⚠ Out-of-tolerance reading raises a Corrective Action automatically</span>
          </div>
        )}
        <div style={{marginTop:12}}>
          <button className="cmp-btn cmp-btn-primary" disabled={!probes || probes.length === 0} onClick={submit}>Log Reading (PIN required)</button>
        </div>
      </div>}
      {visibleRows.length > 0 && (
        <div className="cmp-card">
          <h3>{hist.isHistorical ? "Calibrations on " + cmpFriendly(hist.viewDate) : "This Session"}</h3>
          <table className="cmp-row-table">
            <thead><tr><th>Time</th><th>Probe</th><th>Method</th><th>Reading</th><th>Error</th><th>Action</th><th>By</th></tr></thead>
            <tbody>
              {visibleRows.map((r, i) => (
                <tr key={i} style={r.outOfTol ? {background:"var(--cm-coral-soft)"} : null}>
                  <td style={{fontWeight:700}}>{new Date(r.ts).toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"})}</td>
                  <td style={{fontSize:12}}>{r.probeName}</td>
                  <td style={{fontSize:12}}>{r.method === "boiling" ? "🔥 Boiling" : "🧊 Ice"}</td>
                  <td style={{fontVariantNumeric:"tabular-nums",fontWeight:800}}>{r.reading}</td>
                  <td><span className={"cmp-badge " + (r.outOfTol ? "cmp-bad" : "cmp-ok")}>{r.errorC > 0 ? "+" : ""}{r.errorC}</span></td>
                  <td style={{fontSize:12}}>{r.action || <span style={{color:"var(--cm-nori-60)"}}>—</span>}</td>
                  <td><span style={{fontFamily:"gelica,serif",background:"var(--cm-wasabi)",padding:"2px 6px",borderRadius:4,fontWeight:800,fontSize:11}}>{r.init}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {hist.isHistorical && !hist.loading && visibleRows.length === 0 && (
        <div className="cmp-empty">No calibrations recorded on {cmpFriendly(hist.viewDate)}.</div>
      )}
    </div>
  );
}

// ---- Fish Freezing (CCP-4) ----
function CmpFishFreezing({backend, site, requestPin, staffByPin}) {
  const pinPrompt = useMemo(() => makeCmpPinPrompt(requestPin, staffByPin), [requestPin, staffByPin]);
  const [batches, setBatches] = useState([]);
  const [intakeOpen, setIntakeOpen] = useState(false);
  const [outFor, setOutFor] = useState(null);
  const hist = useCmpHistoryView(backend, site.key, "FishFreezingLog", "day");
  const visibleBatches = hist.isHistorical ? (() => {
    const byBatch = {};
    hist.histRows.slice().reverse().forEach(r => {
      const id = String(r.BatchId || "");
      if (!id) return;
      if (String(r.Event) === "intake") {
        byBatch[id] = {
          batchId: id, species: r.Species || "", supplier: r.Supplier || "",
          batchRef: r.BatchRef || "", freezer: r.Freezer || "",
          timeIn: r.TimeIn || r._ts, timeOut: "", coreTemp: "", pass: null,
          init: cmpInitials(r.StaffName || ""), incidentId: ""
        };
      } else if (String(r.Event) === "out" && byBatch[id]) {
        byBatch[id].timeOut = r.TimeOut || r._ts;
        byBatch[id].coreTemp = typeof r.CoreTemp === "number" ? r.CoreTemp : parseFloat(r.CoreTemp);
        byBatch[id].pass = String(r.Pass) === "Y";
        byBatch[id].init = cmpInitials(r.StaffName || "");
        byBatch[id].incidentId = r.CorrectiveActionId || "";
      }
    });
    return Object.values(byBatch).sort((a, b) => (b.timeIn || "").localeCompare(a.timeIn || ""));
  })() : batches;

  // Rehydrate batches from the last 7 days. Group intake/out by BatchId.
  useEffect(() => {
    cmpFetchFormToday(backend, site.key, "FishFreezingLog", 7)
      .then(rows => {
        const byBatch = {};
        // Process oldest-first so intake lands before out
        rows.slice().reverse().forEach(r => {
          const id = String(r.BatchId || "");
          if (!id) return;
          if (String(r.Event) === "intake") {
            byBatch[id] = {
              batchId: id, species: r.Species || "", supplier: r.Supplier || "",
              batchRef: r.BatchRef || "", freezer: r.Freezer || "",
              timeIn: r.TimeIn || r._ts, timeOut: "", coreTemp: "", pass: null,
              init: cmpInitials(r.StaffName || ""), incidentId: ""
            };
          } else if (String(r.Event) === "out" && byBatch[id]) {
            byBatch[id].timeOut = r.TimeOut || r._ts;
            byBatch[id].coreTemp = typeof r.CoreTemp === "number" ? r.CoreTemp : parseFloat(r.CoreTemp);
            byBatch[id].pass = String(r.Pass) === "Y";
            byBatch[id].init = cmpInitials(r.StaffName || "");
            byBatch[id].incidentId = r.CorrectiveActionId || "";
          }
        });
        // Newest intake first in display
        const arr = Object.values(byBatch).sort((a, b) => (b.timeIn || "").localeCompare(a.timeIn || ""));
        setBatches(arr);
      })
      .catch(() => {});
  }, [backend, site.key]);

  const submitIntake = (vals) => {
    setIntakeOpen(false);
    pinPrompt({
      prompt: "Sign off fish intake (CCP-4)",
      desc: "Confirm with your PIN to start this batch.",
      requiredRoles: ["BOH","Admin"],
      onConfirm: (staff) => {
        cmpComplianceLog(backend, site.key, "fish-freezing-intake", {
          species: vals.species, supplier: vals.supplier, batchRef: vals.batchRef, freezer: vals.freezer
        }, staff.pin).then(d => {
          setBatches(arr => [{
            batchId: d && d.batchId ? d.batchId : "(pending)",
            species: vals.species, supplier: vals.supplier, batchRef: vals.batchRef, freezer: vals.freezer,
            timeIn: d && d.timeIn ? d.timeIn : new Date().toISOString(),
            timeOut: "", coreTemp: "", pass: null, init: staff.initials, incidentId: ""
          }, ...arr]);
          cmpToast("Intake logged · " + (d && d.batchId ? d.batchId : ""), "success");
        }).catch(e => cmpToast("Intake failed: " + e.message, "error"));
      },
      onCancel: () => setIntakeOpen(true)
    });
  };

  const submitOut = (vals) => {
    const batchId = outFor;
    // Validate BEFORE closing the modal — losing the user's input on a
    // validation toast is hostile, especially the multi-field FAIL case.
    const coreTemp = parseFloat(vals.coreTemp);
    if (isNaN(coreTemp)) { cmpToast("Core temp must be numeric", "error"); return; }
    const pass = coreTemp <= -20;
    if (!pass && !String(vals.actionNotes || "").trim()) {
      cmpToast("Out-of-spec core temp needs a corrective action note", "error"); return;
    }
    setOutFor(null);
    pinPrompt({
      prompt: "Sign off freezing complete",
      desc: "Confirm with your PIN to close out " + batchId + ".",
      requiredRoles: ["BOH","Admin"],
      onConfirm: (staff) => {
        cmpComplianceLog(backend, site.key, "fish-freezing-out", {
          batchId: batchId, coreTemp: coreTemp, actionNotes: String(vals.actionNotes || "").trim()
        }, staff.pin).then(d => {
          setBatches(arr => arr.map(b => b.batchId === batchId ? Object.assign({}, b, {
            timeOut: new Date().toISOString(), coreTemp: coreTemp, pass: !!(d && d.pass),
            init: staff.initials, incidentId: d && d.correctiveActionId ? d.correctiveActionId : ""
          }) : b));
          cmpToast(pass ? "Freezing complete · " + coreTemp + "°C · PASS" : "⚠ Core " + coreTemp + "°C · corrective action raised", pass ? "success" : "warn");
        }).catch(e => cmpToast("Out failed: " + e.message, "error"));
      },
      onCancel: () => setOutFor(batchId)
    });
  };

  const open = visibleBatches.filter(b => !b.timeOut);
  const closed = visibleBatches.filter(b => !!b.timeOut);

  return (
    <div className="cmp-wrap">
      <CmpHistoryBanner date={hist.viewDate} onClear={() => hist.setViewDate(null)} />
      <CmpHistoricalSignerBanner date={hist.viewDate} formLabel="Fish Freezing — historical view" signedBy={hist.signedBy} />
      <div className="cmp-card">
        <div style={{display:"flex",justifyContent:"space-between",flexWrap:"wrap",gap:12,alignItems:"center"}}>
          <div>
            <h3>Fish Freezing Log (CCP-4)</h3>
            <div className="cmp-sub">−20°C ≥ 24h before raw use. Time-out reading &gt; −20°C raises a corrective action.</div>
          </div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            <CmpCalendarButton onPick={hist.setViewDate} viewingDate={hist.viewDate} mode="day" />
            <button className="cmp-btn cmp-btn-ghost cmp-btn-sm" onClick={() => cmpOpenSop({id:"SOP-30",title:"SOP 30 — Freezing of Fish (parasite control)",version:"v1",updated:"2026-03-05"})}>📄 View Fish Freezing Policy</button>
            {!hist.isHistorical && <button className="cmp-btn cmp-btn-primary" onClick={() => setIntakeOpen(true)}>+ Log Intake (PIN required)</button>}
          </div>
        </div>
      </div>
      {open.length > 0 && (
        <div className="cmp-card">
          <h3>Open Batches</h3>
          <table className="cmp-row-table">
            <thead><tr><th>Batch</th><th>Species</th><th>Supplier · ref</th><th>Freezer</th><th>Time in</th><th>Action</th></tr></thead>
            <tbody>
              {open.map(b => (
                <tr key={b.batchId}>
                  <td style={{fontWeight:700,fontFamily:"monospace",fontSize:12}}>{b.batchId}</td>
                  <td>{b.species}</td>
                  <td style={{fontSize:12}}>{b.supplier} · {b.batchRef}</td>
                  <td>{b.freezer}</td>
                  <td style={{fontSize:12}}>{cmpFriendly(b.timeIn)} {new Date(b.timeIn).toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"})}</td>
                  <td>{!hist.isHistorical ? <button className="cmp-btn cmp-btn-primary cmp-btn-sm" onClick={() => setOutFor(b.batchId)}>Close out · core °C</button> : <span style={{fontSize:11,color:"var(--cm-nori-60)"}}>open at end of day</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {closed.length > 0 && (
        <div className="cmp-card">
          <h3>{hist.isHistorical ? "Closed on " + cmpFriendly(hist.viewDate) : "Closed (this session)"}</h3>
          <table className="cmp-row-table">
            <thead><tr><th>Batch</th><th>Species</th><th>Core °C</th><th>Result</th><th>By</th></tr></thead>
            <tbody>
              {closed.map(b => (
                <tr key={b.batchId} style={!b.pass ? {background:"var(--cm-coral-soft)"} : null}>
                  <td style={{fontWeight:700,fontFamily:"monospace",fontSize:12}}>{b.batchId}</td>
                  <td>{b.species}</td>
                  <td style={{fontVariantNumeric:"tabular-nums",fontWeight:800}}>{b.coreTemp}</td>
                  <td><span className={"cmp-badge " + (b.pass ? "cmp-ok" : "cmp-bad")}>{b.pass ? "PASS" : "FAIL"}</span></td>
                  <td><span style={{fontFamily:"gelica,serif",background:"var(--cm-wasabi)",padding:"2px 6px",borderRadius:4,fontWeight:800,fontSize:11}}>{b.init}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {open.length === 0 && closed.length === 0 && !hist.loading && (
        <div className="cmp-empty">{hist.isHistorical ? "No fish-freezing batches recorded on " + cmpFriendly(hist.viewDate) + "." : "No batches in this session. Log an intake to start."}</div>
      )}
      {!hist.isHistorical && intakeOpen && (
        <CmpFormModal
          title="Log fish intake"
          desc="Capture species, supplier, batch ref, and which freezer. Time-in stamps automatically when you sign with your PIN."
          fields={[
            {key: "species", label: "Species", type: "text", placeholder: "Atlantic salmon / Tuna", required: true},
            {key: "supplier", label: "Supplier", type: "text", placeholder: "e.g. Sailbrand", required: true},
            {key: "batchRef", label: "Batch / invoice ref", type: "text", placeholder: "e.g. PBS-02312 · 4kg fillet", required: true},
            {key: "freezer", label: "Freezer", type: "text", placeholder: "e.g. Fish freezer", required: true}
          ]}
          submitLabel="Continue → PIN"
          onSubmit={submitIntake}
          onCancel={() => setIntakeOpen(false)}
        />
      )}
      {!hist.isHistorical && outFor && (
        <CmpFormModal
          title={"Close out " + outFor}
          desc="Capture the core temperature reading. Anything warmer than −20°C will raise a corrective action."
          fields={[
            {key: "coreTemp", label: "Core temperature (°C)", type: "text", placeholder: "e.g. -20.4", required: true, hint: "Must be ≤ −20°C to pass."},
            {key: "actionNotes", label: "Corrective action (required if > −20°C)", type: "textarea", placeholder: "What did you do with the batch?"}
          ]}
          submitLabel="Continue → PIN"
          onSubmit={submitOut}
          onCancel={() => setOutFor(null)}
        />
      )}
    </div>
  );
}

// ---- Emergency Fridge ----
function CmpEmergencyFridge({backend, site, requestPin, staffByPin}) {
  const pinPrompt = useMemo(() => makeCmpPinPrompt(requestPin, staffByPin), [requestPin, staffByPin]);
  const [incidents, setIncidents] = useState([]);
  const [raiseOpen, setRaiseOpen] = useState(false);
  const [checkFor, setCheckFor] = useState(null);
  const [disposalFor, setDisposalFor] = useState(null);
  const [closeFor, setCloseFor] = useState(null);
  const hist = useCmpHistoryView(backend, site.key, "EmergencyLog", "day");
  const visibleIncidents = hist.isHistorical ? (() => {
    const byId = {};
    hist.histRows.slice().reverse().forEach(r => {
      const id = String(r.IncidentId || "");
      if (!id) return;
      if (String(r.Event) === "raise") {
        byId[id] = {
          incidentId: id, unit: r.Unit || "", failureTime: r.FailureTime || "",
          initialTemp: r.InitialTemp, status: r.Status || "open",
          rows: [{event:"raise", ts: r._ts, temp: r.InitialTemp, notes: r.ActionNotes || "", init: cmpInitials(r.StaffName || "")}],
          correctiveActionId: r.CorrectiveActionId || "",
          disposalSigned: false
        };
      } else if (byId[id]) {
        byId[id].rows.push({
          event: r.Event, ts: r._ts,
          temp: r.CheckTemp || "", notes: r.ActionNotes || "",
          init: cmpInitials(r.StaffName || "")
        });
        if (String(r.Event) === "disposal") byId[id].disposalSigned = true;
        if (String(r.Event) === "close") byId[id].status = "resolved";
        if (String(r.Status) === "resolved") byId[id].status = "resolved";
      }
    });
    return Object.values(byId).sort((a,b) => (b.rows[0] && b.rows[0].ts || "").localeCompare(a.rows[0] && a.rows[0].ts || ""));
  })() : incidents;

  // Rehydrate from the last 14 days. Group all events by IncidentId.
  useEffect(() => {
    cmpFetchFormToday(backend, site.key, "EmergencyLog", 14)
      .then(rows => {
        const byId = {};
        // Oldest-first so the timeline appends in order
        rows.slice().reverse().forEach(r => {
          const id = String(r.IncidentId || "");
          if (!id) return;
          if (String(r.Event) === "raise") {
            byId[id] = {
              incidentId: id, unit: r.Unit || "", failureTime: r.FailureTime || "",
              initialTemp: r.InitialTemp, status: r.Status || "open",
              rows: [{event:"raise", ts: r._ts, temp: r.InitialTemp, notes: r.ActionNotes || "", init: cmpInitials(r.StaffName || "")}],
              correctiveActionId: r.CorrectiveActionId || "",
              disposalSigned: false
            };
          } else if (byId[id]) {
            byId[id].rows.push({
              event: r.Event, ts: r._ts,
              temp: r.CheckTemp || "", notes: r.ActionNotes || "",
              init: cmpInitials(r.StaffName || "")
            });
            if (String(r.Event) === "disposal") byId[id].disposalSigned = true;
            if (String(r.Event) === "close") byId[id].status = "resolved";
            // Status column on later rows can also flip the incident
            if (String(r.Status) === "resolved") byId[id].status = "resolved";
          }
        });
        const arr = Object.values(byId).sort((a, b) =>
          (b.rows[0] && b.rows[0].ts || "").localeCompare(a.rows[0] && a.rows[0].ts || "")
        );
        setIncidents(arr);
      })
      .catch(() => {});
  }, [backend, site.key]);

  const submitRaise = (vals) => {
    setRaiseOpen(false);
    pinPrompt({
      prompt: "Raise emergency fridge incident",
      desc: "Your PIN starts the incident. 2-hourly monitoring required per FSMS §6.4.",
      onConfirm: (staff) => {
        cmpComplianceLog(backend, site.key, "emergency-fridge-raise", {
          unit: vals.unit, failureTime: vals.failureTime,
          initialTemp: vals.initialTemp, actionNotes: vals.actionNotes
        }, staff.pin).then(d => {
          setIncidents(arr => [{
            incidentId: d && d.incidentId ? d.incidentId : "(pending)",
            unit: vals.unit, failureTime: vals.failureTime, initialTemp: vals.initialTemp,
            status: "open",
            rows: [{event:"raise", ts: new Date().toISOString(), temp: vals.initialTemp, notes: vals.actionNotes, init: staff.initials}],
            correctiveActionId: d && d.correctiveActionId ? d.correctiveActionId : ""
          }, ...arr]);
          cmpToast("Incident raised · " + (d && d.incidentId ? d.incidentId : "") + " · admin notified", "warn");
        }).catch(e => cmpToast("Raise failed: " + e.message, "error"));
      },
      onCancel: () => setRaiseOpen(true)
    });
  };

  const submitCheck = (vals) => {
    const id = checkFor;
    setCheckFor(null);
    pinPrompt({
      prompt: "Sign off temp check",
      desc: "Reading " + (vals.checkTemp || "—") + "°C on " + id,
      requiredRoles: ["BOH","Admin"],
      onConfirm: (staff) => {
        cmpComplianceLog(backend, site.key, "emergency-fridge-check", {
          incidentId: id, checkTemp: vals.checkTemp, actionNotes: vals.actionNotes
        }, staff.pin).then(() => {
          setIncidents(arr => arr.map(x => x.incidentId === id ? Object.assign({}, x, {
            rows: [...x.rows, {event:"check", ts: new Date().toISOString(), temp: vals.checkTemp, notes: vals.actionNotes, init: staff.initials}]
          }) : x));
          cmpToast("Check logged on " + id, "success");
        }).catch(e => cmpToast("Check failed: " + e.message, "error"));
      },
      onCancel: () => setCheckFor(id)
    });
  };

  const submitDisposal = (vals) => {
    const id = disposalFor;
    setDisposalFor(null);
    pinPrompt({
      prompt: "Authorise stock disposal",
      desc: "4-hour rule disposal on " + id + ". Admin PIN only.",
      requiredRoles: ["Admin"],
      onConfirm: (staff) => {
        cmpComplianceLog(backend, site.key, "emergency-fridge-disposal", {
          incidentId: id, actionNotes: vals.actionNotes
        }, staff.pin).then(() => {
          setIncidents(arr => arr.map(x => x.incidentId === id ? Object.assign({}, x, {
            rows: [...x.rows, {event:"disposal", ts: new Date().toISOString(), temp: "", notes: vals.actionNotes, init: staff.initials}],
            disposalSigned: true
          }) : x));
          cmpToast("Stock disposal authorised by " + staff.name, "warn");
        }).catch(e => cmpToast("Disposal failed: " + e.message, "error"));
      },
      onCancel: () => setDisposalFor(id)
    });
  };

  const submitClose = (vals) => {
    const id = closeFor;
    setCloseFor(null);
    pinPrompt({
      prompt: "Close incident",
      desc: "Mark " + id + " resolved — fridge back in service.",
      onConfirm: (staff) => {
        cmpComplianceLog(backend, site.key, "emergency-fridge-close", {
          incidentId: id, actionNotes: vals.actionNotes
        }, staff.pin).then(() => {
          setIncidents(arr => arr.map(x => x.incidentId === id ? Object.assign({}, x, {
            status: "resolved",
            rows: [...x.rows, {event:"close", ts: new Date().toISOString(), temp: "", notes: vals.actionNotes, init: staff.initials}]
          }) : x));
          cmpToast("Incident " + id + " closed", "success");
        }).catch(e => cmpToast("Close failed: " + e.message, "error"));
      },
      onCancel: () => setCloseFor(id)
    });
  };

  const open = visibleIncidents.filter(i => i.status === "open");
  const closed = visibleIncidents.filter(i => i.status !== "open");

  return (
    <div className="cmp-wrap">
      <CmpHistoryBanner date={hist.viewDate} onClear={() => hist.setViewDate(null)} />
      <CmpHistoricalSignerBanner date={hist.viewDate} formLabel="Emergency Fridge — historical view" signedBy={hist.signedBy} />
      <div className="cmp-card cmp-coral">
        <div style={{display:"flex",justifyContent:"space-between",flexWrap:"wrap",gap:12,alignItems:"center"}}>
          <div>
            <h3>Emergency Fridge</h3>
            <div className="cmp-sub">Incident-driven workflow. 2-hourly checks per FSMS §6.4. Over 4h = 4-hour-rule disposal (admin sign-off).</div>
          </div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            <CmpCalendarButton onPick={hist.setViewDate} viewingDate={hist.viewDate} mode="day" />
            {!hist.isHistorical && <button className="cmp-btn cmp-btn-danger" onClick={() => setRaiseOpen(true)}>+ Raise incident</button>}
          </div>
        </div>
        <div className="cmp-card" style={{background:"var(--cm-sun-soft)",border:"1px solid var(--cm-amber)",margin:"12px 0 0",padding:"10px 14px"}}>
          <div style={{fontSize:12,lineHeight:1.7,color:"var(--cm-nori-70)"}}>
            <strong>&lt; 2h above +8°C:</strong> move stock to a verified-working secondary fridge after checking its temp.<br/>
            <strong>2–4h above +8°C:</strong> high-risk stock may be sold but <strong>must not be moved or held to next day</strong>. Label all affected stock with the exact time temp rose &gt;+8°C.<br/>
            <strong>&gt; 4h above +8°C:</strong> all remaining stock <strong>discarded</strong>. Not returned to a working fridge or sold. Recorded in Waste / Corrective Action Log.<br/>
            If the unit cannot maintain ≤+5°C, clear it, mark "Do Not Use", out of service until repaired.
          </div>
        </div>
      </div>
      {open.length === 0 && closed.length === 0 && !hist.loading && (
        <div className="cmp-empty">{hist.isHistorical ? "No emergency-fridge incidents recorded on " + cmpFriendly(hist.viewDate) + "." : "No incidents in this session. Tap Raise above to start."}</div>
      )}
      {open.map(i => (
        <div key={i.incidentId} className="cmp-card cmp-coral">
          <div style={{display:"flex",justifyContent:"space-between",flexWrap:"wrap",gap:12,alignItems:"flex-start"}}>
            <div style={{flex:1,minWidth:0}}>
              <h4 style={{fontSize:16,marginBottom:4}}>{i.incidentId} · {i.unit}</h4>
              <div className="cmp-sub" style={{marginBottom:6}}>Initial temp <strong>{i.initialTemp}°C</strong> · {i.rows.length} event(s){i.correctiveActionId ? " · CA " + i.correctiveActionId : ""}</div>
            </div>
            <span className="cmp-chip cmp-bad"><span className="cmp-dot" />open</span>
          </div>
          {i.rows.map((r, ri) => (
            <div key={ri} style={{padding:"8px 0",borderTop: ri > 0 ? "1px dashed var(--cm-nori-08)" : "none"}}>
              <div style={{display:"flex",justifyContent:"space-between",gap:12,flexWrap:"wrap"}}>
                <div style={{fontWeight:700,fontSize:13}}>{r.event === "raise" ? "Discovery" : r.event === "check" ? "Temp check" : r.event === "disposal" ? "Stock disposal" : "Close"}</div>
                <div style={{fontSize:11,color:"var(--cm-nori-60)"}}>{cmpFriendly(r.ts)} {new Date(r.ts).toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"})}</div>
              </div>
              <div style={{fontSize:12,marginTop:4}}>{r.temp ? <strong>{r.temp}°C · </strong> : null}{r.notes}</div>
              <div style={{fontSize:11,marginTop:4}}>by <span style={{fontFamily:"gelica,serif",background:"var(--cm-wasabi)",padding:"1px 5px",borderRadius:3,fontWeight:800}}>{r.init}</span></div>
            </div>
          ))}
          {!hist.isHistorical && (
            <div style={{display:"flex",gap:8,flexWrap:"wrap",marginTop:10}}>
              <button className="cmp-btn cmp-btn-primary cmp-btn-sm" onClick={() => setCheckFor(i.incidentId)}>+ Log check</button>
              <button className="cmp-btn cmp-btn-danger cmp-btn-sm" onClick={() => setDisposalFor(i.incidentId)}>Stock disposal (Admin)</button>
              <button className="cmp-btn cmp-btn-wasabi cmp-btn-sm" onClick={() => setCloseFor(i.incidentId)}>Close incident</button>
            </div>
          )}
        </div>
      ))}
      {closed.length > 0 && (
        <>
          <h3 style={{fontSize:14,margin:"16px 0 8px",color:"var(--cm-nori-60)"}}>Closed (this session)</h3>
          {closed.map(i => (
            <div key={i.incidentId} className="cmp-card cmp-green">
              <div style={{display:"flex",justifyContent:"space-between",gap:12,flexWrap:"wrap"}}>
                <div>
                  <strong>{i.incidentId} · {i.unit}</strong>
                  <div style={{fontSize:12,color:"var(--cm-nori-70)",marginTop:3}}>{i.rows.length} events · resolved</div>
                </div>
                <span className="cmp-chip cmp-ok"><span className="cmp-dot" />resolved</span>
              </div>
            </div>
          ))}
        </>
      )}
      {raiseOpen && (
        <CmpFormModal
          title="Raise emergency fridge incident"
          desc="Capture which unit, when failure was discovered, and the initial temp + initial action taken."
          fields={[
            {key: "unit", label: "Unit affected", type: "text", placeholder: "e.g. Prep fridge", required: true},
            {key: "failureTime", label: "Time failure discovered (HH:MM)", type: "time", required: true, default: new Date().toTimeString().slice(0,5)},
            {key: "initialTemp", label: "Initial temp (°C)", type: "text", placeholder: "e.g. 9.4", required: true},
            {key: "actionNotes", label: "Initial action taken", type: "textarea", placeholder: "Stock moved to the backup fridge · seal/airflow checked · compressor not running...", required: true}
          ]}
          submitLabel="Continue → PIN"
          onSubmit={submitRaise}
          onCancel={() => setRaiseOpen(false)}
        />
      )}
      {checkFor && (
        <CmpFormModal
          title={"Log check on " + checkFor}
          desc="Capture the current temp reading and any action notes."
          fields={[
            {key: "checkTemp", label: "Current temp (°C)", type: "text", placeholder: "e.g. 7.8", required: true},
            {key: "actionNotes", label: "Notes", type: "textarea", placeholder: "What you observed; any stock movement or labelling."}
          ]}
          submitLabel="Continue → PIN"
          onSubmit={submitCheck}
          onCancel={() => setCheckFor(null)}
        />
      )}
      {disposalFor && (
        <CmpFormModal
          title={"Stock disposal · " + disposalFor}
          desc="Admin PIN required. Capture what was discarded and the reason (4-hour rule)."
          fields={[
            {key: "actionNotes", label: "Disposal notes (required)", type: "textarea", placeholder: "Full freezer contents discarded (~4kg mixed fish). Waste log updated.", required: true}
          ]}
          submitLabel="Continue → Admin PIN"
          onSubmit={submitDisposal}
          onCancel={() => setDisposalFor(null)}
        />
      )}
      {closeFor && (
        <CmpFormModal
          title={"Close incident " + closeFor}
          desc="Confirm the unit is back in service. Notes capture the resolution."
          fields={[
            {key: "actionNotes", label: "Resolution notes", type: "textarea", placeholder: "Compressor repaired by contractor · running stable at 4°C · stock returned."}
          ]}
          submitLabel="Continue → PIN"
          onSubmit={submitClose}
          onCancel={() => setCloseFor(null)}
        />
      )}
    </div>
  );
}

// ---- HACCP Daily ----
const CMP_HACCP_OPENING = [
  "Hot/cold water available",
  "Fridges & display ≤ 5°C / freezers ≤ −18°C — readings recorded",
  "Probe + pH calibrated/working",
  "Personal hygiene OK (uniform, hair, jewellery, illness)",
  "Hand wash basin stocked (soap, paper, hot water)",
  "Sanitiser at correct dilution + contact time",
  "Pest evidence check around prep area",
  "Allergen kit available (separate utensils, signage)"
];
const CMP_HACCP_CLOSING = [
  "All food date-labelled & rotated (FIFO)",
  "All surfaces sanitised, contact time observed",
  "Bins emptied, lids closed, area tidy",
  "Fridges & freezers final reading recorded",
  "Hand wash basin restocked",
  "All probes returned to charger / rack"
];
// Storage units come from the admin-managed "fridges" surface (Command Centre →
// Compliance → Emergency fridge procedure → storage units) so a site's HACCP day
// logs against ITS units with their own keys, never the closed kitchen's F1–F5.
// Resolution + the legacy fallback: complianceUnits.js.
function CmpHaccpDaily({backend, site, requestPin, staffByPin}) {
  const pinPrompt = useMemo(() => makeCmpPinPrompt(requestPin, staffByPin), [requestPin, staffByPin]);
  const [unitList, setUnitList] = useState(null);        // null = config not loaded yet
  const [unitsError, setUnitsError] = useState(false);
  useEffect(() => {
    let alive = true;
    cmpFetchComplianceConfig(backend, site.key)
      .then(c => { if (!alive) return; setUnitList(resolveFridges(cmpResolveSiteOnlyItems(c, "fridges", site.key), site.key)); setUnitsError(false); })
      .catch(() => { if (!alive) return; const list = resolveFridges([], site.key); setUnitList(list); setUnitsError(list.length === 0); });
    return () => { alive = false; };
  }, [backend, site.key]);
  const [opening, setOpening] = useState({});
  const [closing, setClosing] = useState({});
  const [fridges, setFridges] = useState({});
  const [deliveries, setDeliveries] = useState([]);
  const [cooks, setCooks] = useState([]);
  const [phBatches, setPhBatches] = useState([]);
  const [fridgeForm, setFridgeForm] = useState(null); // {fridgeId, slot, fridgeLabel}
  const [deliveryForm, setDeliveryForm] = useState(false);
  const [cookForm, setCookForm] = useState(false);
  const [phForm, setPhForm] = useState(false);
  const [chefSig, setChefSig] = useState(null);
  // viewDate drives the entire HACCP rehydrate + write pipeline. Today
  // = editable; any past date = read-only historical view (calendar
  // picker). HACCP has its own reader (cmpFetchHaccpDay) — distinct
  // from useCmpHistoryView since it returns a structured sheet object,
  // not a flat row array.
  const [viewDate, setViewDateRaw] = useState(() => cmpTodayIso());
  const isHistorical = viewDate !== cmpTodayIso();
  const setViewDate = (d) => setViewDateRaw(d || cmpTodayIso());
  const today = viewDate;

  // Rehydrate the day's HACCP state from the audit trail. State Reconstruction
  // memory: a "submitted/locked" UI must hydrate every form field from the
  // server response, not just paint a banner. Refresh = paint exactly what
  // the sheet has. Historical view picks a past date via the calendar.
  useEffect(() => {
    // Reset state when switching dates so a stale today's view never
    // bleeds into a historical view (or vice versa). Also close any
    // in-flight modal so a half-typed delivery/cook/pH/fridge form
    // can't get submitted against the wrong date.
    setOpening({}); setClosing({}); setFridges({});
    setDeliveries([]); setCooks([]); setPhBatches([]); setChefSig(null);
    setFridgeForm(null); setDeliveryForm(false); setCookForm(false); setPhForm(false);
    cmpFetchHaccpDay(backend, site.key, today)
      .then(d => {
        if (!d || !d.sheet) return;
        const sh = d.sheet;
        // Opening / closing checks
        const openObj = {};
        Object.keys(sh.openingChecks || {}).forEach(idx => {
          const stamp = sh.openingChecks[idx];
          if (stamp) openObj[idx] = {init: cmpInitials(stamp.by || ""), ts: stamp.at || ""};
        });
        setOpening(openObj);
        const closeObj = {};
        Object.keys(sh.closingChecks || {}).forEach(idx => {
          const stamp = sh.closingChecks[idx];
          if (stamp) closeObj[idx] = {init: cmpInitials(stamp.by || ""), ts: stamp.at || ""};
        });
        setClosing(closeObj);
        // Per-fridge AM/PM
        const perFridge = sh.storageTempsPerFridge || {};
        const fridgeObj = {};
        Object.keys(perFridge).forEach(fId => {
          fridgeObj[fId] = {};
          ["am","pm"].forEach(slot => {
            const v = perFridge[fId][slot];
            if (v) fridgeObj[fId][slot] = {value: v.temp, init: cmpInitials((v.initials && v.initials.by) || "")};
          });
        });
        setFridges(fridgeObj);
        // CCP-1 deliveries
        setDeliveries((sh.deliveries || []).map(dItem => ({
          time: dItem.time || "",
          supplier: dItem.supplier || "",
          invoice: dItem.invoice || "",
          item: dItem.item || "",
          temp: dItem.temp || "",
          pkg: dItem.pkg || "S",
          veh: dItem.veh || "S",
          init: cmpInitials((dItem.initials && dItem.initials.by) || "")
        })));
        // CCP-5 cooking
        setCooks((sh.cookingEvents || []).map(c => ({
          item: c.item || "",
          first: c.first,
          last: c.last,
          init: cmpInitials((c.initials && c.initials.by) || "")
        })));
        // CCP-2 pH batches — backend stores under ricePh keyed by field
        const phRows = [];
        Object.keys(sh.ricePh || {}).forEach(k => {
          const entry = sh.ricePh[k];
          if (!entry || typeof entry === "string") return;
          phRows.push({
            batchNo: entry.batchNo || phRows.length + 1,
            ph: entry.ph,
            action: entry.action || "",
            init: cmpInitials((entry.initials && entry.initials.by) || ""),
            ts: (entry.initials && entry.initials.at) || ""
          });
        });
        phRows.sort((a, b) => (a.batchNo || 0) - (b.batchNo || 0));
        setPhBatches(phRows);
        // Chef sign-off
        if (d.signOff && d.signOff.at) {
          setChefSig({init: cmpInitials(d.signOff.name || ""), name: d.signOff.name || "", ts: d.signOff.at});
        }
      })
      .catch(() => {/* silent — form starts blank */});
  }, [backend, site.key, today]);

  // Opening/closing ticks map to handleHaccpEvent's section="opening"|"closing"
  // contract: {section, field, value:true}. Backend keys state.openingChecks /
  // state.closingChecks by `field` (the tick index as a string).
  const tickItem = (kind, idx, label) => {
    if (isHistorical) return;
    pinPrompt({
      prompt: "Initial this check",
      desc: kind + " · " + label,
      onConfirm: (staff) => {
        cmpHaccpEvent(backend, site.key, {
          section: kind, field: String(idx), value: true,
          extra: {checkLabel: label}, date: today
        }, staff.pin).then(() => {
          const setter = kind === "opening" ? setOpening : setClosing;
          setter(s => Object.assign({}, s, {[idx]: {init: staff.initials, ts: new Date().toISOString()}}));
          cmpToast("✓ " + label, "success");
        }).catch(e => cmpToast("Save failed: " + e.message, "error"));
      }
    });
  };

  // Per-fridge AM/PM readings via FormModal (Phase 2 v1.2, 2026-04-29).
  // Backend `storageTemp` section accepts `field:"<fridgeId>_<slot>"` and
  // writes to `state.storageTempsPerFridge[fridgeId][slot]` so 5 fridges
  // no longer overwrite each other. window.prompt replaced with the
  // styled CmpFormModal (tablet-friendly).
  const submitFridge = (vals) => {
    const f = fridgeForm;
    setFridgeForm(null);
    if (isHistorical) return;
    const num = parseFloat(vals.temp);
    if (isNaN(num)) { cmpToast("Reading must be numeric", "error"); return; }
    pinPrompt({
      prompt: "Log fridge reading",
      desc: f.fridgeLabel + " · " + f.slot.toUpperCase() + " · " + num + "°C",
      onConfirm: (staff) => {
        cmpHaccpEvent(backend, site.key, {
          section: "storageTemp",
          field: f.fridgeId + "_" + f.slot,
          value: {temp: num, equipment: f.fridgeId, equipmentName: f.fridgeLabel},
          date: today
        }, staff.pin).then(() => {
          setFridges(s => Object.assign({}, s, {
            [f.fridgeId]: Object.assign({}, s[f.fridgeId] || {}, {[f.slot]: {value: num, init: staff.initials}})
          }));
          cmpToast("Logged " + f.fridgeLabel + " " + f.slot.toUpperCase() + ": " + num + "°C", "success");
        }).catch(e => cmpToast("Save failed: " + e.message, "error"));
      },
      onCancel: () => setFridgeForm(f)
    });
  };
  const logFridge = (fridgeId, slot, fridgeLabel, target) => { if (isHistorical) return; setFridgeForm({fridgeId, slot, fridgeLabel, target}); };

  // CCP-1 Goods Receipt — chilled ≤8°C, frozen ≤−15°C, fish 0 to +3°C.
  const submitDelivery = (vals) => {
    setDeliveryForm(false);
    if (isHistorical) return;
    pinPrompt({
      prompt: "Log delivery",
      desc: vals.supplier + " · " + vals.item + " · " + vals.temp + "°C",
      requiredRoles: ["BOH","Admin"],
      onConfirm: (staff) => {
        cmpHaccpEvent(backend, site.key, {
          section: "delivery",
          field: "add",
          value: {
            time: vals.time, supplier: vals.supplier, invoice: vals.invoice,
            item: vals.item, cf: vals.cf, temp: vals.temp,
            method: vals.method || "Hard probe",
            pkg: vals.pkg || "S", veh: vals.veh || "S"
          },
          date: today
        }, staff.pin).then(() => {
          setDeliveries(arr => [...arr, {
            time: vals.time, supplier: vals.supplier, invoice: vals.invoice,
            item: vals.item, cf: vals.cf, temp: vals.temp,
            pkg: vals.pkg || "S", veh: vals.veh || "S", init: staff.initials
          }]);
          cmpToast("Delivery logged · " + vals.supplier, "success");
        }).catch(e => cmpToast("Save failed: " + e.message, "error"));
      },
      onCancel: () => setDeliveryForm(true)
    });
  };

  // CCP-5 Cook batch — first + last probe ≥75°C.
  const submitCook = (vals) => {
    setCookForm(false);
    if (isHistorical) return;
    const first = parseFloat(vals.first);
    const last = parseFloat(vals.last);
    if (isNaN(first) || isNaN(last)) { cmpToast("Both probes must be numeric", "error"); return; }
    pinPrompt({
      prompt: "Log cook batch",
      desc: vals.item + " · first " + first + "°C / last " + last + "°C",
      requiredRoles: ["BOH","Admin"],
      onConfirm: (staff) => {
        cmpHaccpEvent(backend, site.key, {
          section: "cooking",
          field: "add",
          value: {item: vals.item, first: first, last: last},
          date: today
        }, staff.pin).then(() => {
          setCooks(arr => [...arr, {item: vals.item, first: first, last: last, init: staff.initials}]);
          if (first < 75 || last < 75) cmpToast("⚠ Below 75°C — re-cook + re-probe before service", "warn");
          else cmpToast("Cook batch logged", "success");
        }).catch(e => cmpToast("Save failed: " + e.message, "error"));
      },
      onCancel: () => setCookForm(true)
    });
  };

  // CCP-2 Sushi rice acidification — pH ≤ 4.3.
  const submitPh = (vals) => {
    setPhForm(false);
    if (isHistorical) return;
    const num = parseFloat(vals.ph);
    if (isNaN(num)) { cmpToast("pH must be numeric", "error"); return; }
    const outOfRange = num >= 4.3;
    if (outOfRange && !String(vals.action || "").trim()) {
      cmpToast("pH ≥ 4.3 needs a corrective action note", "error");
      setPhForm(true);
      return;
    }
    pinPrompt({
      prompt: "Log rice batch pH",
      desc: "Batch " + (phBatches.length + 1) + " · pH " + num,
      requiredRoles: ["BOH","Admin"],
      onConfirm: (staff) => {
        const batchNo = phBatches.length + 1;
        cmpHaccpEvent(backend, site.key, {
          section: "ricePh",
          field: "batch_" + batchNo,
          value: {batchNo: batchNo, ph: num, action: vals.action || ""},
          date: today
        }, staff.pin).then(() => {
          setPhBatches(arr => [...arr, {batchNo: batchNo, ph: num, action: vals.action || "", init: staff.initials, ts: new Date().toISOString()}]);
          if (outOfRange) cmpToast("pH " + num + " · corrective action raised", "warn");
          else cmpToast("Batch " + batchNo + " · pH " + num + " ✓", "success");
        }).catch(e => cmpToast("Save failed: " + e.message, "error"));
      },
      onCancel: () => setPhForm(true)
    });
  };

  const signChef = () => {
    if (isHistorical) return;
    pinPrompt({
      prompt: "Chef sign-off (close day)",
      desc: "Locks all HACCP entries for today. BOH or Admin PIN.",
      requiredRoles: ["BOH","Admin"],
      onConfirm: (staff) => {
        cmpHaccpSign(backend, site.key, {date: today}, staff.pin).then(() => {
          setChefSig({init: staff.initials, name: staff.name, ts: new Date().toISOString()});
          cmpToast("HACCP day locked — " + staff.name + " signed off", "success");
        }).catch(e => cmpToast("Sign failed: " + e.message, "error"));
      }
    });
  };

  const renderTicks = (kind, list, state) => (
    <div style={{display:"flex",flexDirection:"column",gap:6}}>
      {list.map((label, i) => {
        const tick = state[i];
        return (
          <div key={i} className="cmp-card" style={{padding:"10px 12px",margin:0,display:"flex",justifyContent:"space-between",alignItems:"center",gap:10}}>
            <div style={{flex:1,fontSize:13}}>{tick ? "✓ " : "○ "}{label}</div>
            {tick ? (
              <span style={{fontSize:11,background:"var(--cm-wasabi)",padding:"2px 6px",borderRadius:4,fontFamily:"gelica,serif",fontWeight:800}}>{tick.init}</span>
            ) : (
              <button className="cmp-btn cmp-btn-primary cmp-btn-sm" disabled={!!chefSig || isHistorical} onClick={() => tickItem(kind, i, label)}>Tick (PIN)</button>
            )}
          </div>
        );
      })}
    </div>
  );

  // Build a "signedBy" object the historical banner understands so it
  // shows the chef who closed the picked day, not the live signer.
  const histSignedBy = isHistorical && chefSig
    ? {name: chefSig.name, ts: chefSig.ts}
    : null;

  return (
    <div className="cmp-wrap">
      <CmpHistoryBanner date={viewDate} onClear={() => setViewDate(null)} backLabel="← Back to today" />
      <CmpHistoricalSignerBanner date={viewDate} formLabel="HACCP — historical view" signedBy={histSignedBy} />
      <div className="cmp-card">
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,marginBottom:8}}>
          <h3 style={{margin:0}}>Daily HACCP</h3>
          <CmpCalendarButton onPick={setViewDate} viewingDate={viewDate} mode="day" />
        </div>
        <div className="cmp-sub">Opening checks · CCP-6 fridge readings (AM + PM) · closing checks · chef sign-off locks the day.</div>
      </div>
      {chefSig && (
        <div className="cmp-card cmp-green">
          <h4 style={{color:"var(--cm-green)",marginBottom:4}}>✓ Day closed</h4>
          <div style={{fontSize:13}}>Signed by <strong>{chefSig.name}</strong> at {new Date(chefSig.ts).toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"})}. Entries locked.</div>
        </div>
      )}
      <div className="cmp-card">
        <h4>Opening checks</h4>
        <div className="cmp-sub" style={{marginBottom:10}}>{Object.keys(opening).length}/{CMP_HACCP_OPENING.length} complete</div>
        {renderTicks("opening", CMP_HACCP_OPENING, opening)}
      </div>
      <div className="cmp-card">
        <h4>CCP-6 · Chilled storage AM + PM</h4>
        <div className="cmp-sub" style={{marginBottom:10}}>Fridge ≤ +5°C, freezer ≤ −18°C, fish freezer ≤ −20°C.</div>
        {unitList === null && <div className="cmp-empty">Loading this site's storage units…</div>}
        {unitList !== null && unitList.length === 0 && (
          <div className="cmp-empty">{unitsError
            ? "Couldn't load the storage-unit list — check the connection and reopen this page."
            : "No fridges or freezers are set up for " + site.name + " yet. Add them in the Command Centre → Compliance → Emergency fridge procedure → storage units, and they'll appear here with AM / PM slots."}</div>
        )}
        {unitList !== null && unitList.length > 0 && (
        <table className="cmp-row-table">
          <thead><tr><th>Equipment</th><th>Target</th><th>AM</th><th>PM</th></tr></thead>
          <tbody>
            {unitList.map(f => {
              const entry = fridges[f.id] || {};
              const renderSlot = (slot) => {
                const v = entry[slot];
                if (v) return <div style={{fontVariantNumeric:"tabular-nums",fontWeight:800}}>{v.value}°C <span style={{fontSize:10,fontFamily:"gelica,serif",fontWeight:800,color:"var(--cm-nori-60)",marginLeft:4}}>{v.init}</span></div>;
                return <button className="cmp-btn cmp-btn-ghost cmp-btn-sm" disabled={!!chefSig || isHistorical} onClick={() => logFridge(f.id, slot, f.name, f.target)}>Log {slot.toUpperCase()}</button>;
              };
              return (
                <tr key={f.id}>
                  <td style={{fontWeight:700}}>{f.name}</td>
                  <td style={{fontSize:12,color:"var(--cm-nori-60)"}}>{f.target}</td>
                  <td>{renderSlot("am")}</td>
                  <td>{renderSlot("pm")}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        )}
      </div>
      <div className="cmp-card">
        <h4>CCP-1 · Goods Receipt</h4>
        <div className="cmp-sub" style={{marginBottom:10}}>Chilled ≤+8°C · Frozen ≤−15°C · Fish 0 to +3°C. Probe one chilled + one frozen item per supplier per delivery.</div>
        {deliveries.length > 0 && (
          <table className="cmp-row-table" style={{marginBottom:10}}>
            <thead><tr><th>Time</th><th>Supplier</th><th>Item</th><th>°C</th><th>Pkg</th><th>Veh</th><th>By</th></tr></thead>
            <tbody>
              {deliveries.map((d, i) => (
                <tr key={i}>
                  <td style={{fontFamily:"monospace",fontSize:11,fontWeight:700}}>{d.time}</td>
                  <td style={{fontWeight:700}}>{d.supplier}</td>
                  <td style={{fontSize:12}}>{d.item}</td>
                  <td style={{fontVariantNumeric:"tabular-nums",fontWeight:800}}>{d.temp}</td>
                  <td><span className={"cmp-badge " + (d.pkg === "S" ? "cmp-ok" : "cmp-bad")}>{d.pkg}</span></td>
                  <td><span className={"cmp-badge " + (d.veh === "S" ? "cmp-ok" : "cmp-bad")}>{d.veh}</span></td>
                  <td><span style={{fontFamily:"gelica,serif",background:"var(--cm-wasabi)",padding:"2px 6px",borderRadius:4,fontWeight:800,fontSize:11}}>{d.init}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <button className="cmp-btn cmp-btn-ghost cmp-btn-sm" disabled={!!chefSig || isHistorical} onClick={() => setDeliveryForm(true)}>+ Add delivery</button>
      </div>

      <div className="cmp-card">
        <h4>CCP-5 · Cooking (poultry / prawns)</h4>
        <div className="cmp-sub" style={{marginBottom:10}}>Core 75°C for 30s. Probe first AND last item every batch.</div>
        {cooks.length > 0 && (
          <table className="cmp-row-table" style={{marginBottom:10}}>
            <thead><tr><th>Item</th><th>First</th><th>Last</th><th>By</th></tr></thead>
            <tbody>
              {cooks.map((c, i) => (
                <tr key={i}>
                  <td style={{fontWeight:700}}>{c.item}</td>
                  <td style={{fontVariantNumeric:"tabular-nums",fontWeight:800}}>{c.first}°C {c.first >= 75 ? <span className="cmp-badge cmp-ok">OK</span> : <span className="cmp-badge cmp-bad">⚠</span>}</td>
                  <td style={{fontVariantNumeric:"tabular-nums",fontWeight:800}}>{c.last}°C {c.last >= 75 ? <span className="cmp-badge cmp-ok">OK</span> : <span className="cmp-badge cmp-bad">⚠</span>}</td>
                  <td><span style={{fontFamily:"gelica,serif",background:"var(--cm-wasabi)",padding:"2px 6px",borderRadius:4,fontWeight:800,fontSize:11}}>{c.init}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <button className="cmp-btn cmp-btn-ghost cmp-btn-sm" disabled={!!chefSig || isHistorical} onClick={() => setCookForm(true)}>+ Add cook batch</button>
      </div>

      <div className="cmp-card">
        <h4>CCP-2 · Sushi rice acidification (pH ≤ 4.3)</h4>
        <div className="cmp-sub" style={{marginBottom:10}}>Every batch logged individually. ≥4.3 auto-raises a corrective action.</div>
        {phBatches.length > 0 && (
          <table className="cmp-row-table" style={{marginBottom:10}}>
            <thead><tr><th>Batch</th><th>pH</th><th>Status</th><th>Logged</th><th>By</th></tr></thead>
            <tbody>
              {phBatches.map((b, i) => (
                <tr key={i} style={b.ph >= 4.3 ? {background:"var(--cm-coral-soft)"} : null}>
                  <td style={{fontWeight:700}}>Batch {b.batchNo}</td>
                  <td style={{fontVariantNumeric:"tabular-nums",fontWeight:800}}>{b.ph}</td>
                  <td><span className={"cmp-badge " + (b.ph >= 4.3 ? "cmp-bad" : "cmp-ok")}>{b.ph >= 4.3 ? "⚠ Over 4.3" : "OK"}</span></td>
                  <td style={{fontSize:11,color:"var(--cm-nori-60)"}}>{new Date(b.ts).toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"})}</td>
                  <td><span style={{fontFamily:"gelica,serif",background:"var(--cm-wasabi)",padding:"2px 6px",borderRadius:4,fontWeight:800,fontSize:11}}>{b.init}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <button className="cmp-btn cmp-btn-ghost cmp-btn-sm" disabled={!!chefSig || isHistorical} onClick={() => setPhForm(true)}>+ Log pH for new batch</button>
      </div>

      <div className="cmp-card">
        <h4>Closing checks</h4>
        <div className="cmp-sub" style={{marginBottom:10}}>{Object.keys(closing).length}/{CMP_HACCP_CLOSING.length} complete</div>
        {renderTicks("closing", CMP_HACCP_CLOSING, closing)}
      </div>
      {!chefSig && !isHistorical && (
        <div className="cmp-card" style={{textAlign:"center"}}>
          <h3 style={{marginBottom:4}}>Chef sign-off</h3>
          <div className="cmp-sub">Locks the HACCP day. BOH or Admin PIN.</div>
          <button className="cmp-btn cmp-btn-primary" onClick={signChef}>Sign off &amp; lock day</button>
        </div>
      )}
      {fridgeForm && (
        <CmpFormModal
          title={"Log " + fridgeForm.fridgeLabel + " " + fridgeForm.slot.toUpperCase()}
          desc={"Target " + (fridgeForm.target || "≤ +5°C") + ". Reading is captured against your PIN on the next step."}
          fields={[{key: "temp", label: "Temperature (°C)", type: "text", placeholder: "e.g. 3.4 or -20.1", required: true}]}
          submitLabel="Continue → PIN"
          onSubmit={submitFridge}
          onCancel={() => setFridgeForm(null)}
        />
      )}
      {deliveryForm && (
        <CmpFormModal
          title="Log delivery"
          desc="Capture the supplier, item, and temperature reading. Probe one chilled + one frozen item per supplier."
          fields={[
            {key: "time", label: "Time received (HH:MM)", type: "time", required: true, default: new Date().toTimeString().slice(0,5)},
            {key: "supplier", label: "Supplier", type: "text", placeholder: "e.g. Sailbrand", required: true},
            {key: "invoice", label: "Invoice / batch ref", type: "text", placeholder: "e.g. PBS-02281"},
            {key: "item", label: "Item", type: "text", placeholder: "e.g. Atlantic salmon fillet 6kg", required: true},
            {key: "cf", label: "Chilled / Frozen / Ambient", type: "select", options: ["Chilled","Frozen","Ambient"], default: "Chilled", required: true},
            {key: "temp", label: "Temperature (°C)", type: "text", placeholder: "e.g. 3.2", required: true},
            {key: "pkg", label: "Packaging", type: "select", options: [{value:"S",label:"Satisfactory"},{value:"U",label:"Unsatisfactory"}], default: "S"},
            {key: "veh", label: "Vehicle", type: "select", options: [{value:"S",label:"Satisfactory"},{value:"U",label:"Unsatisfactory"}], default: "S"}
          ]}
          submitLabel="Continue → PIN"
          onSubmit={submitDelivery}
          onCancel={() => setDeliveryForm(false)}
        />
      )}
      {cookForm && (
        <CmpFormModal
          title="Log cook batch"
          desc="Probe first AND last item of the batch. Both must hit ≥75°C for 30s."
          fields={[
            {key: "item", label: "Item", type: "text", placeholder: "e.g. Crispy chicken — fryer", required: true},
            {key: "first", label: "First probe (°C)", type: "text", placeholder: "e.g. 82", required: true},
            {key: "last", label: "Last probe (°C)", type: "text", placeholder: "e.g. 79", required: true}
          ]}
          submitLabel="Continue → PIN"
          onSubmit={submitCook}
          onCancel={() => setCookForm(false)}
        />
      )}
      {phForm && (
        <CmpFormModal
          title="Log rice batch pH"
          desc="Target < 4.3. Anything ≥ 4.3 raises a corrective action — describe what you did."
          fields={[
            {key: "ph", label: "pH reading", type: "text", placeholder: "e.g. 3.9", required: true, hint: "Target: pH < 4.3"},
            {key: "action", label: "Corrective action (required if ≥ 4.3)", type: "textarea", placeholder: "Re-acidified, re-tested, batch released at pH 3.8…"}
          ]}
          submitLabel="Continue → PIN"
          onSubmit={submitPh}
          onCancel={() => setPhForm(false)}
        />
      )}
    </div>
  );
}

// ---- Declaration / FSMS Acceptance ----
// Wave 6.5 (2026-04-30): the per-section commitment list was lifted from
// the hardcoded CMP_DECLARATION_DOCS map into the admin-managed
// `declaration-commitments` surface. Doc title + intro are rare-change
// EHO copy and stay hardcoded here. The frontend regroups the flat
// surface items by (docType, sectionTitle) preserving items-array order.
const CMP_DECLARATION_VERSION = "v1.0";
const CMP_DECLARATION_HEADERS = {
  BOH: {
    title: "Declaration of Competency & Food Safety — BOH",
    intro: "As a Back of House team member at Rolled, I confirm I have been trained in the procedures below and that I will uphold these standards as a legal requirement for food safety at Rolled."
  },
  FOH: {
    title: "Declaration of Competency & Food Safety — FOH",
    intro: "As a Front of House team member at Rolled, I confirm I have been trained in the procedures below and that I will uphold these standards as a legal requirement for food safety at Rolled."
  }
};

// Rebuilds the {sections:[{title, commitments:[{label,detail}]}]} tree
// from the flat surface items. Section order = first-appearance order.
function cmpBuildDeclarationDoc(items, docType) {
  const header = CMP_DECLARATION_HEADERS[docType] || {title: docType, intro: ""};
  const sectionsByTitle = {};
  const sectionOrder = [];
  (items || []).forEach(it => {
    if (!it || it.docType !== docType) return;
    const title = String(it.sectionTitle || "Commitments");
    if (!sectionsByTitle[title]) {
      sectionsByTitle[title] = {title, commitments: []};
      sectionOrder.push(title);
    }
    sectionsByTitle[title].commitments.push({
      id: it.id, label: it.label || "", detail: it.detail || ""
    });
  });
  return {
    title: header.title, intro: header.intro,
    sections: sectionOrder.map(t => sectionsByTitle[t])
  };
}

function CmpDeclaration({backend, site, requestPin, staffByPin}) {
  const pinPrompt = useMemo(() => makeCmpPinPrompt(requestPin, staffByPin), [requestPin, staffByPin]);
  const [docType, setDocType] = useState("BOH");
  const [config, setConfig] = useState(null);
  const [ack, setAck] = useState({});
  const [signed, setSigned] = useState(null);
  const [historyByPinPrefix, setHistoryByPinPrefix] = useState({});
  const hist = useCmpHistoryView(backend, site.key, "DeclarationLog", "day");

  useEffect(() => {
    cmpFetchComplianceConfig(backend, site.key)
      .then(setConfig)
      .catch(e => cmpToast("Couldn't load declaration: " + e.message, "error"));
  }, [backend, site.key]);

  const commitmentItems = useMemo(
    () => config ? cmpResolveSurfaceItems(config, "declaration-commitments", site.key) : [],
    [config, site.key]
  );
  const doc = useMemo(() => cmpBuildDeclarationDoc(commitmentItems, docType), [commitmentItems, docType]);

  // Filter historical rows to staff-sign + matching docType so the
  // banner reflects "who signed this declaration on the picked day".
  const histDeclSign = hist.isHistorical
    ? hist.histRows.filter(r => String(r.Event) === "staff-sign" && String(r.DocumentType) === docType)
    : [];
  const totalItems = doc.sections.reduce((n, s) => n + s.commitments.length, 0);
  const ackedCount = Object.values(ack).filter(Boolean).length;
  const allAcked = totalItems > 0 && ackedCount === totalItems;

  // Rehydrate the latest staff-sign per PIN-prefix for this docType. Used
  // so a returning user sees "Already signed by you on [date]" rather than
  // a blank ack list. Frontend can't see the full PIN, only the prefix —
  // the user's identity is confirmed when they next PIN-gate any action.
  useEffect(() => {
    cmpFetchFormToday(backend, site.key, "DeclarationLog", 365)
      .then(rows => {
        const byPin = {};
        rows.forEach(r => {
          if (String(r.Event) !== "staff-sign") return;
          if (String(r.DocumentType) !== docType) return;
          const pp = String(r.StaffPin || "");
          if (!pp || byPin[pp]) return;
          byPin[pp] = {
            name: r.StaffName || "", ts: r._ts, nextDueAt: r.NextDueAt || ""
          };
        });
        setHistoryByPinPrefix(byPin);
      })
      .catch(() => {});
  }, [backend, site.key, docType]);

  const sign = () => {
    if (!allAcked) { cmpToast("Tick every commitment before signing", "error"); return; }
    pinPrompt({
      prompt: "Sign declaration",
      desc: "Your PIN is your signature. Annual refresh required.",
      onConfirm: (staff) => {
        cmpComplianceLog(backend, site.key, "declaration-sign", {
          documentType: docType, declarationVersion: CMP_DECLARATION_VERSION, fsmsVersion: ""
        }, staff.pin).then(d => {
          setSigned({name: staff.name, init: staff.initials, ts: new Date().toISOString(), nextDueAt: d && d.nextDueAt});
          setAck({});
          cmpToast("Signed by " + staff.name + " — pending manager countersign", "success");
        }).catch(e => cmpToast("Sign failed: " + e.message, "error"));
      }
    });
  };

  return (
    <div className="cmp-wrap">
      <CmpHistoryBanner date={hist.viewDate} onClear={() => hist.setViewDate(null)} />
      <CmpHistoricalSignerBanner date={hist.viewDate} formLabel="Declaration — historical view" signedBy={hist.signedBy} />
      <div className="cmp-card">
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,marginBottom:8}}>
          <h3 style={{margin:0}}>{doc.title}</h3>
          <CmpCalendarButton onPick={hist.setViewDate} viewingDate={hist.viewDate} mode="day" />
        </div>
        <div className="cmp-sub">{doc.intro}</div>
        <div className="cmp-tabs" style={{marginTop:10,maxWidth:300}}>
          {["BOH","FOH"].map(t => (
            <button key={t} className={docType === t ? "cmp-active" : ""} onClick={() => { setDocType(t); setAck({}); setSigned(null); }}>{t}</button>
          ))}
        </div>
      </div>
      {hist.isHistorical && (
        <div className="cmp-card">
          <h3>{docType} signs on {cmpFriendly(hist.viewDate)}</h3>
          {histDeclSign.length === 0 && !hist.loading && <div className="cmp-empty">No {docType} declarations signed on this day.</div>}
          {histDeclSign.map((r, i) => (
            <div key={i} className="cmp-doc-row">
              <div className="cmp-doc-icon">✍️</div>
              <div className="cmp-doc-body">
                <div className="cmp-doc-name">{r.StaffName}</div>
                <div className="cmp-doc-meta">{new Date(r._ts).toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"})} · v{r.DeclarationVersion || "—"} · next due {cmpFriendly(r.NextDueAt)}</div>
              </div>
              <div className="cmp-doc-right"><span className="cmp-chip cmp-ok"><span className="cmp-dot" />signed</span></div>
            </div>
          ))}
        </div>
      )}
      {!hist.isHistorical && signed && (
        <div className="cmp-card cmp-green">
          <h4 style={{color:"var(--cm-green)",marginBottom:4}}>✓ Signed by {signed.name}</h4>
          <div style={{fontSize:13}}>at {new Date(signed.ts).toLocaleTimeString("en-GB",{hour:"2-digit",minute:"2-digit"})}{signed.nextDueAt ? " · next due " + cmpFriendly(signed.nextDueAt) : ""}. Manager countersign in admin Compliance Overview.</div>
        </div>
      )}
      {!hist.isHistorical && <div className="cmp-card">
        {ackedCount > 0 && ackedCount < totalItems && (
          <div className="cmp-card" style={{background:"var(--cm-sky-soft)",border:"1px solid var(--cm-sky)",margin:"0 0 10px",padding:"8px 12px"}}>
            <div style={{fontSize:12}}>⏳ <strong>{ackedCount}/{totalItems} ticked.</strong> Ticks live on this device until you sign with your PIN.</div>
          </div>
        )}
        {doc.sections.map((sec, si) => (
          <div key={si} style={{marginBottom:14}}>
            <h5 style={{fontSize:11,letterSpacing:"0.06em",textTransform:"uppercase",color:"var(--cm-nori-60)",marginBottom:8,fontWeight:800}}>{sec.title}</h5>
            <div style={{display:"flex",flexDirection:"column",gap:6}}>
              {sec.commitments.map((c, i) => {
                const k = si + "/" + i;
                const ticked = !!ack[k];
                return (
                  <div key={i} onClick={() => { if (!signed) setAck(a => Object.assign({}, a, {[k]: !a[k]})); }}
                    style={{display:"flex",alignItems:"flex-start",gap:10,padding:"10px 12px",border:"1px solid " + (ticked ? "var(--cm-green)" : "var(--cm-nori-08)"),borderRadius:10,cursor:signed?"default":"pointer",background: ticked ? "var(--cm-green-soft)" : "var(--cm-rice)"}}>
                    <div style={{width:24,height:24,borderRadius:6,border:"1.5px solid " + (ticked ? "var(--cm-green)" : "var(--cm-nori-30)"),background: ticked ? "var(--cm-green)" : "transparent",color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,fontSize:14,flexShrink:0}}>{ticked ? "✓" : ""}</div>
                    <div style={{flex:1,fontSize:13,lineHeight:1.4}}><strong>{c.label}:</strong> {c.detail}</div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>}
      {!hist.isHistorical && !signed && (
        <div className="cmp-card" style={{textAlign:"center"}}>
          <div className="cmp-sub" style={{marginBottom:12}}>{ackedCount}/{totalItems} ticked. Your PIN signs the {docType} declaration · annual refresh.</div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap",justifyContent:"center"}}>
            <button className="cmp-btn cmp-btn-ghost cmp-btn-sm" onClick={() => cmpOpenPdf({title:CMP_FSMS_SUMMARY.title,meta:CMP_FSMS_SUMMARY.meta,body:CMP_FSMS_SUMMARY.body})}>📕 Open the FSMS to read it now</button>
            <button className="cmp-btn cmp-btn-primary" onClick={sign} disabled={!allAcked}>{allAcked ? "Sign with my PIN" : "Tick every item to enable"}</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Corrective Actions ----
function CmpCorrectiveActions({backend, site, requestPin, staffByPin}) {
  const pinPrompt = useMemo(() => makeCmpPinPrompt(requestPin, staffByPin), [requestPin, staffByPin]);
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("open");
  const [formOpen, setFormOpen] = useState(false);
  const [resolveFor, setResolveFor] = useState(null);
  const hist = useCmpHistoryView(backend, site.key, "CorrectiveActions", "day");
  const visibleItems = hist.isHistorical
    ? hist.histRows.map(r => ({
        id: r.IncidentId || "(pending)",
        source: r.Source || "Ad hoc raise",
        site: site.key, ts: r._ts,
        desc: r.Description || "",
        action: r.Action || "",
        flagged: r.RaisedByName || "",
        status: r.Status || "open",
        resolvedBy: r.ResolvedByName || "",
        resolvedAt: r.ResolvedAt || "",
        resolutionAction: r.ResolutionAction || ""
      }))
    : items;

  // Rehydrate the last 14 days of corrective actions, every status.
  useEffect(() => {
    cmpFetchFormToday(backend, site.key, "CorrectiveActions", 14)
      .then(rows => {
        const arr = rows.map(r => ({
          id: r.IncidentId || "(pending)",
          source: r.Source || "Ad hoc raise",
          site: site.key, ts: r._ts,
          desc: r.Description || "",
          action: r.Action || "",
          flagged: r.RaisedByName || "",
          status: r.Status || "open",
          resolvedBy: r.ResolvedByName || "",
          resolvedAt: r.ResolvedAt || "",
          resolutionAction: r.ResolutionAction || ""
        }));
        setItems(arr);
      })
      .catch(() => {});
  }, [backend, site.key]);

  const submitRaise = (vals) => {
    setFormOpen(false);
    pinPrompt({
      prompt: "Sign off corrective action",
      desc: "Confirm with your PIN to log this concern.",
      onConfirm: (s) => {
        cmpComplianceLog(backend, site.key, "corrective-raise", {
          description: vals.description, action: vals.action
        }, s.pin).then(d => {
          setItems(arr => [{
            id: d && d.incidentId ? d.incidentId : "(pending)",
            source: "Ad hoc raise", site: site.key, ts: new Date().toISOString(),
            desc: vals.description, action: vals.action,
            flagged: s.name, status: "open"
          }, ...arr]);
          cmpToast("Corrective action raised", "warn");
        }).catch(e => cmpToast("Raise failed: " + e.message, "error"));
      },
      onCancel: () => setFormOpen(true)
    });
  };

  const submitResolve = (vals) => {
    const id = resolveFor;
    setResolveFor(null);
    pinPrompt({
      prompt: "Sign off resolution",
      desc: "Confirm with your PIN to close " + id + ".",
      onConfirm: (s) => {
        cmpComplianceLog(backend, site.key, "corrective-resolve", {
          incidentId: id, resolutionAction: vals.resolution
        }, s.pin).then(() => {
          setItems(arr => arr.map(x => x.id === id ? Object.assign({}, x, {
            status: "resolved", resolvedBy: s.name, resolvedAt: new Date().toISOString(),
            resolutionAction: vals.resolution
          }) : x));
          cmpToast("Action resolved by " + s.name, "success");
        }).catch(e => cmpToast("Resolve failed: " + e.message, "error"));
      },
      onCancel: () => setResolveFor(id)
    });
  };

  const filtered = visibleItems.filter(i => tab === "all" || i.status === tab);

  return (
    <div className="cmp-wrap">
      <CmpHistoryBanner date={hist.viewDate} onClear={() => hist.setViewDate(null)} />
      <CmpHistoricalSignerBanner date={hist.viewDate} formLabel="Corrective Actions — historical view" signedBy={hist.signedBy} />
      <div className="cmp-card">
        <div style={{display:"flex",justifyContent:"space-between",flexWrap:"wrap",gap:12,alignItems:"center"}}>
          <div>
            <h3>Corrective Actions</h3>
            <div className="cmp-sub">Raise a concern, sign with your PIN. Resolved actions stay on file.</div>
          </div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            <CmpCalendarButton onPick={hist.setViewDate} viewingDate={hist.viewDate} mode="day" />
            {!hist.isHistorical && <button className="cmp-btn cmp-btn-danger" onClick={() => setFormOpen(true)}>+ Raise concern</button>}
          </div>
        </div>
        <div className="cmp-tabs" style={{marginTop:12}}>
          {["open","resolved","all"].map(t => (
            <button key={t} className={tab === t ? "cmp-active" : ""} onClick={() => setTab(t)}>
              {t.charAt(0).toUpperCase() + t.slice(1)} ({visibleItems.filter(i => t === "all" || i.status === t).length})
            </button>
          ))}
        </div>
      </div>
      {filtered.length === 0 && !hist.loading && (
        <div className="cmp-empty">
          {hist.isHistorical
            ? "No corrective actions matching this filter on " + cmpFriendly(hist.viewDate) + "."
            : (tab === "open" ? "No open actions in this session." : tab === "resolved" ? "Nothing resolved yet in this session." : "Nothing logged yet in this session.")}
        </div>
      )}
      {filtered.map(i => (
        <div key={i.id} className={"cmp-ca-row cmp-" + i.status}>
          <div className={"cmp-pill cmp-" + i.status}>{i.status === "open" ? "!" : "✓"}</div>
          <div className="cmp-body">
            <div className="cmp-desc">{i.desc}</div>
            <div className="cmp-meta">
              <strong style={{color:"var(--cm-nori)"}}>{i.id}</strong> · {i.source} · {cmpFriendly(i.ts)} · flagged by {i.flagged}
              {i.resolvedBy && <span> · <span style={{color:"var(--cm-green)"}}>resolved by {i.resolvedBy}</span></span>}
            </div>
            <div style={{fontSize:12,marginTop:8,lineHeight:1.45}}>{i.action}</div>
            {i.resolutionAction && (
              <div style={{fontSize:12,marginTop:8,lineHeight:1.45,padding:"8px 10px",background:"var(--cm-green-soft)",borderRadius:6,borderLeft:"3px solid var(--cm-green)"}}>
                <strong style={{color:"var(--cm-green)"}}>Resolution:</strong> {i.resolutionAction}
              </div>
            )}
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:6,alignItems:"flex-end"}}>
            <span className={"cmp-chip " + (i.status === "open" ? "cmp-bad" : "cmp-ok")}><span className="cmp-dot" />{i.status}</span>
            {!hist.isHistorical && i.status === "open" && <button className="cmp-btn cmp-btn-ghost cmp-btn-sm" onClick={() => setResolveFor(i.id)}>Mark resolved</button>}
          </div>
        </div>
      ))}
      {!hist.isHistorical && formOpen && (
        <CmpFormModal
          title="Raise corrective action"
          desc="Describe the issue and the action you're taking. You'll confirm with your PIN on the next step."
          fields={[
            {key: "description", label: "Describe the issue", type: "textarea", placeholder: "What did you spot? Where? When?", required: true},
            {key: "action", label: "Action you're taking", type: "textarea", placeholder: "What have you done / will do to fix it?", required: true}
          ]}
          submitLabel="Continue → PIN"
          onSubmit={submitRaise}
          onCancel={() => setFormOpen(false)}
        />
      )}
      {!hist.isHistorical && resolveFor && (
        <CmpFormModal
          title="Resolve corrective action"
          desc={"Closing " + resolveFor + ". Capture the corrective action taken before signing — required."}
          fields={[
            {key: "resolution", label: "Corrective action taken", type: "textarea", placeholder: "What was done to close this issue?", required: true, hint: "Logged on the row and on the audit trail with your PIN signature."}
          ]}
          submitLabel="Continue → PIN"
          onSubmit={submitResolve}
          onCancel={() => setResolveFor(null)}
        />
      )}
    </div>
  );
}

// ---- ComplianceModule shell ----
const CMP_VALID_VIEWS = ["home","cleaning","pest","pestmonday","corrective","haccp","probe","fish","emergency","declaration",
  "hub-sops","hub-fsms","hub-risk","hub-ssow","hub-suppliers","hub-legal","hub-reference","hub-certs"];

// Lane AK read-source dot — shows ONLY when the compliance read-flip gate is on
// (COMPLIANCE_READ_HOSTS lists this host, or ?compread=on). Green = reading the
// food-safety record from Supabase; coral = fell back to the Google Sheet.
// Invisible in the shipped OFF state, so it changes nothing for staff today.
function CmpReadSourceDot() {
  const [src, setSrc] = useState(() => complianceReader.getComplianceSource());
  useEffect(() => {
    setSrc(complianceReader.getComplianceSource());
    return complianceReader.subscribeComplianceSource(setSrc);
  }, []);
  if ((!complianceReader.complianceReadEnabled() && !complianceReader.haccpReadEnabled()) || !src) return null;
  const supa = src === "supabase";
  return (
    <div style={{fontSize:"0.68rem",fontWeight:600,margin:"0 auto 8px",display:"flex",alignItems:"center",justifyContent:"center",gap:6,color:supa?"#6FAF8F":"#FF7A5C"}}>
      <span style={{fontSize:"0.9rem",lineHeight:1}}>●</span>
      {supa ? "Live — compliance record connected" : "⚠ Fallback — compliance record from the backup copy"}
    </div>
  );
}

function ComplianceModule({site, backend, requestPin, staffByPin}) {
  useLayoutEffect(() => { ensureCss(); }, []);
  const [view, setView] = useState("home");
  const sundayTone = cmpSundayPillTone();
  // Per-site storage units + probes (admin-managed surfaces; complianceUnits.js).
  // They drive the HACCP progress total and the probes-due banner on the home
  // screen. Config is the 5-min cached read the forms use, so this is cheap.
  const [units, setUnits] = useState(null);
  const [probes, setProbes] = useState(null);
  const [lastByProbe, setLastByProbe] = useState({});
  useEffect(() => {
    if (view !== "home") return;
    let alive = true;
    cmpFetchComplianceConfig(backend, site.key)
      .then(c => {
        if (!alive) return;
        setUnits(resolveFridges(cmpResolveSiteOnlyItems(c, "fridges", site.key), site.key));
        setProbes(resolveProbes(cmpResolveSiteOnlyItems(c, "probes", site.key), site.key));
      })
      .catch(() => {
        if (!alive) return;
        setUnits(resolveFridges([], site.key));
        setProbes(resolveProbes([], site.key));
      });
    return () => { alive = false; };
  }, [view, backend, site.key]);
  // Live home-screen counts. Read on-mount from today's HACCP sheet
  // (existing public GET endpoint) so the status strip reflects real
  // state. Other counts (cleaning today, open incidents) are wired in
  // v1.3 once getComplianceFormHistory ships; for now we show what's
  // cheaply queryable.
  const [todayCounts, setTodayCounts] = useState({haccpDone: 0, haccpTotal: 0, cleaningCellsToday: 0, pestDoneToday: false, openIncidents: 0, openCorrective: 0});
  useEffect(() => {
    if (view !== "home") return;
    const today = cmpTodayIso();
    // HACCP today (live progress strip). Routed through cmpFetchHaccpDay so it
    // inherits the Lane AK read-flip gate — otherwise the home dashboard would
    // read the Sheet while the forms read Supabase (split-brain).
    cmpFetchHaccpDay(backend, site.key, today)
      .catch(() => null)
      .then(d => {
        if (!d || !d.sheet) return;
        const sh = d.sheet;
        const opening = Object.keys(sh.openingChecks || {}).length;
        const closing = Object.keys(sh.closingChecks || {}).length;
        const fridges = sh.storageTempsPerFridge || {};
        const fridgeReadings = Object.keys(fridges).reduce((n, fId) => n + Object.keys(fridges[fId] || {}).length, 0);
        const haccpDone = opening + closing + fridgeReadings;
        // haccpTotal is derived at render from the site's unit list (below).
        setTodayCounts(c => Object.assign({}, c, {haccpDone}));
      }).catch(() => {});
    // Pest done today + open corrective count + per-probe last-calibration
    Promise.all([
      cmpFetchFormToday(backend, site.key, "PestLog", 1).catch(() => []),
      cmpFetchFormToday(backend, site.key, "CorrectiveActions", 14).catch(() => []),
      cmpFetchFormToday(backend, site.key, "ProbeLog", 30).catch(() => []),
      cmpFetchFormToday(backend, site.key, "EmergencyLog", 14).catch(() => []),
      cmpFetchFormToday(backend, site.key, "CleaningLog", 7).catch(() => [])
    ]).then(([pestRows, caRows, probeRows, emRows, cleaningRows]) => {
      const pestDoneToday = pestRows.some(r => String(r.Date) === today && String(r.Event) === "daily-check");
      const openCorrective = caRows.filter(r => String(r.Status) === "open").length;
      // Open emergency incidents — group by IncidentId, latest status row wins
      const incStatus = {};
      emRows.slice().reverse().forEach(r => {
        const id = String(r.IncidentId || "");
        if (!id) return;
        if (String(r.Event) === "close" || String(r.Status) === "resolved") incStatus[id] = "resolved";
        else if (incStatus[id] !== "resolved") incStatus[id] = "open";
      });
      const openIncidents = Object.values(incStatus).filter(v => v === "open").length;
      const cleaningCellsToday = cleaningRows.filter(r => String(r.DayDate) === today && String(r.State) === "done").length;
      setTodayCounts(c => Object.assign({}, c, {pestDoneToday, openCorrective, openIncidents, cleaningCellsToday}));
      // Per-probe last calibration (rows newest-first) → probes-due banner,
      // joined at render against the site's configured probe list.
      const last = {};
      probeRows.forEach(r => {
        const pId = String(r.ProbeId || "");
        if (!pId || last[pId]) return;
        last[pId] = r._ts;
      });
      setLastByProbe(last);
    });
  }, [view, backend, site.key]);

  useEffect(() => {
    try {
      const v = sessionStorage.getItem("cmpView");
      if (v && CMP_VALID_VIEWS.indexOf(v) !== -1) setView(v);
    } catch(_) {}
  }, []);
  useEffect(() => {
    try { sessionStorage.setItem("cmpView", view); } catch(_) {}
    window.scrollTo({top: 0, left: 0, behavior: "auto"});
  }, [view]);

  // Derived home-screen numbers: the HACCP total counts the opening + closing
  // ticks plus AM + PM per configured unit (the kitchen hardcoded 8 + 6 + 10);
  // the probes-due banner only ever names probes this site actually has.
  const counts = Object.assign({}, todayCounts, {
    haccpTotal: units ? haccpTotalFor(CMP_HACCP_OPENING.length, CMP_HACCP_CLOSING.length, units.length) : 0
  });
  const probesDue = (probes || [])
    .map(p => {
      const ts = lastByProbe[p.id];
      const daysAgo = ts ? Math.floor((Date.now() - new Date(ts).getTime()) / (1000*60*60*24)) : null;
      return {id: p.id, name: p.name, daysAgo};
    })
    .filter(p => p.daysAgo === null || p.daysAgo >= 7);

  let body;
  if (view === "cleaning")        body = <CmpCleaningSchedule backend={backend} site={site} requestPin={requestPin} staffByPin={staffByPin} />;
  else if (view === "pest")       body = <CmpPestDaily       backend={backend} site={site} requestPin={requestPin} staffByPin={staffByPin} />;
  else if (view === "pestmonday") body = <CmpPestMondaySweep backend={backend} site={site} requestPin={requestPin} staffByPin={staffByPin} />;
  else if (view === "corrective") body = <CmpCorrectiveActions backend={backend} site={site} requestPin={requestPin} staffByPin={staffByPin} />;
  else if (view === "haccp")      body = <CmpHaccpDaily      backend={backend} site={site} requestPin={requestPin} staffByPin={staffByPin} />;
  else if (view === "probe")      body = <CmpProbeCalibration backend={backend} site={site} requestPin={requestPin} staffByPin={staffByPin} />;
  else if (view === "fish")       body = <CmpFishFreezing    backend={backend} site={site} requestPin={requestPin} staffByPin={staffByPin} />;
  else if (view === "emergency")  body = <CmpEmergencyFridge backend={backend} site={site} requestPin={requestPin} staffByPin={staffByPin} />;
  else if (view === "declaration")body = <CmpDeclaration     backend={backend} site={site} requestPin={requestPin} staffByPin={staffByPin} />;
  else if (view === "hub-sops")     body = <CmpHubSops backend={backend} site={site} />;
  else if (view === "hub-fsms")     body = <CmpHubFsms />;
  else if (view === "hub-risk")     body = <CmpHubRisk />;
  else if (view === "hub-ssow")     body = <CmpHubSsow />;
  else if (view === "hub-suppliers")body = <CmpHubSuppliers backend={backend} site={site} />;
  else if (view === "hub-legal")    body = <CmpHubLegal     backend={backend} site={site} />;
  else if (view === "hub-reference")body = <CmpHubReference />;
  else if (view === "hub-certs")    body = <CmpHubCerts />;
  else                            body = <CmpHome nav={setView} sundayTone={sundayTone} todayCounts={counts} probesDue={probesDue} siteName={site.name} />;

  return (
    <div className="cmp-root">
      <CmpReadSourceDot />
      {view !== "home" && (
        <div className="cmp-wrap" style={{paddingTop:0,paddingBottom:0,marginBottom:8}}>
          <button className="cmp-btn cmp-btn-ghost cmp-btn-sm" onClick={() => setView("home")}>← Compliance home</button>
        </div>
      )}
      {body}
      <CmpToastLayer />
      <CmpPdfLayer />
    </div>
  );
}

export { ComplianceModule as ComplianceModuleFull };
